const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

// [ERP-ENV-VISIBILITY-BANNER-DEV-20260626-001]
// 환경 배지(EnvBadge)용 — API base/실해석 URL/health 환경 조회. read-only.
/** 설정된 API base (상대경로 "/api" 또는 절대 URL) 그대로 반환. */
export function getApiBase(): string {
  return API_URL;
}
/** 상대경로면 현재 origin 을 붙여 실제 해석된 API URL 반환. 브라우저 한정(SSR 시 base 그대로). */
export function getResolvedApiUrl(): string {
  if (typeof window === "undefined") return API_URL;
  if (/^https?:\/\//i.test(API_URL)) return API_URL;
  return `${window.location.origin}${API_URL}`;
}
export interface HealthEnv {
  status?: string;
  database?: string;
  db_name?: string;
  app_env?: string;
}
/** GET /health — db_name/app_env 포함(backend 신버전). 구버전이면 해당 필드 없음. */
export function getHealthEnv(): Promise<HealthEnv> {
  return request<HealthEnv>("/health");
}

function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("access_token");
}

// 26차 후속 #82 Phase 1 — 구조화된 에러 정보를 호출자에게 노출.
// 기존 호출자(.message 만 읽음)는 그대로 동작하고,
// 신규 호출자는 .status / .errorCode / .detail 로 분기 가능.
export class ApiError extends Error {
  status: number;
  errorCode: string | null;
  detail: unknown;
  constructor(message: string, status: number, errorCode: string | null, detail: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.detail = detail;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers: Record<string, string> = {
    // FormData 인 경우 boundary 자동 부여를 위해 Content-Type 미설정
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new ApiError("인증이 만료되었습니다. 다시 로그인해주세요.", 401, null, null);
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "서버 오류가 발생했습니다." }));
    // FastAPI validation 에러 detail 이 배열인 경우를 평문화
    let detailMsg: string;
    let errorCode: string | null = null;
    if (Array.isArray(err.detail)) {
      detailMsg = err.detail
        .map((d: any) => {
          if (typeof d === "string") return d;
          const loc = Array.isArray(d?.loc) ? d.loc.slice(-1).join(".") : "";
          return [loc, d?.msg].filter(Boolean).join(": ");
        })
        .join(" / ");
    } else if (typeof err.detail === "string") {
      detailMsg = err.detail;
    } else if (err.detail && typeof err.detail === "object") {
      // 26차 후속 #82 Phase 1 — 백엔드가 detail={code, message, ...} 로 내려주는 케이스.
      const d = err.detail as Record<string, unknown>;
      if (typeof d.code === "string") errorCode = d.code;
      detailMsg =
        (typeof d.message === "string" ? d.message : null) ||
        (typeof d.msg === "string" ? d.msg : null) ||
        JSON.stringify(err.detail);
    } else {
      detailMsg = "요청 처리 중 오류가 발생했습니다.";
    }
    throw new ApiError(`[${res.status}] ${detailMsg}`, res.status, errorCode, err.detail);
  }

  // 204 No Content 등 빈 응답은 json 파싱하지 않음
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return res.json();
}

// ─── Auth ───────────────────────────────────

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user_id: number;
  name: string;
  role: string;
  // [ERP-ORDER-DETAIL-SKU-CHANGE-LV2-PERMISSION-HOTFIX-20260615-001] 권한등급 lv1/lv2/lv3.
  permission_level?: string;
  company_id: number | null;
  company_code?: string | null;
  company_name?: string | null;
}

export async function login(login_id: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ login_id, password }),
  });
}

export interface UserResponse {
  id: number;
  login_id: string;
  name: string;
  role: string;
  // [ERP-USER-PERMISSION-LEVEL-SCHEMA-AND-UI-20260604-001] 권한등급 lv1/lv2/lv3.
  permission_level?: string;
  status: string;
  is_active: boolean;
  birth_date: string | null;
  phone: string | null;
  hire_date?: string | null;
  company_id: number | null;
  company_code?: string | null;
  company_name?: string | null;
  created_at: string;
  // [ERP-USER-ACCOUNT-LEAVE-20260519-001] Phase 1 — 잔여 휴가 정보.
  // /auth/users 목록 응답에서만 채워짐. /auth/me 는 null.
  remaining_leave_days?: number | null;
  leave_accrued_days?: number | null;
  leave_used_days?: number | null;
  leave_period_start?: string | null;
  leave_period_end?: string | null;
}

export async function signup(body: {
  login_id: string;
  password: string;
  name: string;
  birth_date?: string;
  phone?: string;
  hire_date?: string;
}): Promise<{ message: string }> {
  return request<{ message: string }>("/auth/signup", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getUsers(): Promise<UserResponse[]> {
  return request<UserResponse[]>("/auth/users");
}

// 담당자 드롭다운용 — 활성(approved + is_active) 사용자의 id/name만 반환.
export function getAssignees(): Promise<{ id: number; name: string }[]> {
  return request<{ id: number; name: string }[]>("/auth/assignees");
}

export function approveUser(userId: number): Promise<{ message: string }> {
  return request<{ message: string }>(`/auth/users/${userId}/approve`, { method: "POST" });
}

export function rejectUser(userId: number): Promise<{ message: string }> {
  return request<{ message: string }>(`/auth/users/${userId}/reject`, { method: "POST" });
}

export function deactivateUser(userId: number): Promise<{ message: string }> {
  return request<{ message: string }>(`/auth/users/${userId}/deactivate`, { method: "POST" });
}

// [ERP-USER-ACCOUNT-LEAVE-20260519-001] role / company 변경 helper.
export function updateUserRole(
  userId: number,
  role: string,
): Promise<{ message: string; user_id: number; role: string }> {
  return request(`/auth/users/${userId}/role`, {
    method: "POST",
    body: JSON.stringify({ role }),
  });
}

// [ERP-USER-PERMISSION-LEVEL-SCHEMA-AND-UI-20260604-001] 권한등급(permission_level) 변경.
export function updateUserPermissionLevel(
  userId: number,
  permissionLevel: string,
): Promise<{ message: string; user_id: number; permission_level: string }> {
  return request(`/auth/users/${userId}/permission-level`, {
    method: "POST",
    body: JSON.stringify({ permission_level: permissionLevel }),
  });
}

export function updateUserCompany(
  userId: number,
  companyId: number | null,
): Promise<{ message: string; user_id: number; company_id: number | null }> {
  return request(`/auth/users/${userId}/company`, {
    method: "POST",
    body: JSON.stringify({ company_id: companyId }),
  });
}

// ─── Products ───────────────────────────────

export interface Product {
  id: number;
  sku: string;
  product_name: string;
  // [ERP-PRODUCT-LAST-INBOUND-DATE-DISPLAY-20260618-001] 상세 최근 입고일
  last_inbound_at?: string | null;
  last_inbound_source?: string | null;
  name_cn: string | null;                 // 품명(중문) — 제조사 공유 양식 "中文" SoT
  brand: string | null;
  category: string | null;
  option1: string | null;
  option1_cn: string | null;
  option2: string | null;
  option2_cn: string | null;
  option3: string | null;
  option3_cn: string | null;
  model_name: string | null;
  cost_price: number | null;
  barcode: string | null;
  // [ERP-BARCODE-20260513-002] 바코드 스티커 표시 텍스트 전용.
  barcode_label_name?: string | null;
  barcode_option_name?: string | null;
  // [ERP-BARCODE-PREFERENCE-PHASE-A-PRODUCT-DEFAULT-STYLE-20260616-001]
  // SKU별 기본 바코드 출력 스타일. null fallback: size→small, code→(small=qr/large=code128).
  barcode_label_size?: BarcodeLabelSize | null;
  barcode_code_type?: BarcodeCodeType | null;
  image_url: string | null;
  purchase_url: string | null;
  supplier_name: string | null;
  supplier_product_code: string | null;
  purchase_type: string | null;
  shipping_alias: string | null;
  as_available: boolean;
  is_active: boolean;
  product_status?: string;
  safety_stock_qty?: number;
  part_type?: string;
  default_labor_fee?: number;
  // 치수/무게
  width_cm: number | null;
  depth_cm: number | null;
  height_cm: number | null;
  weight_kg: number | null;
  // 원가 계산기
  cost_price_cny: number | null;
  cost_factor: number | null;
  // 판매가 / 원가 유형
  sale_price?: number | null;
  cost_currency?: string;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Cost Calculator ─────────────────────────────────────────────────────────

export interface CustomsCategory { label: string; customs_rate: number; vat_rate: number; }
export interface CostPreset {
  exchange_rate: number;
  cbm_unit_price: number;
  handling_fee: number;
  lcl_customs_fee: number;
  lcl_coo_fee: number;
  bl_fee: number;
  /** 외국인결제 수수료 % — CNY 원가에 복리 곱 */
  foreign_payment_fee_rate: number;
  /** 카드 수수료 % */
  card_fee_rate: number;
  /** 기타 수수료 % */
  etc_fee_rate: number;
  customs_categories: CustomsCategory[];
}

export interface ProductSearchItem {
  sku: string;
  product_name: string;
  cost_price_cny: number | null;
  cost_price: number | null;
  brand: string | null;
  default_labor_fee: number | null;
  // 26차 후속 #14(2026-04-24) — 중국 수입 PO 품목 추가 UX 에서 옵션/중문옵션/구매링크를
  // 선택 즉시 폼에 채우기 위해 응답 확장.
  name_cn?: string | null;
  option1?: string | null;
  option2?: string | null;
  option1_cn?: string | null;
  option2_cn?: string | null;
  purchase_url?: string | null;
  image_url?: string | null;
  // [ERP-WARRANTY-POLICY-SKU-SEARCH-UX-FIX-20260617-001]
  // sku_priority/include_inactive 모드에서 상품상태/활성여부 배지 표시용(옵션 — 기존 호출부 무영향).
  product_status?: string | null;
  is_active?: boolean;
  // [ERP-PRODUCT-BUNDLE-COMPONENT-SEARCH-EXCLUDE-BUNDLE-SKU-20260619-001]
  // true 면 이미 세트상품(product_bundles.bundle_sku) — 다른 세트 구성품 후보에서 제외.
  is_bundle?: boolean;
}

export function searchProducts(
  q: string,
  limit = 10,
  part_type?: string,
  excludeBrands?: string[],
  offset = 0,
): Promise<ProductSearchItem[]> {
  const qs = new URLSearchParams({ q, limit: String(limit) });
  if (offset) qs.set("offset", String(offset));
  if (part_type) qs.set("part_type", part_type);
  if (excludeBrands && excludeBrands.length > 0) {
    qs.set("exclude_brands", excludeBrands.join(","));
  }
  return request<ProductSearchItem[]>(`/products/search?${qs}`);
}

// [ERP-WARRANTY-POLICY-SKU-SEARCH-UX-FIX-20260617-001]
// 무상 AS 정책 / 대상 SKU 지정 전용 검색. /products/search 를 sku_priority+include_inactive
// 옵션으로 재사용 — exact SKU 최상단 + 비활성 상품도 배지용으로 포함. 다른 화면 무영향.
export function searchProductsForWarranty(q: string, limit = 20): Promise<ProductSearchItem[]> {
  const qs = new URLSearchParams({
    q,
    limit: String(limit),
    sku_priority: "true",
    include_inactive: "true",
  });
  return request<ProductSearchItem[]>(`/products/search?${qs}`);
}

// [ERP-WARRANTY-POLICY-SKU-SEARCH-PAGINATION-20260617-001]
// 보증정책 대상 SKU "더보기" 전용 페이지네이션 검색. /products/search 를 with_meta=true 로
// 호출해 {items, has_more, next_offset} 를 받는다. sku_priority/include_inactive 옵션은
// 기존 searchProductsForWarranty 와 동일. with_meta 는 백엔드 opt-in 이라 기본 배열 호출(다른 화면)은 무영향.
export interface WarrantySkuSearchPage {
  items: ProductSearchItem[];
  has_more: boolean;
  next_offset: number | null;
}

export function searchProductsForWarrantyPaged(
  q: string,
  offset = 0,
  limit = 20,
): Promise<WarrantySkuSearchPage> {
  const qs = new URLSearchParams({
    q,
    limit: String(limit),
    offset: String(offset),
    sku_priority: "true",
    include_inactive: "true",
    with_meta: "true",
  });
  return request<WarrantySkuSearchPage>(`/products/search?${qs}`);
}

// [ERP-PRODUCT-DETAIL-BUNDLE-SKU-SEARCH-UNIFY-20260617-001]
// 공용 SKU picker 페이지네이션 검색. /products/search 를 sku_priority+with_meta 로 호출해
// {items, has_more, next_offset} 반환. 상품상세 번들 SKU 검색 등에서 재사용.
// (warranty 전용 searchProductsForWarrantyPaged 는 그대로 두고, 공용 흐름은 이 함수로.)
export interface SkuSearchPage {
  items: ProductSearchItem[];
  has_more: boolean;
  next_offset: number | null;
}

export function searchProductsForSkuPicker(
  q: string,
  offset = 0,
  limit = 20,
  includeInactive = true,
  // [ERP-AS-PARTS-SKU-SEARCH-PAGINATION-UNIFY-20260618-001]
  // AS 파츠 검색 등 part_type 필터가 필요한 경우 전달(예: "repair_part").
  // 미전달 시 기존 동작 그대로 — 번들/보증정책 호출 회귀 0.
  partType?: string,
): Promise<SkuSearchPage> {
  const qs = new URLSearchParams({
    q,
    limit: String(limit),
    offset: String(offset),
    sku_priority: "true",
    include_inactive: includeInactive ? "true" : "false",
    with_meta: "true",
  });
  if (partType) qs.set("part_type", partType);
  return request<SkuSearchPage>(`/products/search?${qs}`);
}

// [ERP-GLOBAL-SKU-SEARCH-PAGINATION-PO-ITEM-ADD-FIRST-DEV-20260622-001]
// 전역 SKU 검색 페이지네이션 공용 helper. /products/search 를 with_meta+with_total 로 호출해
// page/page_size/total/total_pages/has_more 까지 받는다(이전/다음/총 N건/전체 페이지 UX).
// 발주서 품목 추가부터 적용. 다른 검색창은 기존 helper 유지(점진 이관 — backend opt-in 이라 회귀 0).
export interface SkuSearchPagedResult {
  items: ProductSearchItem[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_more: boolean;
  next_offset: number | null;
}

export function searchProductsPaged(
  q: string,
  opts: {
    page?: number;          // 1-base
    pageSize?: number;
    skuPriority?: boolean;   // exact>prefix>contains 우선 정렬 + 브랜드 contains 매칭
    includeInactive?: boolean;
    partType?: string;
    excludeBrands?: string[];
  } = {},
): Promise<SkuSearchPagedResult> {
  const {
    page = 1, pageSize = 20, skuPriority = true, includeInactive = true, partType, excludeBrands,
  } = opts;
  const offset = (Math.max(1, page) - 1) * pageSize;
  const qs = new URLSearchParams({
    q,
    limit: String(pageSize),
    offset: String(offset),
    with_meta: "true",
    with_total: "true",
  });
  if (skuPriority) qs.set("sku_priority", "true");
  if (includeInactive) qs.set("include_inactive", "true");
  if (partType) qs.set("part_type", partType);
  if (excludeBrands && excludeBrands.length > 0) qs.set("exclude_brands", excludeBrands.join(","));
  return request<SkuSearchPagedResult>(`/products/search?${qs}`);
}

export interface AsSettings {
  default_labor_fee: number;
  default_shipping_fee: number;
}

export function getAsSettings(): Promise<AsSettings> {
  return request<AsSettings>("/as/settings");
}

export function updateAsSettings(body: Partial<AsSettings>): Promise<AsSettings> {
  return request<AsSettings>("/as/settings", { method: "PUT", body: JSON.stringify(body) });
}

export interface ApplyCostFactorRequest {
  cost_factor: number;
  cost_price_cny?: number | null;
}

export interface ApplyCostFactorResponse {
  sku: string;
  cost_factor: number;
  cost_price_cny: number | null;
  cost_price: number | null;
}

export function getCostPreset(): Promise<CostPreset> {
  return request<CostPreset>("/cost-calculator/preset");
}

export function saveCostPreset(preset: CostPreset): Promise<CostPreset> {
  return request<CostPreset>("/cost-calculator/preset", { method: "PUT", body: JSON.stringify(preset) });
}

export function applyCostFactor(sku: string, body: ApplyCostFactorRequest): Promise<ApplyCostFactorResponse> {
  return request<ApplyCostFactorResponse>(`/cost-calculator/products/${sku}/apply-cost-factor`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface ProductPrice {
  id: number;
  product_id: number;
  sku: string;
  platform: string;
  price: number;
  is_base_price: boolean;
  fee_rate: number | null;
  margin_rate: number | null;
  memo: string | null;
}

export async function getProducts(params?: { is_active?: boolean }): Promise<Product[]> {
  const query = params?.is_active !== undefined ? `?is_active=${params.is_active}` : "";
  return request<Product[]>(`/products${query}`);
}

export async function getProduct(sku: string): Promise<Product> {
  return request<Product>(`/products/${sku}`);
}

export async function createProduct(data: Partial<Product>): Promise<Product> {
  return request<Product>("/products", { method: "POST", body: JSON.stringify(data) });
}

export async function updateProduct(sku: string, data: Partial<Product>): Promise<Product> {
  return request<Product>(`/products/${sku}`, { method: "PATCH", body: JSON.stringify(data) });
}

export async function getProductPrices(sku: string): Promise<ProductPrice[]> {
  return request<ProductPrice[]>(`/products/${sku}/prices`);
}

export async function upsertPrice(sku: string, data: Partial<ProductPrice>): Promise<ProductPrice> {
  return request<ProductPrice>(`/products/${sku}/prices`, { method: "PUT", body: JSON.stringify(data) });
}

// ─── Import ─────────────────────────────────

// ─── Import Preview ─────────────────────────
export interface ImportPreviewResponse {
  detected_import_type: string;
  is_valid: boolean;
  validation_errors: string[];
  warnings: string[];
  file_name: string;
  detected_company_hint: string | null;
  selected_company_code: string;
  matched_company_confidence: string;
  row_count: number;
  sample_rows: Record<string, string>[];
  detected_headers: string[];
  header_row_idx: number;
  duplicate_order_nos_count: number | null;
}

async function uploadFilePreview(path: string, file: File, companyCode: string): Promise<ImportPreviewResponse> {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_URL}${path}?company_code=${encodeURIComponent(companyCode)}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "서버 오류가 발생했습니다." }));
    throw new Error(err.detail || "미리보기 처리 중 오류가 발생했습니다.");
  }
  return res.json();
}

export function previewProductMaster(file: File, companyCode = "HITCAMP"): Promise<ImportPreviewResponse> {
  return uploadFilePreview("/products/import/product-master/preview", file, companyCode);
}

export function previewToggleOrders(file: File, companyCode = "HITCAMP"): Promise<ImportPreviewResponse> {
  return uploadFilePreview("/orders/import/toggle/preview", file, companyCode);
}

export interface ImportErrorRow {
  row: number;
  sku: string | null;
  error: string;
}

export interface ImportResult {
  total_rows: number;
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  errors: ImportErrorRow[];
}

// ─── Order Import ────────────────────────────

export interface OrderImportResult {
  total_rows: number;
  inserted_orders: number;
  updated_orders: number;
  inserted_items: number;
  updated_items: number;
  skipped_rows: number;
  sku_matched: number;
  sku_unmatched: number;
  new_customers: number;
  existing_customers: number;
  errors: Array<{ order_no?: string; error: string }>;
  header_row_idx: number;
  detected_headers: string[];
  sku_samples: Array<{ order_no: string; seller_codes: string[]; matched_sku: string | null; product_name: string }>;
  customer_samples: Array<{ action: string; order_no: string; customer_name: string; phone_normalized: string | null; customer_id?: number }>;
  status_samples: Array<{ order_no: string; raw_status: string; order_status: string; is_cancelled: boolean; is_returned: boolean }>;
  // [ERP-HOLD-FLOW-20260518-007] 출고중지 완료 이력 주문 자동 덮어쓰기 차단 결과.
  protected_hold_skipped_count?: number;
  protected_hold_skipped_orders?: Array<{
    order_no: string;
    receiver_name?: string | null;
    invoice_no?: string | null;
    reason?: string;
  }>;
}

// ─── Order Review ────────────────────────────

export interface DaiwaItemInfo {
  sku: string;
  product_name: string;
  stock_location: string;
}

export interface ImportBatchSummary {
  id: number;
  file_name: string | null;
  uploaded_at: string | null;
  order_count: number;
  uploaded_by: string | null;
}

export interface OrderReviewItem {
  // [ERP-TOGGLE-TODAY-REVIEW-HIDE-ORDER-LV2-FIX-DEV-20260625-001] hide 대상 식별 + 숨김 표시.
  order_id: number;
  order_no: string;
  platform: string | null;
  platform_store_name: string | null;
  order_type: string;
  order_date: string | null;
  total_product_amount: number | null;
  paid_amount: number | null;
  item_count: number;
  sku_matched_count: number;
  sku_unmatched_count: number;
  receiver_name: string | null;
  order_status: string | null;
  is_cancelled: boolean;
  is_returned: boolean;
  daiwa_direct_eligible: boolean;
  is_daiwa_direct_ship: boolean;
  daiwa_invoice_no: string | null;
  daiwa_stock_location?: string | null;
  daiwa_fulfillment_choice?: string | null;
  daiwa_items?: DaiwaItemInfo[];
  // 26차 후속 #65 — 운영 판단용 보강 필드 (펼침 행 / 인라인 표시)
  receiver_phone?: string | null;
  zipcode?: string | null;
  address1?: string | null;
  address2?: string | null;
  invoice_no?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  raw_status?: string | null;
  memo?: string | null;
  first_product_name?: string | null;
  first_option_name?: string | null;
  has_active_hold?: boolean;
  // [ERP-TOGGLE-TODAY-REVIEW-HIDE-ORDER-LV2-FIX-DEV-20260625-001] 당일주문검토 큐 숨김 상태.
  review_hidden_at?: string | null;
  review_hidden_reason?: string | null;
}

export interface OrderReviewResponse {
  total: number;
  items: OrderReviewItem[];
  order_type_counts: Record<string, number>;
  import_batches: ImportBatchSummary[];
  // 26차 후속 #64 — 미출고/합포장 통계 (해당 날짜 업로드 전체 기준, 검색/필터 무관)
  pending_total: number;
  package_count: number;
  // 26차 후속 #67 — 페이지네이션 메타
  page: number;
  page_size: number;
  total_pages: number;
}

// 26차 후속 #67 — 전체 주문 검색 (고객 DB 관리 주문 검색 탭용)

export interface OrderSearchItem {
  order_id: number;
  order_no: string;
  platform: string | null;
  platform_store_name: string | null;
  order_date: string | null;
  order_status: string | null;
  raw_status: string | null;
  is_cancelled: boolean;
  is_returned: boolean;
  customer_id: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  address2: string | null;
  invoice_no: string | null;
  paid_amount: number | null;
  item_count: number;
  total_quantity: number;
  first_product_name: string | null;
  first_option_name: string | null;
  other_distinct_count: number;
  // [ERP-ORDER-HIDE-DETAIL-AND-CUSTOMER-ORDER-HIDDEN-FILTER-DEV-20260625-001] 숨김 상태.
  review_hidden_at?: string | null;
  review_hidden_reason?: string | null;
}

export interface OrderSearchResponse {
  items: OrderSearchItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export function searchOrders(params: {
  q?: string;
  date_from?: string;
  date_to?: string;
  platform?: string;
  order_status?: string;
  sku_unmatched_only?: boolean;
  customer_id?: number;
  page?: number;
  page_size?: number;
  // [ERP-ORDER-HIDE-DETAIL-AND-CUSTOMER-ORDER-HIDDEN-FILTER-DEV-20260625-001] 숨김 필터.
  include_hidden?: boolean;
  hidden_only?: boolean;
}): Promise<OrderSearchResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (params.platform) qs.set("platform", params.platform);
  if (params.order_status) qs.set("order_status", params.order_status);
  if (params.sku_unmatched_only) qs.set("sku_unmatched_only", "true");
  if (params.customer_id != null) qs.set("customer_id", String(params.customer_id));
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  if (params.include_hidden) qs.set("include_hidden", "true");
  if (params.hidden_only) qs.set("hidden_only", "true");
  return request<OrderSearchResponse>(`/orders/search?${qs}`);
}

// [ERP-ORDER-HIDE-DETAIL-AND-CUSTOMER-ORDER-HIDDEN-FILTER-DEV-20260625-001]
// 일반 주문 숨김(soft hide, LV2 이상). 주문 상세/고객주문 내역 공용. 원본/고객/재고 무변경.
export function hideOrder(orderId: number, reason?: string): Promise<OrderReviewHideResponse> {
  return request<OrderReviewHideResponse>(`/orders/${orderId}/hide`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export function setDaiwaFulfillmentChoice(
  orderNo: string,
  choice: "hitcamp" | "daiwa",
): Promise<{ ok: boolean }> {
  return request(`/orders/${encodeURIComponent(orderNo)}/daiwa-fulfillment?choice=${choice}`, {
    method: "POST",
  });
}

export function getOrderReview(params?: {
  order_type?: string;
  sku_unmatched_only?: boolean;
  platform?: string;
  search?: string;
  // 26차 후속 #67 — page/page_size 페이지네이션
  page?: number;
  page_size?: number;
  review_date?: string;
  pending_only?: boolean;
  // [ERP-TOGGLE-TODAY-REVIEW-HIDE-ORDER-LV2-FIX-DEV-20260625-001] 숨김 포함 조회.
  include_hidden?: boolean;
}): Promise<OrderReviewResponse> {
  const qs = new URLSearchParams();
  if (params?.order_type) qs.set("order_type", params.order_type);
  if (params?.sku_unmatched_only) qs.set("sku_unmatched_only", "true");
  if (params?.platform) qs.set("platform", params.platform);
  if (params?.search) qs.set("search", params.search);
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.page_size != null) qs.set("page_size", String(params.page_size));
  if (params?.review_date) qs.set("review_date", params.review_date);
  if (params?.pending_only != null) qs.set("pending_only", String(params.pending_only));
  if (params?.include_hidden) qs.set("include_hidden", "true");
  return request<OrderReviewResponse>(`/orders/review?${qs}`);
}

// [ERP-TOGGLE-TODAY-REVIEW-HIDE-ORDER-LV2-FIX-DEV-20260625-001]
// 당일주문검토 큐에서 개별 주문 숨김(soft hide, LV2 이상). 원본 주문/고객 데이터 삭제 아님.
export interface OrderReviewHideResponse {
  order_id: number;
  order_no: string;
  review_hidden_at: string | null;
  review_hidden_reason: string | null;
  already_hidden: boolean;
  message: string;
}
export function hideOrderFromReview(orderId: number, reason?: string): Promise<OrderReviewHideResponse> {
  return request<OrderReviewHideResponse>(`/orders/review/${orderId}/hide`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export async function updateOrderType(orderNo: string, orderType: string, memo?: string): Promise<void> {
  await request(`/orders/order-type/${encodeURIComponent(orderNo)}?order_type=${orderType}${memo ? `&memo=${encodeURIComponent(memo)}` : ""}`, {
    method: "PATCH",
  });
}

export async function setDaiwaDirctShip(
  orderNo: string,
  params: { invoice_no?: string; requested_by?: string },
): Promise<{ order_no: string; is_daiwa_direct_ship: boolean; daiwa_invoice_no: string | null }> {
  const qs = new URLSearchParams();
  if (params.invoice_no) qs.set("invoice_no", params.invoice_no);
  if (params.requested_by) qs.set("requested_by", params.requested_by);
  return request(`/orders/${encodeURIComponent(orderNo)}/set-direct-ship?${qs}`, { method: "POST" });
}

// ─── Order Detail ────────────────────────────

export interface OrderItemDetail {
  id: number;
  order_item_key: string | null;
  product_name: string | null;
  option_name: string | null;
  // [ERP-ORDER-DETAIL-ITEM-OPTION2-DISPLAY-20260527-001]
  // products.option1/option2 join 결과. 매칭 SKU 가 없으면 null.
  option1?: string | null;
  option2?: string | null;
  sku: string | null;
  order_type: string;
  quantity: number | null;
  unit_price: number | null;
  line_amount: number | null;           // = 상품결제금액
  // raw 원본값 (합산 금지)
  raw_shipping_fee: number | null;
  raw_additional_shipping_fee: number | null;
  raw_order_amount: number | null;
  raw_fee_amount: number | null;
  raw_settlement_amount: number | null;
  item_status: string | null;
  raw_item_status: string | null;
  is_cancelled: boolean;
  is_returned: boolean;
  is_exchange: boolean;
  memo: string | null;
  // SKU 매칭 힌트 (Tier 2 수동 매칭 UI 분류)
  // matched | no_catalog_match | ambiguous_multi_match | bundle_set
  // | as_parts_notation | as_repair_notes | numeric_vendor_id | unknown
  match_hint?: string | null;
  // [ERP-ORDER-INVOICE-SPLIT-ITEM-MOVE-REDESIGN-20260529-001]
  // moved_out 항목은 분할자식으로 이동된 이력 → 원주문 스캔/재고차감 제외.
  split_status?: string;             // none | moved_out
  split_child_order_no?: string | null;
  split_child_invoice_no?: string | null;
  split_moved_at?: string | null;
}

export interface OrderDetail {
  id: number;
  order_no: string;
  platform: string | null;
  platform_store_name: string | null;
  order_type: string;
  order_date: string | null;
  paid_date: string | null;
  order_status: string | null;
  raw_status: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  // 정규화 금액 (대시보드 집계용)
  total_product_amount: number | null;
  shipping_fee: number | null;
  additional_shipping_fee: number | null;
  discount_amount: number | null;
  paid_amount: number | null;
  fee_amount: number | null;
  settlement_amount: number | null;
  is_cancelled: boolean;
  is_returned: boolean;
  is_exchange: boolean;
  source_file_name: string | null;
  memo: string | null;
  // [ERP-PACKING-REQUEST-NOTE-COLUMNS-AND-HIGHLIGHT-20260527-001]
  packing_request_note?: string | null;
  packing_request_updated_at?: string | null;
  packing_request_updated_by_name?: string | null;
  customer_id: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  items: OrderItemDetail[];
  // [ERP-ORDER-HIDE-DETAIL-AND-CUSTOMER-ORDER-HIDDEN-FILTER-DEV-20260625-001] 숨김 상태(배지).
  review_hidden_at?: string | null;
  review_hidden_reason?: string | null;
}

export async function getOrderDetail(orderNo: string): Promise<OrderDetail> {
  return request<OrderDetail>(`/orders/${encodeURIComponent(orderNo)}`);
}

// [ERP-PACKING-REQUEST-NOTE-COLUMNS-AND-HIGHLIGHT-20260527-001]
export interface PackingRequestNoteUpdateResponse {
  order_no: string;
  packing_request_note: string | null;
  packing_request_updated_at: string | null;
  packing_request_updated_by_name: string | null;
}

export async function updatePackingRequestNote(
  orderNo: string,
  note: string | null,
): Promise<PackingRequestNoteUpdateResponse> {
  return request<PackingRequestNoteUpdateResponse>(
    `/orders/${encodeURIComponent(orderNo)}/packing-request`,
    {
      method: "PATCH",
      body: JSON.stringify({ note }),
    },
  );
}

// [ERP-PACKING-REQUEST-TOP-BANNER-COMPLETE-AND-MANUAL-FINISH-20260528-001]
export interface PackingRequestCompleteResponse {
  ok: boolean;
  order_no: string;
  packing_request_completed_at: string;
  packing_request_completed_by_name: string;
}

export async function completePackingRequest(
  orderNo: string,
  note?: string | null,
): Promise<PackingRequestCompleteResponse> {
  return request<PackingRequestCompleteResponse>(
    `/orders/${encodeURIComponent(orderNo)}/packing-request/complete`,
    {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    },
  );
}

// ─── Product Search ──────────────────────────

export interface ProductSearchResult {
  sku: string;
  product_name: string;
  option1: string | null;
  // [ERP-MANUAL-ORDER-OPTION2-AND-SPECIAL-PART-SKU-20260527-001]
  option2?: string | null;
  brand: string | null;
  selling_price?: number | null;
  is_bundle?: boolean;
  components?: { component_sku: string; quantity: number; component_type: string }[];
  // [ERP-STORE-BARCODE-PRODUCT-NAME-DISPLAY-FIX-DEV-20260626-001]
  // /store addToCart 어댑터에서 바코드상품명 전달용(옵셔널). product-search 응답엔 없음.
  barcode_product_name?: string | null;
}

export async function searchProductsForSku(q: string, limit = 20, offset = 0): Promise<ProductSearchResult[]> {
  const off = offset ? `&offset=${offset}` : "";
  return request<ProductSearchResult[]>(`/orders/product-search?q=${encodeURIComponent(q)}&limit=${limit}${off}`);
}

// [ERP-SKU-PRODUCT-SEARCH-MODAL-INFINITE-SCROLL-PHASE2-20260603-001]
// 더보기/무한스크롤용 메타 응답 helper. with_meta=true → {items,has_more,next_offset,...}.
export interface ProductSearchPagedResponse {
  items: ProductSearchResult[];
  limit: number;
  offset: number;
  next_offset: number | null;
  has_more: boolean;
  query: string;
}

export async function searchProductsForSkuPaged(
  q: string,
  limit = 30,
  offset = 0,
): Promise<ProductSearchPagedResponse> {
  return request<ProductSearchPagedResponse>(
    `/orders/product-search?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}&with_meta=true`,
  );
}

// ─── Naver price lookup (오프라인 매장 자동 단가 채움) ───────────────
export async function getNaverPrice(sku: string): Promise<{ sku: string; naver_price: number | null }> {
  return request<{ sku: string; naver_price: number | null }>(
    `/products/${encodeURIComponent(sku)}/naver-price`,
  );
}

// ─── SKU Match ───────────────────────────────

export async function matchSku(itemId: number, sku: string, memo?: string): Promise<void> {
  await request(`/orders/order-items/${itemId}/match-sku`, {
    method: "PATCH",
    body: JSON.stringify({ sku, memo: memo || null }),
  });
}

// ─── SKU Suggestions (Tier 3 — 추천만, 자동 확정 없음) ───────────────

export interface SkuSuggestion extends ProductSearchResult {
  reason: string;
}

export interface SkuSuggestionsResponse {
  item_id: number;
  product_name: string | null;
  reason: string;
  count: number;
  suggestions: SkuSuggestion[];
}

export async function getSkuSuggestions(itemId: number): Promise<SkuSuggestionsResponse> {
  return request<SkuSuggestionsResponse>(`/orders/order-items/${itemId}/sku-suggestions`);
}

export async function importToggleOrders(file: File, companyCode = "HITCAMP"): Promise<OrderImportResult> {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_URL}/orders/import/toggle?company_code=${encodeURIComponent(companyCode)}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "서버 오류가 발생했습니다." }));
    throw new Error(err.detail || "Import 처리 중 오류가 발생했습니다.");
  }
  return res.json();
}

// ─── Shipment Holds ──────────────────────────

export interface RestockCandidate {
  sku: string;
  location_code: string;
  qty: number;
  movement_id: number;
  movement_type: string;
  source_ref: string | null;
  created_at: string | null;
}

// 26차 후속 #71 — 출고중지 row 에 동봉되는 주문 요약 (목록 응답에서만 채워짐)
export interface HoldOrderItemSummary {
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
}

export interface HoldOrderSummary {
  order_id: number | null;
  invoice_no?: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  display_customer_name: string | null;
  display_phone: string | null;
  address1: string | null;
  address2: string | null;
  platform: string | null;
  platform_store_name: string | null;
  order_status: string | null;
  order_date: string | null;
  paid_amount: number | null;
  memo: string | null;
  item_count: number;
  total_qty: number;
  product_summary: string | null;
  items: HoldOrderItemSummary[];
}

export interface ShipmentHoldRequest {
  id: number;
  order_no: string;
  invoice_no: string | null;
  request_type: string;
  status: string;
  request_reason: string | null;
  requested_by: string;
  seen_by: string | null;
  memo: string | null;
  requested_at: string;
  seen_at: string | null;
  stopped_at: string | null;
  label_discarded_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  // cancel_completed 전이 응답에만 포함
  restock_candidates?: RestockCandidate[] | null;
  // /shipment-holds 목록 응답에서만 채워짐. 단건 GET/POST/PATCH 응답은 null.
  order_summary?: HoldOrderSummary | null;
}

export interface ShipmentHoldListResponse {
  total: number;
  items: ShipmentHoldRequest[];
  status_counts: Record<string, number>;
}

export async function createShipmentHold(data: {
  order_no: string;
  invoice_no?: string;
  request_type?: string;
  request_reason?: string;
  requested_by: string;
  memo?: string;
}): Promise<ShipmentHoldRequest> {
  return request<ShipmentHoldRequest>("/shipment-holds", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getShipmentHolds(params?: {
  status?: string;
  order_no?: string;
  request_type?: string;
  limit?: number;
  offset?: number;
}): Promise<ShipmentHoldListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.order_no) qs.set("order_no", params.order_no);
  if (params?.request_type) qs.set("request_type", params.request_type);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<ShipmentHoldListResponse>(`/shipment-holds?${qs}`);
}

export async function updateShipmentHoldStatus(
  holdId: number,
  data: { status: string; seen_by?: string; memo?: string }
): Promise<ShipmentHoldRequest> {
  return request<ShipmentHoldRequest>(`/shipment-holds/${holdId}/status`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// [ERP-SHIPMENT-HOLD-FORCE-CLOSE-20260520-001]
// 이미 배송이 진행/완료된 주문의 출고중지 요청을 안전하게 종료한다.
// finalize_order_cancellation 호출 없음 — 주문 상태는 보존, hold.status 만
// cancel_completed 로 마킹 + audit_logs 기록. 권한: require_admin.
export async function forceCloseShippedShipmentHold(
  holdId: number,
): Promise<ShipmentHoldRequest> {
  return request<ShipmentHoldRequest>(
    `/shipment-holds/${holdId}/force-close-shipped`,
    { method: "POST" },
  );
}

// [ERP-SHIPMENT-HOLD-REQUEST-CANCEL-FLOW-20260527-001]
// 출고중지 "요청 취소" — 잘못 등록되었거나 더 이상 필요 없게 된 요청을 정리.
// "출고중지 완료(cancel_completed)" 와 명확히 구분되며 주문 상태는 보존된다.
// 응답 hold.status = "cancelled", memo prefix prepend.
export async function cancelShipmentHoldRequest(
  holdId: number,
  reason?: string,
): Promise<ShipmentHoldRequest> {
  return request<ShipmentHoldRequest>(
    `/shipment-holds/${holdId}/cancel-request`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? null }),
    },
  );
}

// 출고중지 force-close 대상 order_status 화이트리스트 (backend 와 동일).
export const HOLD_BLOCKED_ORDER_STATUSES = new Set<string>([
  "shipped",
  "in_transit",
  "shipping",
  "out_for_delivery",
  "delivery_area_arrived",
  "delivered",
  "completed",
]);

export async function importProductMaster(file: File, companyCode = "HITCAMP"): Promise<ImportResult> {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_URL}/products/import/product-master?company_code=${encodeURIComponent(companyCode)}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "서버 오류가 발생했습니다." }));
    throw new Error(err.detail || "Import 처리 중 오류가 발생했습니다.");
  }
  return res.json();
}

// [ERP-PRODUCT-MASTER-EXPORT-VERSIONED-XLSX-PHASE1-WITH-COST-POLICY-20260611-001]
// 상품DB → PRODUCT MASTER V2 xlsx 다운로드 + 컬럼 정의 조회.
export interface MasterColumnDef {
  key: string; title: string; sheet: string; required: boolean;
  import: boolean; export: boolean; kind: string;
  db_source: string | null; note: string;
}
export interface MasterColumnsResponse {
  column_version: string;
  export_version: string;
  columns: MasterColumnDef[];
  import_enabled: string[];
  export_enabled: string[];
  reference_only: string[];
  deferred: string[];
  required: string[];
  cost_policy: Record<string, string>;
}

export async function getMasterColumns(): Promise<MasterColumnsResponse> {
  return request<MasterColumnsResponse>("/products/master-columns");
}

export async function downloadMasterExport(opts: {
  scope?: "active" | "all";
  include_as?: boolean;
  include_discontinued?: boolean;
  include_stock_ref?: boolean;
}): Promise<void> {
  const token = getToken();
  const qs = new URLSearchParams();
  qs.set("scope", opts.scope ?? "active");
  qs.set("include_as", String(opts.include_as ?? true));
  qs.set("include_discontinued", String(opts.include_discontinued ?? false));
  qs.set("include_stock_ref", String(opts.include_stock_ref ?? true));
  const res = await fetch(`${API_URL}/products/master-export?${qs.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "다운로드 오류" }));
    throw new Error(err.detail || "PRODUCT MASTER 다운로드 중 오류가 발생했습니다.");
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename=([^;]+)/);
  const filename = m ? m[1].trim() : `PRODUCT_MASTER_V2_${Date.now()}.xlsx`;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 일부 브라우저는 click 직후 동기 revoke 시 다운로드가 중단될 수 있어 지연 해제.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// [ERP-PRODUCT-MASTER-PHASE2-META-GUARD-IMPORT-HISTORY-20260612-001]
// safe master-import (preview/apply/history) — 구 /products/import/product-master 와 별개 경로.
export interface MasterFileIdentity {
  has_meta: boolean;
  is_official_export?: boolean;
  file_purpose?: string;
  export_id?: string;
  export_version?: string;
  column_version?: string;
  exported_at?: string;
  exported_by?: string;
  source_db?: string;
  product_master_count?: string;
  as_spare_parts_count?: string;
  is_current_column_version?: boolean;
  is_same_source_db?: boolean;
}
export interface MasterRiskSummary {
  new_count: number;
  // [ERP-PRODUCT-MASTER-PREVIEW-TERMS-ACTUAL-CHANGE-CLARITY-20260615-001]
  // existing_matched_count = 기존 상품 확인(파일에 포함된 기존 SKU 수),
  // actual_changed_count = 실제 변경 예정(값이 실제로 달라지는 row 수).
  existing_matched_count: number;
  actual_changed_count: number;
  update_count: number; // 하위호환 별칭 = actual_changed_count
  price_change_count: number;
  large_price_drop_count: number;
  large_price_increase_count: number;
  duplicate_sku_count: number;
  ignored_reference_columns: string[];
  ignored_deferred_columns: string[];
  errors: number;
  blocking_warnings: number;
}
export interface MasterApplyGuard {
  can_apply: boolean;
  requires_admin_confirm: boolean;
  requires_strong_confirm_text: boolean;
  confirm_text: string;
}
// safe preview 응답 (sheet_results 는 그대로 통과, Phase2 필드만 타입화)
export interface MasterPreviewResponse {
  mode: string;
  ignored_sheets: string[];
  sheet_results: Record<string, unknown>[];
  error?: string;
  file_identity: MasterFileIdentity;
  risk_level: "no_change" | "low" | "medium" | "high" | "blocked";
  risk_warnings: string[];
  risk_summary: MasterRiskSummary;
  apply_guard: MasterApplyGuard;
}
export interface MasterImportHistoryItem {
  id: number;
  file_name: string | null;
  imported_by: string | null;
  row_count: number;
  created_count: number;
  updated_count: number;
  skipped_count: number;
  warning_count: number;
  error_count: number;
  status: string;
  error_summary: string | null;
  created_at: string | null;
  completed_at: string | null;
}

async function _masterUpload<T>(path: string, file: File): Promise<T> {
  const token = getToken();
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (res.status === 401) {
    localStorage.removeItem("access_token");
    window.location.href = "/login";
    throw new Error("인증이 만료되었습니다.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "서버 오류" }));
    const detail = err.detail;
    if (detail && typeof detail === "object") {
      throw new ApiError(detail.message || "요청 실패", res.status, detail.error_code ?? null, detail);
    }
    throw new Error(typeof detail === "string" ? detail : "처리 중 오류가 발생했습니다.");
  }
  return res.json();
}

export function previewMasterImport(file: File): Promise<MasterPreviewResponse> {
  return _masterUpload<MasterPreviewResponse>("/products/master-import/preview", file);
}
export function applyMasterImport(file: File): Promise<Record<string, unknown>> {
  return _masterUpload<Record<string, unknown>>("/products/master-import/apply", file);
}
export async function getMasterImportHistory(limit = 20): Promise<MasterImportHistoryItem[]> {
  return request<MasterImportHistoryItem[]>(`/products/master-import/history?limit=${limit}`);
}

// ─── Returns (반품/교환 검수) ──────────────────────────────────

export interface ReturnInspectionResponse {
  id: number;
  return_receipt_id: number;
  order_no: string;
  order_item_id: number | null;
  inspection_result: string;
  restock_decision: string;
  inspected_qty: number;
  missing_qty: number;
  damaged_qty: number;
  opened_box: boolean;
  used_trace: boolean;
  missing_parts: boolean;
  notes: string | null;
  inspected_by: string;
  inspected_at: string;
  created_at: string;
  updated_at: string;
}

// 회수 유형 (교환/반품/AS회수 검수장 공통 흐름)
export type RecoveryType =
  | "courier_pickup"
  | "customer_ships"
  | "walkin_repair_same_day_pickup"
  | "walkin_repair_ship_later";

// 검수 후 처리결정
export type RestockDecision =
  | "restock"
  | "quarantine"
  | "as_transfer"
  | "exchange_resend"
  | "discard"
  | "hold";

export interface LinkedStockMovement {
  id: number;
  sku: string;
  location_code: string;
  movement_type: string;
  quantity_delta: number;
  memo: string | null;
  created_at: string;
}

export interface ReturnReceiptResponse {
  id: number;
  order_no: string | null;
  order_item_id: number | null;
  as_request_id: number | null;
  return_type: string;
  recovery_type: RecoveryType;
  recovery_courier: string | null;
  return_invoice_no: string | null;
  exchange_resend_invoice_no: string | null;
  visit_date: string | null;
  onsite_received_at: string | null;
  collected_at: string | null;
  received_at: string | null;
  status: string;
  received_by: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
  inspections: ReturnInspectionResponse[];
  // server-join inline 주문/고객 정보
  customer_name: string | null;
  customer_phone: string | null;
  customer_address: string | null;
  // [ERP-RETURN-INSPECTION-ORDERER-RECIPIENT-DISPLAY-20260527-001]
  orderer_name?: string | null;
  orderer_phone?: string | null;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  order_date: string | null;
  product_name: string | null;
  option_name: string | null;
  item_quantity: number | null;
  sku: string | null;
  order_memo: string | null;
  order_invoice_no: string | null;
  // 검수창고 drill-down
  linked_movements: LinkedStockMovement[];
  quarantine_reflected_qty: number;
  restock_reflected_qty: number;
  // 교환 재발송 링크 (3차)
  exchange_resend_order_id: number | null;
  exchange_resend_order_no: string | null;
  exchange_resend_order_status: string | null;
  exchange_resend_pick_status: string | null;
  exchange_resend_pick_invoice_no: string | null;
  exchange_resend_shipped_at: string | null;
  // AS 회수 링크 (5차)
  as_no: string | null;
  as_symptom: string | null;
  as_status: string | null;
  as_pickup_type: string | null;
  // 26차 후속 — 입금 확인 큐 통합 Phase 1.
  expected_payment_amount: number | null;
  payment_reason: ReturnPaymentReason | null;
  payment_status: ReturnPaymentStatus;
  payment_requested_at: string | null;
  payment_confirmed_at: string | null;
  payment_confirmed_by_user_id: number | null;
}

// 26차 후속 — 입금 확인 큐 통합 Phase 1.
export type ReturnPaymentReason =
  | "additional_charge"
  | "exchange_diff"
  | "round_trip_shipping"
  | "return_shipping"
  | "reshipping_fee"
  | "other";
export type ReturnPaymentStatus =
  | "none"
  | "pending"
  | "confirmed"
  | "waived"
  | "cancelled";

export interface ReturnReceiptListResponse {
  total: number;
  items: ReturnReceiptResponse[];
  status_counts: Record<string, number>;
  recovery_type_counts: Record<string, number>;
  decision_counts: Record<string, number>;
  quarantine_total_qty: number;
  return_type_counts: Record<string, number>;
}

export async function createReturnReceipt(data: {
  order_no: string;
  order_item_id?: number | null;
  return_type?: string;
  recovery_type?: RecoveryType;
  recovery_courier?: string | null;
  return_invoice_no?: string | null;
  visit_date?: string | null;
  received_by?: string | null;
  memo?: string | null;
  // 26차 후속 — 입금 확인 큐 통합 Phase 1 (선택 입력).
  expected_payment_amount?: number | null;
  payment_reason?: ReturnPaymentReason | null;
}): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>("/returns/receipts", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// 26차 후속 — 반품/교환 입금 확인 / 면제 helper.
export function confirmReturnReceiptPayment(
  receiptId: number,
  memo?: string,
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(
    `/returns/receipts/${receiptId}/confirm-payment`,
    { method: "POST", body: JSON.stringify({ memo: memo ?? null }) },
  );
}
export function waiveReturnReceiptPayment(
  receiptId: number,
  memo?: string,
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(
    `/returns/receipts/${receiptId}/waive-payment`,
    { method: "POST", body: JSON.stringify({ memo: memo ?? null }) },
  );
}

// [ERP-RETURN-RECEIPT-CANCEL-FLOW-20260527-001]
// 회수대기 상태 회수건 취소. reason 은 선택, memo 에 prefix 로 기록된다.
export function cancelReturnReceipt(
  receiptId: number,
  reason?: string,
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(
    `/returns/receipts/${receiptId}/cancel`,
    { method: "POST", body: JSON.stringify({ reason: reason ?? null }) },
  );
}

export async function getReturnReceipts(params?: {
  status?: string;
  order_no?: string;
  return_type?: string;
  recovery_type?: RecoveryType;
  decision?: RestockDecision;
  quarantine_only?: boolean;
  limit?: number;
  offset?: number;
}): Promise<ReturnReceiptListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.order_no) qs.set("order_no", params.order_no);
  if (params?.return_type) qs.set("return_type", params.return_type);
  if (params?.recovery_type) qs.set("recovery_type", params.recovery_type);
  if (params?.decision) qs.set("decision", params.decision);
  if (params?.quarantine_only) qs.set("quarantine_only", "true");
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const query = qs.toString();
  return request<ReturnReceiptListResponse>(`/returns/receipts${query ? "?" + query : ""}`);
}

export async function getReceiptStockMovements(receiptId: number): Promise<LinkedStockMovement[]> {
  return request<LinkedStockMovement[]>(`/returns/receipts/${receiptId}/stock-movements`);
}

// 17차 1단계 — 반품/교환회수 검수창고 SKU별 원천 회수건 조회 (read-only)
export interface QuarantineLotItem {
  movement_id: number;
  receipt_id: number;
  return_type: string;
  order_no: string | null;
  return_receipt_memo: string | null;
  customer_name: string | null;
  product_name: string | null;
  option_name: string | null;
  sku: string;
  quantity: number;
  inspected_at: string | null;
  restock_decision: string | null;
  created_at: string;
}

export interface QuarantineLotListResponse {
  sku: string;
  total_quantity: number;
  items: QuarantineLotItem[];
}

export async function getQuarantineLotsBySku(sku: string): Promise<QuarantineLotListResponse> {
  return request<QuarantineLotListResponse>(
    `/returns/quarantine-lots?sku=${encodeURIComponent(sku)}`
  );
}

export interface ExchangeResendItemIn {
  sku: string;
  product_name: string;
  option_name?: string | null;
  quantity?: number;
  unit_price?: number;
}

export async function createExchangeResendOrder(
  receiptId: number,
  data: {
    receiver_name?: string | null;
    receiver_phone?: string | null;
    zipcode?: string | null;
    address1?: string | null;
    address2?: string | null;
    items?: ExchangeResendItemIn[];
    shipping_fee?: number;
    memo?: string | null;
  } = {},
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(`/returns/receipts/${receiptId}/exchange-resend`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateReturnReceiptStatus(
  receiptId: number,
  data: {
    status: string;
    received_by?: string | null;
    recovery_type?: RecoveryType | null;
    recovery_courier?: string | null;
    return_invoice_no?: string | null;
    exchange_resend_invoice_no?: string | null;
    visit_date?: string | null;
    onsite_received_at?: string | null;
    collected_at?: string | null;
    received_at?: string | null;
    memo?: string | null;
  }
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(`/returns/receipts/${receiptId}/status`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function createAsFromReturn(receiptId: number): Promise<{ as_no: string; as_id: number }> {
  return request<{ as_no: string; as_id: number }>(`/returns/receipts/${receiptId}/create-as`, {
    method: "POST",
  });
}

/** AS 원본 → 교환/반품/AS회수 검수장 허브로 ingest (5차 정리). */
export async function createReceiptFromAs(
  asId: number,
  data: {
    recovery_type?: RecoveryType;
    recovery_courier?: string | null;
    return_invoice_no?: string | null;
    visit_date?: string | null;
    memo?: string | null;
  } = {},
): Promise<ReturnReceiptResponse> {
  return request<ReturnReceiptResponse>(`/returns/receipts/from-as?as_id=${asId}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function createReturnInspection(data: {
  return_receipt_id: number;
  order_no: string;
  order_item_id?: number | null;
  inspection_result?: string;
  restock_decision?: string;
  inspected_qty?: number;
  missing_qty?: number;
  damaged_qty?: number;
  opened_box?: boolean;
  used_trace?: boolean;
  missing_parts?: boolean;
  notes?: string | null;
  inspected_by: string;
}): Promise<ReturnInspectionResponse> {
  return request<ReturnInspectionResponse>("/returns/inspections", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateReturnInspection(
  inspectionId: number,
  data: Partial<{
    inspection_result: string;
    restock_decision: string;
    inspected_qty: number;
    missing_qty: number;
    damaged_qty: number;
    opened_box: boolean;
    used_trace: boolean;
    missing_parts: boolean;
    notes: string | null;
    inspected_by: string;
  }>
): Promise<ReturnInspectionResponse> {
  return request<ReturnInspectionResponse>(`/returns/inspections/${inspectionId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ─── Daiwa / Vendors ──────────────────────────────────────────

export interface DaiwaImportResult {
  sheet_type: string;
  total_rows: number;
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  duplicate_jan_count?: number;
  errors: { row?: number; error: string }[];
}

export interface DaiwaSummary {
  inventory_snapshot_count: number;
  latest_snapshot_date: string | null;
  available_product_count: number;
  price_policy_count: number;
  markup_rule_count: number;
  category_mapping_count: number;
  origin_mapping_count: number;
  supply_source_count: number;
  unprocessed_direct_ship_count: number;
  unregistered_product_count: number;
  stock_zero_sku_count: number;
  // [ERP-DAIWA-ORDER-QUEUE-RESTOCKED-CANDIDATE-AND-NAVER-DK-ZERO-FILTER-20260605-001]
  restocked_candidate_count: number;
}

export interface DaiwaPriceCalc {
  jan_code: string;
  product_group: string | null;
  base_cost: number | null;
  online_min_price_krw: number | null;
  calculated_price: number | null;
  price_source: string;
  kake_rate: number | null;
  margin_rate_applied: number | null;
}

async function uploadFile<T>(path: string, file: File, extraParams?: Record<string, string>): Promise<T> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const formData = new FormData();
  formData.append("file", file);
  let url = `${API_URL}${path}`;
  if (extraParams) {
    const qs = new URLSearchParams(extraParams);
    url += `?${qs}`;
  }
  const res = await fetch(url, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "업로드 실패" }));
    throw new Error(err.detail || "업로드 실패");
  }
  return res.json();
}

export async function getDaiwaSummary(): Promise<DaiwaSummary> {
  return request<DaiwaSummary>("/vendors/daiwa/summary");
}

export interface DaiwaUnregisteredProduct {
  jan_code: string;
  product_name: string | null;
  product_group: string | null;
  base_cost: number | null;
}

export async function getDaiwaUnregisteredProducts(limit = 50): Promise<{ total: number; items: DaiwaUnregisteredProduct[] }> {
  return request(`/vendors/daiwa/unregistered-products?limit=${limit}`);
}

export async function importDaiwaInventory(file: File): Promise<DaiwaImportResult> {
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/inventory", file);
}

export async function importDaiwaPricePolicies(file: File, sheetName?: string): Promise<DaiwaImportResult> {
  const params: Record<string, string> = {};
  if (sheetName) params.sheet_name = sheetName;
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/price-policies", file, params);
}

export async function importDaiwaKakeRules(file: File, sheetName?: string): Promise<DaiwaImportResult> {
  const params: Record<string, string> = {};
  if (sheetName) params.sheet_name = sheetName;
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/kake-rules", file, params);
}

export async function importDaiwaCategoryMappings(file: File, sheetName?: string): Promise<DaiwaImportResult> {
  const params: Record<string, string> = {};
  if (sheetName) params.sheet_name = sheetName;
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/category-mappings", file, params);
}

export async function importDaiwaOriginMappings(file: File, sheetName?: string): Promise<DaiwaImportResult> {
  const params: Record<string, string> = {};
  if (sheetName) params.sheet_name = sheetName;
  return uploadFile<DaiwaImportResult>("/vendors/daiwa/import/origin-mappings", file, params);
}

export async function getDaiwaPriceCalc(
  janCode: string,
  productGroup?: string,
  baseCost?: number
): Promise<DaiwaPriceCalc> {
  const qs = new URLSearchParams();
  if (productGroup) qs.set("product_group", productGroup);
  if (baseCost != null) qs.set("base_cost", String(baseCost));
  return request<DaiwaPriceCalc>(`/vendors/daiwa/price/${encodeURIComponent(janCode)}?${qs}`);
}

export interface DaiwaNaverBulkPreview {
  total_rows: number;
  price_source_counts: Record<string, number>;
  category_mapped_count: number;
  origin_mapped_count: number;
  internet_floor_applied_count?: number;
  sample_rows: Record<string, unknown>[];
}

export async function getDaiwaNaverBulkPreview(limit = 10): Promise<DaiwaNaverBulkPreview> {
  return request<DaiwaNaverBulkPreview>(`/vendors/daiwa/export/naver-bulk/preview?limit=${limit}`);
}

// ── Daiwa Direct Ship Export ───────────────────────────────────

export type FulfillmentClass = "hitcamp" | "daiwa" | "decide" | "needs_match";

export interface DirectShipRow {
  order_no: string;
  product_name: string;
  receiver_name: string;
  phone: string;
  zipcode: string;
  address: string;
  jan_code: string;
  quantity: number;
  delivery_message: string;
  eligible_reason: string;
  /** 분류 (백엔드 추가, 신규 필드라 optional 로 받아 기존 사용처 깨지지 않게 함) */
  fulfillment_class?: FulfillmentClass;
  hitcamp_qty?: number;
  daiwa_qty?: number;
  /** 운영자 결정값 (orders.daiwa_fulfillment_choice) — 미결정시 null */
  daiwa_fulfillment_choice?: "hitcamp" | "daiwa" | null;
  /** 같은 주문 내 다이와 후보 아이템 수. >1 이면 결정 잠금. */
  daiwa_eligible_item_count?: number;
  /** 결정 저장 가능 여부 (단일 후보 아이템 주문). */
  is_decision_writable?: boolean;
  /** [ERP-DAIWA-ACTION-QUEUE-SKU-UNMATCHED-NEEDS-MATCH-FIX-20260528-001]
   *  sku NULL/empty 인 다이와 후보 (option_name JAN + master 매칭으로 식별). */
  sku_missing?: boolean;
  candidate_jan_code?: string | null;
  candidate_source?: string | null;
  match_reason?: string | null;
  option_name?: string;
}

// [ERP-DAIWA-SKU-MISSING-CANDIDATE-PRODUCT-MAPPING-PREVIEW-20260529-001]
// SKU 미매칭 다이와 후보 → products 등록/매핑 Preview (Preview-only).
export interface DaiwaJanMasterInfo {
  jan_code: string;
  supplier_product_code?: string | null;
  name_ko?: string | null;
  name_en?: string | null;
  dk_stock_qty?: number | null;
}
export interface DaiwaNaverListingInfo {
  jan_code: string;
  seller_product_code?: string | null;
  product_name?: string | null;
  listing_status?: string | null;
}
export interface SuggestedProductPreview {
  sku: string;
  barcode?: string | null;
  product_name: string;
  brand: string;
  source: string;
  as_available: boolean;
  product_status: string;
}
export interface ExistingProductMatch {
  sku: string;
  product_name?: string | null;
  barcode?: string | null;
  is_active: boolean;
}
export interface SkuMissingCandidateItem {
  order_no: string;
  order_item_id: number;
  product_name: string;
  option_name?: string | null;
  quantity: number;
  candidate_jan_code?: string | null;
  candidate_source?: string | null;
  daiwa_jan_master?: DaiwaJanMasterInfo | null;
  naver_listing?: DaiwaNaverListingInfo | null;
  snapshot_qty?: number | null;
  products_match?: ExistingProductMatch | null;
  candidate_products: ExistingProductMatch[];
  suggested_product?: SuggestedProductPreview | null;
  recommended_action: "create_product_preview" | "map_existing_product_preview" | "manual_review";
  warning?: string | null;
}
export interface SkuMissingCandidatesResponse {
  total: number;
  items: SkuMissingCandidateItem[];
}
export function getDaiwaSkuMissingCandidates(limit = 50): Promise<SkuMissingCandidatesResponse> {
  return request(`/vendors/daiwa/sku-missing-candidates?limit=${limit}`);
}

// [ERP-PACKING-REQUEST-CARRYOVER-HOLD-ORDER-VISIBILITY-20260529-001]
// 미완료 포장요청 carryover — 주문일 무관. /picking 상단 amber 배너용.
export interface PendingPackingRequestItem {
  order_no: string;
  invoice_no: string | null;
  order_date: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  packing_request_note: string | null;
  packing_request_updated_at: string | null;
  packing_request_updated_by_name: string | null;
  packing_request_completed_at: string | null;
  order_status: string | null;
  shipment_pick_status: string | null;
  can_open_by_invoice: boolean;
}
export interface PendingPackingRequestsResponse {
  total: number;
  items: PendingPackingRequestItem[];
}
export function getPendingPackingRequests(limit = 100): Promise<PendingPackingRequestsResponse> {
  return request(`/orders/packing/pending-requests?limit=${limit}`);
}

export interface DirectShipClassCounts {
  hitcamp: number;
  daiwa: number;
  decide: number;
  needs_match: number;
}

export interface DirectShipPreview {
  total_orders: number;
  total_rows: number;
  sample_rows: DirectShipRow[];
  class_counts?: DirectShipClassCounts;
}

export interface DirectShipFilters {
  order_nos?: string;
  order_date_from?: string;
  order_date_to?: string;
  platform?: string;
  fulfillment_class?: FulfillmentClass;
  /** [ERP-DAIWA-ORDER-ACTION-QUEUE-REDEFINE-20260522-001]
   *  'all' (기본, Export 호환) | 'action_queue' (미처리 액션 큐 카운트) */
  scope?: "all" | "action_queue";
}

export async function getDaiwaDirectShipPreview(
  filters: DirectShipFilters = {},
  limit = 10
): Promise<DirectShipPreview> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (filters.order_nos) qs.set("order_nos", filters.order_nos);
  if (filters.order_date_from) qs.set("order_date_from", filters.order_date_from);
  if (filters.order_date_to) qs.set("order_date_to", filters.order_date_to);
  if (filters.platform) qs.set("platform", filters.platform);
  if (filters.fulfillment_class) qs.set("fulfillment_class", filters.fulfillment_class);
  if (filters.scope) qs.set("scope", filters.scope);
  return request<DirectShipPreview>(`/vendors/daiwa/export/direct-ship/preview?${qs}`);
}

export async function downloadDaiwaDirectShip(
  filters: DirectShipFilters = {},
  includeDecide = false,
): Promise<void> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const qs = new URLSearchParams();
  if (filters.order_nos) qs.set("order_nos", filters.order_nos);
  if (filters.order_date_from) qs.set("order_date_from", filters.order_date_from);
  if (filters.order_date_to) qs.set("order_date_to", filters.order_date_to);
  if (filters.platform) qs.set("platform", filters.platform);
  if (includeDecide) qs.set("include_decide", "true");
  // [ERP-DAIWA-ORDER-MANAGEMENT-TABS-DAILY-SCOPE-20260527-001]
  if (filters.scope) qs.set("scope", filters.scope);
  const res = await fetch(`${API_URL}/vendors/daiwa/export/direct-ship?${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Export 실패" }));
    throw new Error(err.detail || "Export 실패");
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const today = new Date();
  const yyyymmdd = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
  const filename = match?.[1] || `daiwa_direct_orders_${yyyymmdd}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Daiwa Inventory Search (탭1: 재고 조회) ─────────────────────

export interface DaiwaLatestSnapshotInfo {
  snapshot_date: string | null;
  row_count: number;
  latest_created_at: string | null;
}

export interface DaiwaInventorySummaryCard {
  total: number;
  in_stock: number;
  out_of_stock: number;
  category_mapped: number;
  origin_mapped: number;
  kake_mapped: number;
}

export interface DaiwaInventorySearchItem {
  id: number;
  jan_code: string;
  product_name: string | null;          // 다이와 스냅샷 product_name (vendor 원본)
  vendor_category: string | null;
  available_qty: number;
  is_available: boolean;
  snapshot_date: string;
  sku_registered: boolean;
  erp_product_name: string | null;      // Product.product_name (#74)
  model_name: string | null;
  option1: string | null;
  option2: string | null;
  current_price: number | null;         // Product.sale_price (#74)
  naver_category_code: string | null;
  naver_category_name: string | null;
  naver_origin_code: string | null;
  kake_rate: number | null;
  policy_price: number | null;
  // 정책가 비교 상태 (#74): "normal" | "violation" | "no_policy" | "no_product"
  policy_status: string;
  price_diff: number | null;
}

export interface DaiwaInventorySearchResponse {
  latest_snapshot: DaiwaLatestSnapshotInfo;
  summary: DaiwaInventorySummaryCard;
  items: DaiwaInventorySearchItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DaiwaInventorySearchParams {
  q?: string;
  in_stock_only?: boolean;
  unmapped_category_only?: boolean;
  unmapped_origin_only?: boolean;
  unmapped_kake_only?: boolean;
  // 정책가 비교 필터 (#74)
  with_policy_only?: boolean;
  violation_only?: boolean;
  no_policy_only?: boolean;
  page?: number;
  page_size?: number;
}

export async function searchDaiwaInventory(
  params: DaiwaInventorySearchParams = {},
): Promise<DaiwaInventorySearchResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.in_stock_only) qs.set("in_stock_only", "true");
  if (params.unmapped_category_only) qs.set("unmapped_category_only", "true");
  if (params.unmapped_origin_only) qs.set("unmapped_origin_only", "true");
  if (params.unmapped_kake_only) qs.set("unmapped_kake_only", "true");
  if (params.with_policy_only) qs.set("with_policy_only", "true");
  if (params.violation_only) qs.set("violation_only", "true");
  if (params.no_policy_only) qs.set("no_policy_only", "true");
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 100));
  return request<DaiwaInventorySearchResponse>(`/vendors/daiwa/inventory/search?${qs}`);
}

// ── Daiwa JAN Master (Phase 3, #79) — current table 기반 재고 조회 ─

export interface DaiwaJanMasterItem {
  id: number;
  jan_code: string;
  supplier_product_code: string | null;
  name_en: string | null;
  name_ko: string | null;
  min_order_qty: number | null;
  catalog_page: string | null;
  price_jpy: number | null;
  msrp_krw: number | null;
  product_group: string | null;
  barcode: string | null;
  category_large: string | null;
  category_middle: string | null;
  category_small: string | null;
  origin_country: string | null;
  dk_stock_qty: number;
  preorder_qty: number;
  expected_arrival_date: string | null;
  source_file_name: string | null;
  last_uploaded_at: string | null;
  is_new_jan: boolean;
  new_jan_detected_at: string | null;
  new_jan_acknowledged_at: string | null;
  new_jan_acknowledged_by_user_id: number | null;
  direct_ship_eligible: boolean;
  // [ERP-DAIWA-HITCAMP-STOCK-COLUMN-20260520-001] 히트캠프 출고 가능 재고 합산.
  // 매칭: products.sku == jan_code (Q1=a). 합산 location: office/warehouse_401402/
  // warehouse/rocket_growth (Q2=Q). quantity 컬럼 SUM (Q3=X), reserved 차감 X (Q4=M).
  hitcamp_stock_qty: number;
  // [ERP-DAIWA-INVENTORY-OPS-FILTERS-20260520-001] 네이버 등록 여부 + URL.
  naver_listed: boolean;
  naver_listing_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface DaiwaJanMasterListResponse {
  items: DaiwaJanMasterItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  new_jan_count: number;
  last_uploaded_at_max: string | null;
}

export interface DaiwaJanMasterListParams {
  q?: string;
  dk_in_stock_only?: boolean;
  dk_zero_only?: boolean;
  direct_ship_only?: boolean;
  new_jan_only?: boolean;
  // [ERP-DAIWA-CATEGORY-FILTER-20260520-001] 대/중/소 카테고리 계층 필터.
  major_category?: string;
  middle_category?: string;
  minor_category?: string;
  // [ERP-DAIWA-HITCAMP-STOCK-FILTER-20260520-001] 히트캠프 실재고 있는 JAN 만.
  hitcamp_stock_only?: boolean;
  // [ERP-DAIWA-INVENTORY-OPS-FILTERS-20260520-001] 운영 조합 필터 + 정렬.
  dk_stock_hitcamp_zero_only?: boolean;
  dk_zero_hitcamp_stock_only?: boolean;
  sort_by?: "hitcamp_stock_qty";
  sort_dir?: "asc" | "desc";
  // [ERP-DAIWA-JAN-MASTER-BAD-ROW-DEFAULT-HIDE-UI-20260604-001]
  // true 면 leading-zero bad duplicate(앞자리 0 손실 중복) row 포함. 기본 false=숨김.
  include_bad_duplicates?: boolean;
  page?: number;
  page_size?: number;
}

// [ERP-DAIWA-CATEGORY-FILTER-20260520-001] 카테고리 옵션 트리.
export interface DaiwaCategoryOptions {
  major_categories: string[];
  middle_by_major: Record<string, string[]>;
  minor_by_middle: Record<string, string[]>;
}

export async function getDaiwaJanMasterCategoryOptions(): Promise<DaiwaCategoryOptions> {
  return request<DaiwaCategoryOptions>("/vendors/daiwa/jan-master/category-options");
}

export interface DaiwaJanMasterUploadResult {
  sheet_type: string;
  mode: string;
  seed_mode: boolean;
  source_file_name: string | null;
  parsed_rows: number;
  skipped_empty_jan_rows: number;
  invalid_jan_rows: number;
  unusual_jan_rows: number;
  normalized_jan_count: number;
  duplicate_jans_in_file: number;
  duplicate_rows_in_file: number;
  inserted_count: number;
  updated_count: number;
  missing_zeroed_count: number;
  new_jan_count: number;
  acknowledged_preserved_count: number;
  total_current_rows_after: number;
  // [ERP-DAIWA-ZERO-HISTORY-RESPONSE-FIELD-20260526-001]
  full_zero_history_appended_count?: number;
  normalize_samples: Record<string, unknown>[];
  errors: Record<string, unknown>[];
}

// [ERP-DAIWA-JAN-UPLOAD-UI-PREVIEW-SAFETY-20260526-001]
export interface DaiwaJanMasterUploadPreviewResult {
  sheet_type: string;
  mode: string;
  seed_mode: boolean;
  source_file_name: string | null;
  parsed_rows: number;
  skipped_empty_jan_rows: number;
  invalid_jan_rows: number;
  unusual_jan_rows: number;
  normalized_jan_count: number;
  duplicate_jans_in_file: number;
  duplicate_rows_in_file: number;
  estimated_insert_count: number;
  estimated_update_count: number;
  estimated_missing_zeroed_count: number;
  estimated_full_zero_history_appended_count: number;
  estimated_new_jan_count: number;
  total_current_rows_before: number;
  total_current_rows_after_estimated: number;
  zero_out_samples: Array<{
    jan_code: string;
    supplier_product_code?: string | null;
    name_ko?: string | null;
    old_dk_stock_qty: number;
    new_dk_stock_qty: number;
  }>;
  zero_out_confirm_threshold: number;
  zero_out_confirm_required: boolean;
  normalize_samples: Record<string, unknown>[];
  errors: Record<string, unknown>[];
}

export interface DaiwaJanMasterAckResponse {
  id: number;
  jan_code: string;
  is_new_jan: boolean;
  new_jan_acknowledged_at: string | null;
  new_jan_acknowledged_by_user_id: number | null;
}

export async function getDaiwaJanMaster(
  params: DaiwaJanMasterListParams = {},
): Promise<DaiwaJanMasterListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.dk_in_stock_only) qs.set("dk_in_stock_only", "true");
  if (params.dk_zero_only) qs.set("dk_zero_only", "true");
  if (params.direct_ship_only) qs.set("direct_ship_only", "true");
  if (params.new_jan_only) qs.set("new_jan_only", "true");
  // [ERP-DAIWA-CATEGORY-FILTER-20260520-001]
  if (params.major_category) qs.set("major_category", params.major_category);
  if (params.middle_category) qs.set("middle_category", params.middle_category);
  if (params.minor_category) qs.set("minor_category", params.minor_category);
  if (params.hitcamp_stock_only) qs.set("hitcamp_stock_only", "true");
  if (params.dk_stock_hitcamp_zero_only) qs.set("dk_stock_hitcamp_zero_only", "true");
  if (params.dk_zero_hitcamp_stock_only) qs.set("dk_zero_hitcamp_stock_only", "true");
  if (params.sort_by) qs.set("sort_by", params.sort_by);
  if (params.sort_dir) qs.set("sort_dir", params.sort_dir);
  if (params.include_bad_duplicates) qs.set("include_bad_duplicates", "true");
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 100));
  return request<DaiwaJanMasterListResponse>(`/vendors/daiwa/jan-master?${qs}`);
}

export async function uploadDaiwaJanMaster(
  file: File,
  opts: { mode?: "full" | "partial"; seed_mode?: boolean; confirm_zero_out?: boolean } = {},
): Promise<DaiwaJanMasterUploadResult> {
  const params: Record<string, string> = {
    mode: opts.mode ?? "partial",
    seed_mode: String(Boolean(opts.seed_mode)),
    confirm_zero_out: String(Boolean(opts.confirm_zero_out)),
  };
  return uploadFile<DaiwaJanMasterUploadResult>(
    "/vendors/daiwa/jan-master/upload",
    file,
    params,
  );
}

// [ERP-DAIWA-JAN-UPLOAD-UI-PREVIEW-SAFETY-20260526-001]
export async function previewDaiwaJanMaster(
  file: File,
  opts: { mode?: "full" | "partial"; seed_mode?: boolean } = {},
): Promise<DaiwaJanMasterUploadPreviewResult> {
  const params: Record<string, string> = {
    mode: opts.mode ?? "partial",
    seed_mode: String(Boolean(opts.seed_mode)),
  };
  return uploadFile<DaiwaJanMasterUploadPreviewResult>(
    "/vendors/daiwa/jan-master/preview",
    file,
    params,
  );
}

export async function acknowledgeDaiwaNewJan(id: number): Promise<DaiwaJanMasterAckResponse> {
  return request<DaiwaJanMasterAckResponse>(
    `/vendors/daiwa/jan-master/${id}/acknowledge-new`,
    { method: "POST" },
  );
}

export interface DaiwaJanMasterAckAllResponse {
  ok: boolean;
  updated_count: number;
  acknowledged_by_user_id: number;
  acknowledged_at: string;
}

export async function acknowledgeAllDaiwaNewJan(): Promise<DaiwaJanMasterAckAllResponse> {
  return request<DaiwaJanMasterAckAllResponse>(
    "/vendors/daiwa/jan-master/acknowledge-all-new",
    { method: "POST" },
  );
}

// ── Daiwa Naver Listing Master (Phase B/C/D, #80) ─────────────

export interface DaiwaNaverListingUploadResult {
  sheet_type: string;
  mode: "partial" | "full";
  source_file_name: string | null;
  header_row_index: number | null;
  data_start_row_index: number | null;
  parsed_rows: number;
  skipped_empty_jan_rows: number;
  invalid_jan_rows: number;
  normalized_jan_count: number;
  duplicate_jans_in_file: number;
  duplicate_rows_in_file: number;
  inserted_count: number;
  updated_count: number;
  ended_count: number;
  seller_code_conflict_count: number;
  matched_daiwa_jan_count: number;
  unmatched_daiwa_jan_count: number;
  total_current_rows_after: number;
  canonical_jan_redirect_count: number;
  canonical_jan_conflict_count: number;
  canonical_jan_redirect_examples: Record<string, unknown>[];
  canonical_jan_conflict_examples: Record<string, unknown>[];
  errors: Record<string, unknown>[];
}

export interface DaiwaNaverListingSummary {
  total_listings: number;
  active_count: number;
  paused_count: number;
  ended_count: number;
  unknown_count: number;
  matched_daiwa_jan_count: number;
  unmatched_daiwa_jan_count: number;
  daiwa_master_total_count: number;
  daiwa_master_not_listed_count: number;
  candidate_count: number;
  last_uploaded_at_max: string | null;
}

export async function uploadDaiwaNaverListing(
  file: File,
  mode: "partial" | "full" = "partial",
): Promise<DaiwaNaverListingUploadResult> {
  return uploadFile<DaiwaNaverListingUploadResult>(
    "/vendors/daiwa/naver-listing/upload",
    file,
    { mode },
  );
}

export async function getDaiwaNaverListingSummary(): Promise<DaiwaNaverListingSummary> {
  return request<DaiwaNaverListingSummary>("/vendors/daiwa/naver-listing/summary");
}

// ── Daiwa Naver Listing — 목록 (Phase D 2차, #80) ──────────────

export interface DaiwaNaverListingItem {
  id: number;
  jan_code: string;
  seller_product_code: string | null;
  naver_product_no: string | null;
  listing_url: string | null;
  listing_status: string;
  product_name: string | null;
  category_path: string | null;
  sale_price_krw: number | null;
  source_file_name: string | null;
  last_uploaded_at: string | null;
  first_seen_at: string;
  last_seen_at: string;
  created_at: string;
  updated_at: string;
  // daiwa_jan_master LEFT JOIN 파생
  daiwa_master_matched: boolean;
  dk_stock_qty: number | null;
  min_order_qty: number | null;
  direct_ship_eligible: boolean;
  name_ko: string | null;
  product_group: string | null;
  // [ERP-DAIWA-NAVER-DIRECT-SOLDOUT-RESTOCK-ACTION-STATE-20260605-001]
  action_status: string | null;
  soldout_done_at: string | null;
  soldout_done_by_name: string | null;
  restock_done_at: string | null;
  restock_done_by_name: string | null;
}

export interface DaiwaNaverListingListResponse {
  items: DaiwaNaverListingItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DaiwaNaverListingListParams {
  q?: string;
  listing_status?: "active" | "paused" | "ended" | "unknown";
  matched_daiwa_only?: boolean;
  unmatched_daiwa_only?: boolean;
  // [ERP-DAIWA-ORDER-QUEUE-RESTOCKED-CANDIDATE-AND-NAVER-DK-ZERO-FILTER-20260605-001]
  dk_stock_filter?: "all" | "zero" | "in_stock";
  page?: number;
  page_size?: number;
}

export async function getDaiwaNaverListings(
  params: DaiwaNaverListingListParams = {},
): Promise<DaiwaNaverListingListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.listing_status) qs.set("listing_status", params.listing_status);
  if (params.matched_daiwa_only) qs.set("matched_daiwa_only", "true");
  if (params.unmatched_daiwa_only) qs.set("unmatched_daiwa_only", "true");
  if (params.dk_stock_filter && params.dk_stock_filter !== "all")
    qs.set("dk_stock_filter", params.dk_stock_filter);
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 50));
  return request<DaiwaNaverListingListResponse>(
    `/vendors/daiwa/naver-listing?${qs}`,
  );
}

// ── Daiwa Naver Listing — 후보 (Phase D 2차, #80) ──────────────

export interface DaiwaNaverListingCandidateItem {
  id: number;
  jan_code: string;
  supplier_product_code: string | null;
  name_en: string | null;
  name_ko: string | null;
  min_order_qty: number | null;
  direct_ship_eligible: boolean;
  product_group: string | null;
  barcode: string | null;
  category_large: string | null;
  category_middle: string | null;
  category_small: string | null;
  origin_country: string | null;
  dk_stock_qty: number;
  preorder_qty: number;
  expected_arrival_date: string | null;
  price_jpy: number | null;
  msrp_krw: number | null;
  listed: boolean;
  candidate_reason: string | null;
}

export interface DaiwaNaverListingCandidateResponse {
  items: DaiwaNaverListingCandidateItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DaiwaNaverListingCandidateParams {
  q?: string;
  dk_stock_only?: boolean;
  direct_ship_only?: boolean;
  product_group?: string;
  category_large?: string;
  category_middle?: string;
  category_small?: string;
  page?: number;
  page_size?: number;
}

export async function getDaiwaNaverListingCandidates(
  params: DaiwaNaverListingCandidateParams = {},
): Promise<DaiwaNaverListingCandidateResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  // dk_stock_only / direct_ship_only 는 백엔드 기본이 True 라서 false 일 때만 명시
  if (params.dk_stock_only === false) qs.set("dk_stock_only", "false");
  if (params.direct_ship_only === false) qs.set("direct_ship_only", "false");
  if (params.product_group) qs.set("product_group", params.product_group);
  if (params.category_large) qs.set("category_large", params.category_large);
  if (params.category_middle) qs.set("category_middle", params.category_middle);
  if (params.category_small) qs.set("category_small", params.category_small);
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 50));
  return request<DaiwaNaverListingCandidateResponse>(
    `/vendors/daiwa/naver-listing/candidates?${qs}`,
  );
}

// ── [ERP-DAIWA-STOCK-ZERO-TRANSITION-LIST-20260522-001] 재고 0 전환 SKU 상세 ──

export interface DaiwaStockZeroTransitionItem {
  jan_code: string;
  supplier_product_code: string | null;
  name_ko: string | null;
  name_en: string | null;
  prev_stock_qty: number;
  current_stock_qty: number;
  prev_snapshot_at: string;
  current_snapshot_at: string;
  min_order_qty: number | null;
  direct_ship_eligible: boolean;
  naver_listed: boolean;
  naver_listing_url: string | null;
  // [ERP-DAIWA-NAVER-DIRECT-SOLDOUT-RESTOCK-ACTION-STATE-20260605-001]
  naver_listing_id: number | null;
  action_status: string | null;
  soldout_done_at: string | null;
  soldout_done_by_name: string | null;
  restock_done_at: string | null;
  restock_done_by_name: string | null;
}

export interface DaiwaStockZeroTransitionResponse {
  items: DaiwaStockZeroTransitionItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface DaiwaStockZeroTransitionParams {
  q?: string;
  direct_ship_only?: boolean;
  naver_listed_only?: boolean;
  page?: number;
  page_size?: number;
}

export async function getDaiwaStockZeroTransitions(
  params: DaiwaStockZeroTransitionParams = {},
): Promise<DaiwaStockZeroTransitionResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.direct_ship_only) qs.set("direct_ship_only", "true");
  if (params.naver_listed_only) qs.set("naver_listed_only", "true");
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 50));
  return request<DaiwaStockZeroTransitionResponse>(
    `/vendors/daiwa/stock-zero-transitions?${qs}`,
  );
}

// ── [ERP-DAIWA-ORDER-QUEUE-RESTOCKED-CANDIDATE-AND-NAVER-DK-ZERO-FILTER-20260605-001]
// 네이버 직배 재입고/품절해제 후보 (prev=0 & latest>0). 응답 스키마는 품절 후보와 동일.
export async function getDaiwaStockRestockTransitions(
  params: DaiwaStockZeroTransitionParams = {},
): Promise<DaiwaStockZeroTransitionResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.direct_ship_only) qs.set("direct_ship_only", "true");
  if (params.naver_listed_only) qs.set("naver_listed_only", "true");
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 50));
  return request<DaiwaStockZeroTransitionResponse>(
    `/vendors/daiwa/stock-restock-transitions?${qs}`,
  );
}

// ── [ERP-DAIWA-NAVER-DIRECT-SOLDOUT-RESTOCK-ACTION-STATE-20260605-001]
// 네이버 직배 품절/재고복구 수동 작업 상태 (직원 확인 기록, 네이버 자동변경 없음).

export interface DaiwaNaverStockActionState {
  id: number;
  jan_code: string;
  seller_product_code_snapshot: string | null;
  naver_listing_id: number | null;
  naver_product_no_snapshot: string | null;
  product_name_snapshot: string | null;
  supplier_product_code_snapshot: string | null;
  status: string;
  soldout_done_at: string | null;
  soldout_done_by_user_id: number | null;
  soldout_done_by_name_snapshot: string | null;
  soldout_dk_stock_qty_snapshot: number | null;
  restock_detected_at: string | null;
  restock_dk_stock_qty_snapshot: number | null;
  restock_done_at: string | null;
  restock_done_by_user_id: number | null;
  restock_done_by_name_snapshot: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

export async function daiwaNaverSoldoutDone(
  listingId: number,
  memo?: string,
): Promise<DaiwaNaverStockActionState> {
  return request<DaiwaNaverStockActionState>(
    `/vendors/daiwa/naver-listing/${listingId}/soldout-done`,
    { method: "POST", body: JSON.stringify({ memo: memo ?? null }) },
  );
}

export async function daiwaNaverRestockDone(
  listingId: number,
  memo?: string,
): Promise<DaiwaNaverStockActionState> {
  return request<DaiwaNaverStockActionState>(
    `/vendors/daiwa/naver-listing/${listingId}/restock-done`,
    { method: "POST", body: JSON.stringify({ memo: memo ?? null }) },
  );
}

// ── Daiwa Reference Tables (탭2: 기준표 관리) ──────────────────

export interface DaiwaKakeRule {
  id: number;
  vendor_name: string;
  product_group: string;
  kake_rate: number;
  is_active: boolean;
  updated_at: string;
}

export interface DaiwaKakeRuleListResponse {
  items: DaiwaKakeRule[];
  total: number;
  updated_at_max: string | null;
}

export interface DaiwaKakeRuleUpdateRequest {
  kake_rate?: number;
  is_active?: boolean;
}

export async function getDaiwaKakeRules(): Promise<DaiwaKakeRuleListResponse> {
  return request<DaiwaKakeRuleListResponse>("/vendors/daiwa/reference-tables/kake");
}

export async function updateDaiwaKakeRule(
  id: number,
  body: DaiwaKakeRuleUpdateRequest,
): Promise<DaiwaKakeRule> {
  return request<DaiwaKakeRule>(`/vendors/daiwa/reference-tables/kake/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface DaiwaCategoryMapping {
  id: number;
  vendor_name: string;
  vendor_category_name: string;
  naver_category_code: string | null;
  naver_category_name: string | null;
  is_active: boolean;
  updated_at: string;
}

export interface DaiwaCategoryMappingListResponse {
  items: DaiwaCategoryMapping[];
  total: number;
  updated_at_max: string | null;
}

export interface DaiwaCategoryMappingUpdateRequest {
  naver_category_code?: string;
  naver_category_name?: string;
  is_active?: boolean;
}

export async function getDaiwaCategoryMappings(): Promise<DaiwaCategoryMappingListResponse> {
  return request<DaiwaCategoryMappingListResponse>("/vendors/daiwa/reference-tables/category");
}

export async function updateDaiwaCategoryMapping(
  id: number,
  body: DaiwaCategoryMappingUpdateRequest,
): Promise<DaiwaCategoryMapping> {
  return request<DaiwaCategoryMapping>(`/vendors/daiwa/reference-tables/category/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface DaiwaOriginMapping {
  id: number;
  origin_name: string;
  naver_origin_code: string | null;
  is_active: boolean;
  updated_at: string;
}

export interface DaiwaOriginMappingListResponse {
  items: DaiwaOriginMapping[];
  total: number;
  updated_at_max: string | null;
}

export interface DaiwaOriginMappingUpdateRequest {
  naver_origin_code?: string;
  is_active?: boolean;
}

export async function getDaiwaOriginMappings(): Promise<DaiwaOriginMappingListResponse> {
  return request<DaiwaOriginMappingListResponse>("/vendors/daiwa/reference-tables/origin");
}

export async function updateDaiwaOriginMapping(
  id: number,
  body: DaiwaOriginMappingUpdateRequest,
): Promise<DaiwaOriginMapping> {
  return request<DaiwaOriginMapping>(`/vendors/daiwa/reference-tables/origin/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// ── 다이와 인터넷 가격리스트 (#72) ─────────────────────────────

export interface DaiwaPriceListItem {
  id: number;
  jan_code: string;
  product_name: string | null;
  online_min_price_krw: number | null;
  policy_active: boolean;
  effective_month: string | null;
  memo: string | null;
  updated_at: string;
  // 세부스펙 참고표 원본 컬럼 (#76)
  grade: string | null;
  sub_category: string | null;
  product_group: string | null;
  price_jpy: number | null;
  msrp_krw: number | null;
  sales_flag: string | null;
  // 카케 매칭 + 파생 계산 (응답 시점)
  matched_kake_rate: number | null;
  kake_matched: boolean;
  expected_cost: number | null;
  margin_amount: number | null;
  margin_rate: number | null;
  is_not_for_sale: boolean;
  // ERP 등록상품 + 현재 네이버 판매가 비교 (#77)
  has_erp_product: boolean;
  current_naver_price: number | null;
  policy_price_diff: number | null;  // current_naver_price - online_min_price_krw
  price_policy_status: "normal" | "violation" | "no_product" | "no_price" | "no_policy";
}

export interface DaiwaPriceListResponse {
  items: DaiwaPriceListItem[];
  total: number;
  updated_at_max: string | null;
  page: number;
  page_size: number;
  total_pages: number;
}

export async function getDaiwaPriceList(params: {
  q?: string;
  page?: number;
  page_size?: number;
  active_only?: boolean;
  no_kake_only?: boolean;
  not_for_sale_only?: boolean;
  low_margin_only?: boolean;
  erp_registered_only?: boolean;
} = {}): Promise<DaiwaPriceListResponse> {
  const qs = new URLSearchParams();
  if (params.q && params.q.trim()) qs.set("q", params.q.trim());
  qs.set("page", String(params.page ?? 1));
  qs.set("page_size", String(params.page_size ?? 100));
  if (params.active_only === false) qs.set("active_only", "false");
  if (params.no_kake_only) qs.set("no_kake_only", "true");
  if (params.not_for_sale_only) qs.set("not_for_sale_only", "true");
  if (params.low_margin_only) qs.set("low_margin_only", "true");
  if (params.erp_registered_only) qs.set("erp_registered_only", "true");
  return request<DaiwaPriceListResponse>(`/vendors/daiwa/reference-tables/price-list?${qs}`);
}

// ── 다이와 직배 운영자 결정 (decide → hitcamp/daiwa/reset) ──────

export interface DirectShipDecisionResponse {
  order_no: string;
  daiwa_fulfillment_choice: "hitcamp" | "daiwa" | null;
  fulfillment_class: FulfillmentClass;
  daiwa_eligible_item_count: number;
}

export async function setDaiwaDirectShipDecision(
  order_no: string,
  choice: "hitcamp" | "daiwa" | "reset",
): Promise<DirectShipDecisionResponse> {
  return request<DirectShipDecisionResponse>(`/vendors/daiwa/direct-ship/decision`, {
    method: "POST",
    body: JSON.stringify({ order_no, choice }),
  });
}

export async function downloadDaiwaNaverBulk(): Promise<void> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const res = await fetch(`${API_URL}/vendors/daiwa/export/naver-bulk`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Export 실패" }));
    throw new Error(err.detail || "Export 실패");
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] || "daiwa_naver_bulk.xlsx";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Inventory ──────────────────────────────────────────────────

export interface StockImportResult {
  sheet_type: string;
  total_rows: number;
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  errors: { row?: number; error: string }[];
}

export interface InventorySummary {
  total_skus: number;
  total_quantity: number;
  location_counts: Record<string, number>;
  location_quantities: Record<string, number>;
  inhouse_supply_source_count: number;
  recent_movement_count: number;
}

export interface InventoryItem {
  sku: string;
  jan_code: string | null;
  location_code: string;
  quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  is_active: boolean;
  updated_at: string;
}

export interface InventoryByLocationResponse {
  total: number;
  items: InventoryItem[];
}

export interface StockMovementItem {
  id: number;
  sku: string;
  jan_code: string | null;
  location_code: string;
  movement_type: string;
  quantity_delta: number;
  source_type: string;
  source_ref: string | null;
  memo: string | null;
  created_at: string;
}

export async function getInventorySummary(): Promise<InventorySummary> {
  return request<InventorySummary>("/inventory/summary");
}

export async function getInventoryByLocation(params?: {
  location_code?: string;
  sku?: string;
  only_in_stock?: boolean;
  limit?: number;
  offset?: number;
}): Promise<InventoryByLocationResponse> {
  const qs = new URLSearchParams();
  if (params?.location_code) qs.set("location_code", params.location_code);
  if (params?.sku) qs.set("sku", params.sku);
  if (params?.only_in_stock) qs.set("only_in_stock", "true");
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<InventoryByLocationResponse>(`/inventory/by-location?${qs}`);
}

export async function getStockMovements(params?: {
  sku?: string;
  location_code?: string;
  movement_type?: string;
  limit?: number;
}): Promise<StockMovementItem[]> {
  const qs = new URLSearchParams();
  if (params?.sku) qs.set("sku", params.sku);
  if (params?.location_code) qs.set("location_code", params.location_code);
  if (params?.movement_type) qs.set("movement_type", params.movement_type);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  return request<StockMovementItem[]>(`/inventory/movements?${qs}`);
}

export async function importHitcampStock(
  file: File,
  locationCode = "hitcamp_main",
  receiptDate?: string
): Promise<StockImportResult> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const formData = new FormData();
  formData.append("file", file);
  const qs = new URLSearchParams({ location_code: locationCode });
  if (receiptDate) qs.set("receipt_date", receiptDate);
  const res = await fetch(`${API_URL}/vendors/daiwa/import/hitcamp-stock?${qs}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "업로드 실패" }));
    throw new Error(err.detail || "업로드 실패");
  }
  return res.json();
}

// ── 멀티 로케이션 재고 고도화 ──────────────────────────────────

export interface LocationSummaryItem {
  location_code: string;
  label: string;
  sku_count: number;
  total_quantity: number;
  quarantine: boolean;
  rocket: boolean;
}

export interface SkuLocationRow {
  location_code: string;
  quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  updated_at: string;
}

export interface SkuLocationResponse {
  sku: string;
  total_quantity: number;
  locations: SkuLocationRow[];
}

export interface TransferRequest {
  sku: string;
  from_location_code: string;
  to_location_code: string;
  quantity: number;
  memo?: string;
  transferred_by?: string;
}

// 12차 — 백엔드는 `require_or_request_approval` 을 거쳐
// {mode: "applied"|"pending", approval_id, result} 형태로 응답한다.
// `result` 는 admin 일 때 `_apply_transfer` 의 반환값, pending 일 땐 null.
export interface TransferResult {
  sku: string;
  from_location_code: string;
  to_location_code: string;
  quantity: number;
  from_quantity_after: number;
  to_quantity_after: number;
  movement_out_id: number;
  movement_in_id: number;
}

export interface TransferResponse {
  mode: "applied" | "pending";
  approval_id: number;
  result: TransferResult | null;
}

export function getLocationsSummary(): Promise<LocationSummaryItem[]> {
  return request<LocationSummaryItem[]>("/inventory/locations/summary");
}

export function getSkuLocations(sku: string): Promise<SkuLocationResponse> {
  return request<SkuLocationResponse>(`/inventory/sku/${encodeURIComponent(sku)}`);
}

export function transferStock(body: TransferRequest): Promise<TransferResponse> {
  return request<TransferResponse>("/inventory/transfer", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── 상품+재고 통합 화면 ────────────────────────────────────────────────────

export interface ProductWithInventoryItem {
  id: number;
  sku: string;
  product_name: string;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  brand: string | null;
  category: string | null;
  image_url: string | null;
  product_status: string;
  safety_stock_qty: number;
  part_type: string;
  as_available: boolean;
  is_active: boolean;
  sale_price: number | null;
  cost_factor: number | null;
  // [ERP-PRODUCT-PRICE-SOT-SMARTSTORE-DISPLAY-CONSISTENCY-PHASE1-20260608-001]
  // 기준가 SoT = product_prices.smartstore. effective_base_price 우선 표시.
  base_price?: number | null;            // product_prices.smartstore (없으면 null)
  effective_base_price?: number | null;  // base_price ?? sale_price (표시용 최종 기준가)
  sale_price_mismatch?: boolean;         // base_price ≠ sale_price 면 true (불일치 경고)
  total_quantity: number;
  available_quantity: number;
  reserved_from_po: number;
  inventory_by_location: Record<string, number>;
  // [ERP-BUNDLE-AVAILABLE-STOCK-SEPARATION-20260618-001] 일반/번들 가용 분리
  general_available_stock?: number | null;
  bundle_available_stock?: number | null;   // 번들이면 값, 일반이면 null
  effective_available_stock?: number | null;
  is_bundle?: boolean;
  bundle_component_count?: number;
  // [ERP-PRODUCT-LAST-INBOUND-DATE-DISPLAY-20260618-001] 최근 입고일(PO 입고/입고검수, 수동조정 제외)
  last_inbound_at?: string | null;
  last_inbound_source?: string | null;
  // [ERP-PRODUCTS-STOCK-VISIBILITY-BUSINESS-FLOW-SOURCE-ALIGNMENT-20260608-001]
  // 업무화면 source 기준 대기 수량 + location 잔여(확인필요).
  purchase_inbound_waiting?: number;   // 발주/입고검수장 기준 입고검수 대기
  return_inspection_waiting?: number;  // 교환반품 회수검수장 기준 검수 대기
  incoming_orphan_qty?: number;        // hitcamp_incoming 잔여(입고검수장 미연결, 관리자 확인필요)
  quarantine_orphan_qty?: number;      // quarantine 잔여(회수검수장 미연결, 관리자 확인필요)
  safety_stock_recommended: number;
  listed_platforms: string[];
  // [ERP-PRODUCT-LIST-PLATFORM-LINKS-20260519-001] 표준 platform key 의 listing_url.
  // 구 key 제외, http/https 시작 url 만 채택. 키 없으면 미등록 또는 url 없음.
  listing_urls?: Record<string, string>;
  // [ERP-PRODUCT-PURCHASE-URL-QUICKLINK-20260521-001] 매입처(구매) URL — 빠른 링크용
  purchase_url?: string | null;
}

export interface ProductWithInventoryResponse {
  total: number;
  items: ProductWithInventoryItem[];
}

export interface DashboardCardsResponse {
  total_skus: number;
  safety_stock_alert_count: number;
  stockout_count: number;
  stale_3m: number;
  stale_6m: number;
  stale_12m: number;
  recently_moved_7d_count: number;
}

// 상단 카드 drill-down 프리셋 — /products/with-inventory?stock_flag=<v> 와 1:1 매핑
export type StockFlag =
  | "safety_alert"
  | "stockout"
  | "stale_3m"
  | "stale_6m"
  | "stale_12m"
  | "recently_moved_7d";

export interface BatchTransferItem {
  sku: string;
  quantity: number;
}

export interface BatchTransferRequest {
  transfers: BatchTransferItem[];
  from_location_code: string;
  to_location_code: string;
  transferred_by?: string;
  memo?: string;
}

export interface BatchTransferResultItem {
  sku: string;
  quantity: number;
  success: boolean;
  error?: string | null;
}

// 12차 — 배치 이동도 require_or_request_approval 래핑된 응답 사용.
// admin → {mode: "applied", result: {succeeded_count, failed_count, results}}
// 비-admin → {mode: "pending", result: null}
export interface BatchTransferInner {
  succeeded_count: number;
  failed_count: number;
  results: BatchTransferResultItem[];
}

export interface BatchTransferResponse {
  mode: "applied" | "pending";
  approval_id: number;
  result: BatchTransferInner | null;
  // 호환 — 기존 호출부가 평탄 필드를 직접 참조하는 경우 대비
  succeeded_count?: number;
  failed_count?: number;
  results?: BatchTransferResultItem[];
}

export function getDashboardCards(): Promise<DashboardCardsResponse> {
  return request<DashboardCardsResponse>("/inventory/dashboard-cards");
}

export function getProductsWithInventory(params?: {
  page?: number;
  page_size?: number;
  brand?: string;
  category?: string;
  search?: string;
  part_type?: string;
  product_status?: string;
  location_filter?: string;
  unlisted_platform?: string;
  stock_flag?: StockFlag;
  // 관리자 "숨김 포함" 보기 — true 면 is_active=false(숨김/중복 SKU 정리) 상품도 노출.
  include_inactive?: boolean;
}): Promise<ProductWithInventoryResponse> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.page_size) qs.set("page_size", String(params.page_size));
  if (params?.brand) qs.set("brand", params.brand);
  if (params?.category) qs.set("category", params.category);
  if (params?.search) qs.set("search", params.search);
  if (params?.part_type) qs.set("part_type", params.part_type);
  if (params?.product_status) qs.set("product_status", params.product_status);
  if (params?.location_filter) qs.set("location_filter", params.location_filter);
  if (params?.unlisted_platform) qs.set("unlisted_platform", params.unlisted_platform);
  if (params?.stock_flag) qs.set("stock_flag", params.stock_flag);
  if (params?.include_inactive) qs.set("include_inactive", "true");
  return request<ProductWithInventoryResponse>(`/products/with-inventory?${qs}`);
}

export interface LocationInventorySummaryItem {
  location_code: string;
  sku_count: number;
  total_quantity: number;
}
export interface LocationInventorySummaryResponse {
  items: LocationInventorySummaryItem[];
  total_sku_count: number;
  total_quantity: number;
}

/**
 * 위치 탭 카운트 전용. `getProductsWithInventory` 와 동일한 필터(`brand`, `category`, `search`,
 * `part_type`, `product_status`, `unlisted_platform`)를 받아 위치별 집계를 반환한다.
 * `location_filter` 파라미터는 **받지 않음** — 이 응답 자체가 모든 위치의 집계이기 때문.
 */
export function getProductInventorySummary(params?: {
  brand?: string;
  category?: string;
  search?: string;
  part_type?: string;
  product_status?: string;
  unlisted_platform?: string;
  stock_flag?: StockFlag;
  include_inactive?: boolean;
}): Promise<LocationInventorySummaryResponse> {
  const qs = new URLSearchParams();
  if (params?.brand) qs.set("brand", params.brand);
  if (params?.category) qs.set("category", params.category);
  if (params?.search) qs.set("search", params.search);
  if (params?.part_type) qs.set("part_type", params.part_type);
  if (params?.product_status) qs.set("product_status", params.product_status);
  if (params?.unlisted_platform) qs.set("unlisted_platform", params.unlisted_platform);
  if (params?.stock_flag) qs.set("stock_flag", params.stock_flag);
  if (params?.include_inactive) qs.set("include_inactive", "true");
  const q = qs.toString();
  return request<LocationInventorySummaryResponse>(
    `/products/inventory-summary${q ? `?${q}` : ""}`
  );
}

export function transferStockBatch(body: BatchTransferRequest): Promise<BatchTransferResponse> {
  return request<BatchTransferResponse>("/inventory/transfer/batch", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function adjustInventory(body: {
  sku: string;
  location_code: string;
  quantity: number;
  reason?: string;
}): Promise<{ sku: string; location_code: string; quantity: number; delta: number }> {
  return request("/inventory/adjust", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface AdjustDeltaRequest {
  sku: string;
  location_code: string;
  delta: number;
  reason?: string;
  memo?: string;
}

export function adjustInventoryDelta(body: AdjustDeltaRequest): Promise<unknown> {
  return request("/inventory/adjust-delta", { method: "POST", body: JSON.stringify(body) });
}

// ── 상품 등록 모달 보조 ────────────────────────────────────────────────────────

export function getNextSku(brand: string): Promise<{ next_sku: string; prefix: string; next_num: number }> {
  return request(`/products/next-sku?brand=${encodeURIComponent(brand)}`);
}

export function getCategories(): Promise<string[]> {
  return request("/products/categories");
}

export function getBrands(): Promise<string[]> {
  return request<string[]>("/products/brands");
}

export function searchProductNames(q: string, limit = 10): Promise<{ product_name: string; sku: string }[]> {
  return request(`/products/names?q=${encodeURIComponent(q)}&limit=${limit}`);
}

export interface PlatformMeta {
  id?: number;
  sku?: string;
  platform: string;
  is_listed: boolean;
  listing_url?: string | null;
  price_multiplier?: number | null;
}

export function getPlatformMeta(sku: string): Promise<PlatformMeta[]> {
  return request<PlatformMeta[]>(`/products/${encodeURIComponent(sku)}/platform-meta`);
}

// [ERP-DAIWA-BARCODE-SCAN-20260519-001] 실물 바코드 등록 helper.
export interface RegisterBarcodeWarning {
  code: string;
  message: string;
}
export interface RegisterBarcodeResponse {
  success: boolean;
  sku: string;
  barcode: string;
  warnings: RegisterBarcodeWarning[];
  matched_by: string;
}
export function registerProductBarcode(
  sku: string,
  barcode: string,
  source: string = "packing_scan",
): Promise<RegisterBarcodeResponse> {
  return request<RegisterBarcodeResponse>(
    `/products/${encodeURIComponent(sku)}/register-barcode`,
    {
      method: "POST",
      body: JSON.stringify({ barcode, source }),
    },
  );
}

export function upsertPlatformMeta(
  sku: string,
  data: { platform: string; is_listed: boolean; listing_url?: string | null; price_multiplier?: number | null },
): Promise<{ ok: boolean }> {
  return request(`/products/${encodeURIComponent(sku)}/platform-meta`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ── 브랜드 SKU 설정 ────────────────────────────────────────────────────────────

export interface BrandSkuPrefix {
  id: number;
  brand: string;
  prefix: string;
  digit_count: number;
  separator: string;
}

export function getBrandSkuPrefixes(): Promise<BrandSkuPrefix[]> {
  return request<BrandSkuPrefix[]>("/settings/brand-sku-prefixes");
}

export function upsertBrandSkuPrefix(data: {
  brand: string;
  prefix: string;
  digit_count: number;
  separator: string;
}): Promise<BrandSkuPrefix> {
  return request<BrandSkuPrefix>("/settings/brand-sku-prefixes", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function deleteBrandSkuPrefix(id: number): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/settings/brand-sku-prefixes/${id}`, { method: "DELETE" });
}

// ── AS 관리 ──────────────────────────────────────────────────────────────────

// 26차 후속 #22 — AS 5단계 (2026-04-27)
export const AS_STATUS_LABELS: Record<string, string> = {
  received: "접수",
  recovered: "회수완료",
  repaired: "수리완료",
  payment_confirmed: "수리비용입금확인",
  shipped: "출고완료",
  cancelled: "취소",
};

export const AS_STATUS_COLORS: Record<string, string> = {
  received: "bg-blue-100 text-blue-800",
  recovered: "bg-yellow-100 text-yellow-800",
  repaired: "bg-purple-100 text-purple-800",
  payment_confirmed: "bg-emerald-100 text-emerald-800",
  shipped: "bg-gray-100 text-gray-800",
  cancelled: "bg-red-100 text-red-800",
};

// 5단계 정방향 다음-단계 매핑 (AS 상세 페이지의 "다음 단계" 버튼용).
// cancelled / shipped 는 종료 상태.
export const AS_NEXT_STATUS: Record<string, string | null> = {
  received: "recovered",
  recovered: "repaired",
  repaired: "payment_confirmed",
  payment_confirmed: "shipped",
  shipped: null,
  cancelled: null,
};

export interface AsPartsUsedItem {
  id: number;
  as_request_id: number;
  part_name: string;
  part_sku: string | null;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  customer_price: number | null;   // 소비자가
  labor_fee: number | null;        // 파츠별 기술료
  memo: string | null;
  created_at: string;
  // [ERP-AS-WARRANTY-FREE-PARTS-ACTUAL-LOGIC-20260618-001] 파츠비 무상 적용 상태
  is_warranty_free?: boolean;
  warranty_usage_id?: number | null;
  original_part_price?: number | null;  // 무상 적용 전 파츠비
  warranty_policy_name?: string | null;
}

export interface AsCostItem {
  id: number;
  as_request_id: number;
  parts_cost: number;
  labor_cost: number;
  shipping_cost: number;
  extra_cost: number;
  total_cost: number;
  customer_charge: number;
  charge_confirmed: boolean;
  charge_confirmed_at: string | null;
  charge_confirmed_by: string | null;
  // 26차 후속 #22 — 입금확인 큐 (none | pending | confirmed)
  deposit_status: string;
  deposit_confirmed_at: string | null;
  memo: string | null;
  updated_at: string;
}

// [ERP-AS-ADDITIONAL-SALES-ITEMS-WITH-REPAIR-SHIPMENT-DEV-20260622-001]
// AS 고객 추가구매 상품(일반 판매상품). 파츠와 분리 — 재고차감/포장검수 대상.
export interface AsAdditionalItem {
  id: number;
  as_request_id: number;
  product_id: number | null;
  sku: string | null;
  product_name: string;   // 등록 시점 snapshot
  option1: string | null;
  option2: string | null;
  barcode: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  memo: string | null;
  created_at: string;
}

export interface AsShipmentRequest {
  id: number;
  as_request_id: number;
  as_no: string;
  status: string;
  customer_name: string;
  customer_phone: string | null;
  product_name: string;
  requested_by: string | null;
  memo: string | null;
  created_at: string;
}

export interface AsRequest {
  id: number;
  as_no: string;
  order_no: string | null;
  customer_id: number | null;
  customer_name: string;
  customer_phone: string | null;
  sku: string | null;
  product_name: string;
  symptom: string | null;
  request_channel: string;
  received_date: string;
  status: string;
  assignee: string | null;
  memo: string | null;
  company_code: string | null;
  free_repair_eligible: boolean;
  pickup_type: string | null;
  pickup_tracking_no: string | null;
  symptom_match: boolean | null;
  mismatch_comment: string | null;
  repair_comment: string | null;
  out_tracking_no: string | null;
  // [ERP-TOGGLE-EXPORT-AS-DETAIL-ADDRESS-EDIT-HOTFIX-20260618-001] 회수/출고 주소
  pickup_zipcode?: string | null;
  pickup_address1?: string | null;
  pickup_address2?: string | null;
  return_name?: string | null;
  return_phone?: string | null;
  return_zipcode?: string | null;
  return_address1?: string | null;
  return_address2?: string | null;
  created_at: string;
  updated_at: string;
  parts: AsPartsUsedItem[];
  cost: AsCostItem | null;
  // [ERP-AS-ADDITIONAL-SALES-ITEMS-WITH-REPAIR-SHIPMENT-DEV-20260622-001]
  additional_items: AsAdditionalItem[];
  // 7차 정리 — AS 회수 허브 연계 상태 파생
  recovery_receipt_id: number | null;
  recovery_receipt_status: string | null;
  recovery_arrived: boolean | null;
}

export interface AsRequestListItem {
  id: number;
  as_no: string;
  order_no: string | null;
  customer_name: string;
  customer_phone: string | null;
  product_name: string;
  status: string;
  assignee: string | null;
  received_date: string;
  pickup_type: string | null;
  pickup_tracking_no: string | null;
  updated_at: string;
}

export interface AsRequestListResponse {
  total: number;
  items: AsRequestListItem[];
}

export function getAsRequests(params?: {
  status?: string;
  assignee?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<AsRequestListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.assignee) qs.set("assignee", params.assignee);
  if (params?.q) qs.set("q", params.q);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<AsRequestListResponse>(`/as/requests?${qs}`);
}

export function getAsRequest(id: number): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${id}`);
}

// [ERP-AS-REQUEST-LV2-SOFT-DELETE-20260619-001]
// AS 접수건 soft delete (lv2 이상). 물리 삭제 아님 — deleted_at 기록 후 목록에서 숨김.
export function deleteAsRequest(
  id: number,
  reason?: string,
): Promise<{ id: number; as_no: string; deleted_at: string | null; already_deleted: boolean }> {
  return request(`/as/requests/${id}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export function createAsRequest(body: {
  customer_name: string;
  product_name: string;
  received_date: string;
  order_no?: string;
  customer_id?: number;
  customer_phone?: string;
  sku?: string;
  symptom?: string;
  request_channel?: string;
  assignee?: string;
  memo?: string;
  // company_id 우선, company_code 는 호환 fallback. 백엔드는 둘 중 하나 필수.
  company_id?: number;
  company_code?: string;
  pickup_type?: string;
  pickup_tracking_no?: string;
  // [ERP-TOGGLE-EXPORT-AS-DETAIL-ADDRESS-EDIT-HOTFIX-20260618-001] 회수/출고 주소
  pickup_zipcode?: string;
  pickup_address1?: string;
  pickup_address2?: string;
  return_name?: string;
  return_phone?: string;
  return_zipcode?: string;
  return_address1?: string;
  return_address2?: string;
}): Promise<AsRequest> {
  return request<AsRequest>("/as/requests", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateAsRequest(
  id: number,
  body: Partial<{
    customer_name: string;
    customer_phone: string;
    order_no: string;
    sku: string;
    product_name: string;
    symptom: string;
    status: string;
    assignee: string;
    memo: string;
    pickup_type: string;
    pickup_tracking_no: string;
    symptom_match: boolean;
    mismatch_comment: string;
    repair_comment: string;
    out_tracking_no: string;
    // [ERP-TOGGLE-EXPORT-AS-DETAIL-ADDRESS-EDIT-HOTFIX-20260618-001] 회수/출고 주소
    pickup_zipcode: string;
    pickup_address1: string;
    pickup_address2: string;
    return_name: string;
    return_phone: string;
    return_zipcode: string;
    return_address1: string;
    return_address2: string;
  }>
): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export interface CustomerOrderItem {
  product_name: string;
  sku: string | null;
  quantity: number | null;
}

export interface CustomerOrder {
  order_no: string;
  order_date: string | null;
  platform: string | null;
  paid_amount: number;
  items: CustomerOrderItem[];
}

export function getAsCustomerOrders(params: {
  customer_id?: number;
  phone?: string;
}): Promise<{ orders: CustomerOrder[] }> {
  const qs = new URLSearchParams();
  if (params.customer_id) qs.set("customer_id", String(params.customer_id));
  if (params.phone) qs.set("phone", params.phone);
  return request<{ orders: CustomerOrder[] }>(`/as/customer-orders?${qs}`);
}

export function getAsStatusCounts(): Promise<Record<string, number>> {
  return request<Record<string, number>>("/as/status-counts");
}

export function addAsPart(body: {
  as_request_id: number;
  part_name: string;
  part_sku?: string;
  quantity?: number;
  unit_cost?: number;
  customer_price?: number;
  labor_fee?: number;
  memo?: string;
}): Promise<AsPartsUsedItem> {
  return request<AsPartsUsedItem>("/as/parts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function deleteAsPart(partId: number): Promise<void> {
  return request<void>(`/as/parts/${partId}`, { method: "DELETE" });
}

// ── [ERP-AS-ADDITIONAL-SALES-ITEMS-WITH-REPAIR-SHIPMENT-DEV-20260622-001] AS 추가구매 상품 ──
export function addAsAdditionalItem(
  asId: number,
  body: {
    product_id?: number | null;
    sku?: string | null;
    product_name: string;
    option1?: string | null;
    option2?: string | null;
    barcode?: string | null;
    quantity?: number;
    unit_price?: number;
    memo?: string | null;
  },
): Promise<AsAdditionalItem> {
  return request<AsAdditionalItem>(`/as/requests/${asId}/additional-items`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateAsAdditionalItem(
  asId: number,
  itemId: number,
  body: { quantity?: number; unit_price?: number; memo?: string | null },
): Promise<AsAdditionalItem> {
  return request<AsAdditionalItem>(`/as/requests/${asId}/additional-items/${itemId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function deleteAsAdditionalItem(asId: number, itemId: number): Promise<void> {
  return request<void>(`/as/requests/${asId}/additional-items/${itemId}`, { method: "DELETE" });
}

// ── [ERP-AS-WARRANTY-FREE-PARTS-ACTUAL-LOGIC-20260618-001] 파츠비 무상 적용 ──
export interface WarrantyEligibilityResponse {
  status: string;   // available / already_used / already_applied / no_purchase / not_policy_target / inactive_policy / invalid_sku
  eligible: boolean;
  reason: string | null;
  as_part_id: number;
  policy: {
    id: number;
    code: string;
    name: string;
    policy_type: string;
    max_free_as_count: number;
    coverage_scope: string;
  } | null;
  matched_purchase: {
    order_id: number | null;
    order_no: string | null;
    target_sku: string | null;
    policy_sku_qty: number | null;
  } | null;
  usage: {
    used: number;
    remaining: number;
    allowed: number;
    max_per_unit: number;
  } | null;
  pricing: {
    part_sku: string | null;
    original_part_price: number;
    free_part_price: number;
    waived_amount: number;
    labor_fee_policy: string;
  } | null;
}

export function getWarrantyEligibility(
  asId: number,
  asPartId: number,
): Promise<WarrantyEligibilityResponse> {
  return request<WarrantyEligibilityResponse>(
    `/as/${asId}/warranty-eligibility?as_part_id=${asPartId}`,
  );
}

export function applyWarrantyFree(
  asId: number,
  body: { as_part_id: number; policy_id: number; confirm?: boolean },
): Promise<AsRequest> {
  return request<AsRequest>(`/as/${asId}/warranty-apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirm: true, ...body }),
  });
}

export function voidWarrantyFree(
  asId: number,
  body: { usage_id?: number; as_part_id?: number },
): Promise<AsRequest> {
  return request<AsRequest>(`/as/${asId}/warranty-void`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function upsertAsCost(body: {
  as_request_id: number;
  parts_cost?: number;
  labor_cost?: number;
  shipping_cost?: number;
  extra_cost?: number;
  customer_charge?: number;
  memo?: string;
}): Promise<AsCostItem> {
  return request<AsCostItem>("/as/costs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── 중국 구매/입고 관리 ──────────────────────────────────────────────────────

export const PO_STATUS_LABELS: Record<string, string> = {
  requested: "발주진행중",
  ordered: "발주완료",
  received_in_china: "중국창고입고",
  ready_for_forwarding: "포워딩준비",
  shipped_to_korea: "한국행발송",
  arrived_in_korea: "국내도착",
  partial_received: "부분입고",
  stocked_in: "입고완료",
  cancelled: "취소",
};

export const PO_STATUS_COLORS: Record<string, string> = {
  requested: "bg-gray-100 text-gray-700",
  ordered: "bg-blue-100 text-blue-800",
  received_in_china: "bg-yellow-100 text-yellow-800",
  ready_for_forwarding: "bg-orange-100 text-orange-800",
  shipped_to_korea: "bg-purple-100 text-purple-800",
  arrived_in_korea: "bg-indigo-100 text-indigo-800",
  partial_received: "bg-amber-100 text-amber-800",
  stocked_in: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

// 19차 — 중국 수입 PO 운영 4단계 (DB status는 9개 그대로 유지, UI 매핑만)
export type ChinaOperationalStage =
  | "order"             // 발주 (requested/ordered/received_in_china/ready_for_forwarding)
  | "shipped"           // 한국행발송 (shipped_to_korea)
  | "inbound_progress"  // 샵입고진행중 (arrived_in_korea)
  | "inbound_done";     // 샵입고완료 (stocked_in)

export const CHINA_STAGE_ORDER: ChinaOperationalStage[] = [
  "order",
  "shipped",
  "inbound_progress",
  "inbound_done",
];

export const CHINA_STAGE_LABELS: Record<ChinaOperationalStage, string> = {
  order: "발주",
  shipped: "한국행발송",
  inbound_progress: "샵입고진행중",
  inbound_done: "샵입고완료",
};

export const CHINA_STAGE_COLORS: Record<ChinaOperationalStage, string> = {
  order: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  inbound_progress: "bg-indigo-100 text-indigo-800",
  inbound_done: "bg-green-100 text-green-800",
};

// DB 에 들어간 9개 status 중 어느 것이 어느 운영 단계에 해당하는지
export const CHINA_STAGE_STATUS_MAP: Record<ChinaOperationalStage, string[]> = {
  order: ["requested", "ordered", "received_in_china", "ready_for_forwarding"],
  shipped: ["shipped_to_korea"],
  inbound_progress: ["arrived_in_korea", "partial_received"],
  inbound_done: ["stocked_in"],
};

export function statusToChinaStage(status: string): ChinaOperationalStage | null {
  for (const stage of CHINA_STAGE_ORDER) {
    if (CHINA_STAGE_STATUS_MAP[stage].includes(status)) return stage;
  }
  return null;
}

export const SHIPMENT_STATUS_LABELS: Record<string, string> = {
  pending: "대기",
  forwarded: "포워딩완료",
  in_transit: "운송중",
  arrived: "도착",
  completed: "완료",
};

export interface PurchaseOrderItem {
  id: number;
  purchase_order_id: number;
  sku: string | null;
  jan_code: string | null;
  product_name: string;
  product_url: string | null;
  quantity_ordered: number;
  quantity_received_china: number;
  quantity_received_korea: number;
  unit_price_cny: number;
  unit_price_krw: number;
  total_price_krw: number;
  china_tracking_no: string | null;
  memo: string | null;
  // 부분입고 집계 (shimano/daiwa)
  quantity_partial_received: number;
  quantity_remaining: number;
  receipt_status: "pending" | "partial" | "complete";
  // 26차(2026-04-24) — 제조사 공유 양식 copy 용 (Product 조인 값)
  brand: string | null;
  name_cn: string | null;
  option1: string | null;
  option2: string | null;
  option1_cn: string | null;
  option2_cn: string | null;
  // 26차 후속 #3(2026-04-24) — 제조사 공유 양식 "价格" SoT (Product.cost_price_cny, 위안화).
  cost_price_cny: number | null;
  // [Phase1 2026-06-15] 발주 품목 썸네일 표시용 (Product.image_url 조인, 표시 전용)
  image_url?: string | null;
  // [ERP-PO-BARCODE-PRINT-JOBS-PHASE-B-...-20260616-001] SKU 기본 바코드 스타일(Product 조인).
  // Phase B 바코드 모달 자동선택용. 표시 전용.
  barcode_label_size?: BarcodeLabelSize | null;  // small | large | null
  barcode_code_type?: BarcodeCodeType | null;    // qr | code128 | null
  // [ERP-PO-ITEM-COST-SNAPSHOT-CURRENT-STOCK-LOCATION-UPDATE-DEV-20260623-001]
  // 발주 snapshot 단가 vs 상품DB 최신 원가 비교 + 현재고/위치 (모두 표시 전용).
  current_cost_price_cny?: number | null;
  current_cost_price_krw?: number | null;
  current_purchase_url?: string | null;
  cost_diff_exists?: boolean;
  cost_diff_reason?: string | null;
  cost_update_allowed?: boolean;
  current_stock_total?: number;
  current_stock_locations?: { location_code: string; label: string; quantity: number }[];
  safety_stock?: number;
  stock_status?: "ok" | "out_of_stock" | "below_order" | "below_safety" | "unknown";
  stock_status_label?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PurchaseOrder {
  id: number;
  po_no: string;
  vendor_type: string;       // china_import | shimano | daiwa | consumables
  supplier_name: string;
  supplier_type: string;
  status: string;
  import_method: string | null;       // ecommerce | sea | lcl
  forwarder: string | null;           // 배대지명 (이지타오 등)
  forwarder_order_no: string | null;  // 배대지 주문번호
  order_date: string | null;
  expected_arrival_date: string | null;
  ezytao_order_no: string | null;
  ezytao_tracking_no: string | null;
  china_warehouse_address: string | null;
  domestic_courier: string | null;
  domestic_tracking_no: string | null;
  domestic_shipping_type: string | null;
  box_count: number;
  has_invoice: boolean;
  has_packing_list: boolean;
  has_cost_estimate: boolean;
  cost_shipping: number;
  cost_handling: number;
  cost_extra_options: number;
  cost_lcl_customs: number;
  cost_lcl_origin_cert: number;
  cost_bl: number;
  cost_origin_label: number;
  cost_new_box: number;
  cost_misc: number;
  cost_customs_duty: number;
  cost_vat: number;
  cost_domestic_freight: number;
  cost_domestic_logistics: number;
  forwarder_costs_confirmed: boolean;
  customs_costs_confirmed: boolean;
  // 26차 후속 #16(2026-04-24) — china_import PO 공통 CNY→KRW 환율.
  // 미지정 시 백엔드는 190 을 기본값으로 사용.
  exchange_rate_cny_krw: number | null;
  total_item_count: number;
  total_quantity: number;
  total_price_krw: number;
  created_by: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
  items: PurchaseOrderItem[];
  // 26차 후속 #8(2026-04-24) — 샵입고완료 관리자 승인 이력
  stocked_in_approved_at?: string | null;
  stocked_in_approved_by_user_id?: number | null;
  stocked_in_approved_by_user_name?: string | null;
}

export interface PurchaseOrderListItem {
  id: number;
  po_no: string;
  vendor_type: string;
  supplier_name: string;
  supplier_type: string;
  status: string;
  order_date: string | null;
  expected_arrival_date: string | null;
  total_item_count: number;
  total_quantity: number;
  total_price_krw: number;
  total_received: number;   // shimano/daiwa 누적 입고 수량
  total_remaining: number;  // shimano/daiwa 미입고 수량
  created_by: string | null;
  updated_at: string;
}

// 부분입고 (shimano/daiwa)
export interface PartialInboundItem {
  id: number;
  partial_inbound_id: number;
  po_item_id: number;
  quantity_received: number;
  created_at: string;
}

export interface PartialInbound {
  id: number;
  inbound_no: string;
  purchase_order_id: number;
  inbound_date: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  items: PartialInboundItem[];
  // 26차 후속 #21 — B2B 엑셀 업로드로 생성된 배치 메타 (수기 등록은 null)
  source_file_name?: string | null;
  source_uploaded_at?: string | null;
}

// 26차 후속 #21 — 시마노/다이와 B2B 엑셀 업로드 preview 응답
export interface B2BInboundPreviewMatched {
  raw_identifier: string;
  match_key: "jan_code" | "sku";
  po_item_id: number;
  sku: string | null;
  jan_code: string | null;
  product_name: string | null;
  quantity_in_file: number;
  quantity_ordered: number;
  quantity_already_received: number;
  quantity_remaining: number;
  over_receipt: boolean;
}

export interface B2BInboundPreviewUnmatched {
  raw_identifier: string;
  quantity_in_file: number;
  reason: string;
}

export interface B2BInboundPreviewAmbiguous {
  raw_identifier: string;
  quantity_in_file: number;
  candidate_po_item_ids: number[];
}

export interface B2BInboundPreviewResponse {
  po_id: number;
  po_no: string;
  vendor_type: string;
  file_name: string;
  total_rows: number;
  matched: B2BInboundPreviewMatched[];
  unmatched: B2BInboundPreviewUnmatched[];
  ambiguous: B2BInboundPreviewAmbiguous[];
  warnings: string[];
}

// 안전재고 추천
export interface SafetyStockSuggestion {
  sku: string;
  product_name: string;
  current_qty: number;
  safety_stock_qty: number;
  sold_3months: number;
  suggested_order_qty: number;
  // 26차 후속 #14(2026-04-24) — 중국 수입 PO 문맥에서 브랜드 기반 필터에 사용.
  brand?: string | null;
}

export interface PurchaseOrderListResponse {
  total: number;
  items: PurchaseOrderListItem[];
}

export interface InboundShipment {
  id: number;
  shipment_no: string;
  po_ids: number[];
  status: string;
  ezytao_order_no: string | null;
  ezytao_box_count: number | null;
  ezytao_total_weight_kg: number | null;
  ezytao_shipping_method: string;
  ezytao_departure_date: string | null;
  ezytao_arrival_china: string | null;
  ezytao_forwarding_date: string | null;
  ezytao_tracking_no: string | null;
  domestic_courier: string | null;
  domestic_tracking_no: string | null;
  arrival_date_korea: string | null;
  stocked_date: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

export interface InboundShipmentListItem {
  id: number;
  shipment_no: string;
  po_ids: number[];
  status: string;
  ezytao_shipping_method: string;
  ezytao_forwarding_date: string | null;
  ezytao_tracking_no: string | null;
  domestic_tracking_no: string | null;
  arrival_date_korea: string | null;
  updated_at: string;
}

export interface InboundShipmentListResponse {
  total: number;
  items: InboundShipmentListItem[];
}

export function getPurchaseOrders(params?: {
  vendor_type?: string;
  status?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<PurchaseOrderListResponse> {
  const qs = new URLSearchParams();
  if (params?.vendor_type) qs.set("vendor_type", params.vendor_type);
  if (params?.status) qs.set("status", params.status);
  if (params?.q) qs.set("q", params.q);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<PurchaseOrderListResponse>(`/purchase/orders?${qs}`);
}

export function getPurchaseOrder(id: number): Promise<PurchaseOrder> {
  return request<PurchaseOrder>(`/purchase/orders/${id}`);
}

export function createPurchaseOrder(body: {
  vendor_type?: string;
  supplier_name?: string;
  supplier_type?: string;
  import_method?: string;
  forwarder?: string;
  forwarder_order_no?: string;
  order_date?: string;
  expected_arrival_date?: string;
  ezytao_order_no?: string;
  china_warehouse_address?: string;
  created_by?: string;
  memo?: string;
  // 26차 후속 #16(2026-04-24) — china_import 전용 PO 공통 환율(미전달 시 BE 기본 190).
  exchange_rate_cny_krw?: number;
  items?: Array<{
    product_name: string;
    sku?: string;
    jan_code?: string;
    product_url?: string;
    quantity_ordered?: number;
    unit_price_cny?: number;
    unit_price_krw?: number;
  }>;
}): Promise<PurchaseOrder> {
  return request<PurchaseOrder>("/purchase/orders", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updatePurchaseOrder(
  id: number,
  body: Partial<{
    vendor_type: string;
    supplier_name: string;
    status: string;
    import_method: string;
    forwarder: string;
    forwarder_order_no: string;
    order_date: string;
    expected_arrival_date: string;
    ezytao_order_no: string;
    ezytao_tracking_no: string;
    china_warehouse_address: string;
    domestic_courier: string;
    domestic_tracking_no: string;
    domestic_shipping_type: string;
    box_count: number;
    has_invoice: boolean;
    has_packing_list: boolean;
    has_cost_estimate: boolean;
    cost_shipping: number;
    cost_handling: number;
    cost_extra_options: number;
    cost_lcl_customs: number;
    cost_lcl_origin_cert: number;
    cost_bl: number;
    cost_domestic_logistics: number;
    cost_misc: number;
    created_by: string;
    memo: string;
    // 26차 후속 #16(2026-04-24) — 환율 변경 시 품목 원화단가 cascade 재계산 트리거(BE).
    exchange_rate_cny_krw: number;
  }>
): Promise<PurchaseOrder> {
  return request<PurchaseOrder>(`/purchase/orders/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function getPurchaseStatusCounts(vendorType?: string): Promise<Record<string, number>> {
  const qs = new URLSearchParams();
  if (vendorType) qs.set("vendor_type", vendorType);
  return request<Record<string, number>>(`/purchase/status-counts?${qs}`);
}

export function addPurchaseOrderItemWithTracking(poId: number, body: {
  product_name: string;
  sku?: string;
  jan_code?: string;
  product_url?: string;
  quantity_ordered?: number;
  unit_price_cny?: number;
  unit_price_krw?: number;
  china_tracking_no?: string;
  memo?: string;
}): Promise<PurchaseOrderItem> {
  return request<PurchaseOrderItem>(`/purchase/order-items?po_id=${poId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// 부분입고 API (shimano/daiwa)
export function createPartialInbound(body: {
  purchase_order_id: number;
  inbound_date?: string;
  notes?: string;
  created_by?: string;
  items: Array<{ po_item_id: number; quantity_received: number }>;
}): Promise<PartialInbound> {
  return request<PartialInbound>("/purchase/partial-inbounds", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function getPartialInbounds(poId: number): Promise<PartialInbound[]> {
  return request<PartialInbound[]>(`/purchase/partial-inbounds?po_id=${poId}`);
}

export function deletePartialInbound(id: number): Promise<void> {
  return request<void>(`/purchase/partial-inbounds/${id}`, { method: "DELETE" });
}

// 26차 후속 #21 — 시마노/다이와 B2B 엑셀 업로드 preview
export function previewPartialInboundFromB2bExcel(
  poId: number,
  file: File,
): Promise<B2BInboundPreviewResponse> {
  const fd = new FormData();
  fd.append("file", file);
  return request<B2BInboundPreviewResponse>(
    `/purchase/orders/${poId}/partial-inbounds/from-b2b-excel/preview`,
    { method: "POST", body: fd },
  );
}

// 26차 후속 #21 — 시마노/다이와 B2B 엑셀 업로드 → 실입고 배치 생성
export function createPartialInboundFromB2bExcel(
  poId: number,
  file: File,
  opts: { notes?: string; inbound_date?: string; created_by?: string } = {},
): Promise<PartialInbound> {
  const fd = new FormData();
  fd.append("file", file);
  if (opts.notes !== undefined) fd.append("notes", opts.notes);
  if (opts.inbound_date !== undefined) fd.append("inbound_date", opts.inbound_date);
  if (opts.created_by !== undefined) fd.append("created_by", opts.created_by);
  return request<PartialInbound>(
    `/purchase/orders/${poId}/partial-inbounds/from-b2b-excel`,
    { method: "POST", body: fd },
  );
}

// 안전재고 추천
export function getSafetyStockSuggestions(opts?: {
  excludeBrands?: string[];
  excludeSkus?: string[];
}): Promise<SafetyStockSuggestion[]> {
  const qs = new URLSearchParams();
  if (opts?.excludeBrands && opts.excludeBrands.length > 0) {
    qs.set("exclude_brands", opts.excludeBrands.join(","));
  }
  if (opts?.excludeSkus && opts.excludeSkus.length > 0) {
    qs.set("exclude_skus", opts.excludeSkus.join(","));
  }
  const query = qs.toString();
  return request<SafetyStockSuggestion[]>(
    query ? `/purchase/safety-stock-suggestions?${query}` : "/purchase/safety-stock-suggestions",
  );
}

// 발주 서류 파일 관리
export interface PoDocument {
  filename: string;
  size: number;
  modified: number;
}

export function listPoDocuments(poId: number): Promise<{ po_id: number; files: PoDocument[] }> {
  return request(`/purchase/orders/${poId}/documents`);
}

export async function uploadPoDocument(poId: number, file: File): Promise<{ ok: boolean; filename: string; size: number }> {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_URL}/purchase/orders/${poId}/documents`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "업로드 실패" }));
    throw new Error(err.detail || "업로드 실패");
  }
  return res.json();
}

export function deletePoDocument(poId: number, filename: string): Promise<void> {
  return request(`/purchase/orders/${poId}/documents/${encodeURIComponent(filename)}`, { method: "DELETE" });
}

export function getPoDocumentUrl(poId: number, filename: string): string {
  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : "";
  return `${API_URL}/purchase/orders/${poId}/documents/${encodeURIComponent(filename)}`;
}

export function addPurchaseOrderItem(poId: number, body: {
  product_name: string;
  sku?: string;
  jan_code?: string;
  product_url?: string;
  quantity_ordered?: number;
  unit_price_cny?: number;
  unit_price_krw?: number;
  china_tracking_no?: string;
  memo?: string;
}): Promise<PurchaseOrderItem> {
  return request<PurchaseOrderItem>(`/purchase/order-items?po_id=${poId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updatePurchaseOrderItem(
  itemId: number,
  body: Partial<{
    product_name: string;
    sku: string;
    quantity_ordered: number;
    quantity_received_china: number;
    quantity_received_korea: number;
    unit_price_cny: number;
    unit_price_krw: number;
    memo: string;
  }>
): Promise<PurchaseOrderItem> {
  return request<PurchaseOrderItem>(`/purchase/order-items/${itemId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// [ERP-PO-ITEM-ADMIN-QUANTITY-EDIT-20260616-001]
// 관리자 전용 발주수량 정정. backend 에서 require_admin + 입고수량 가드 + audit 기록.
// quantity_ordered 만 수정. 성공 시 호출자가 PO 상세를 reload 해 합계를 갱신한다.
export interface PoItemQuantityUpdateResult {
  ok: boolean;
  item_id: number;
  purchase_order_id: number;
  sku: string | null;
  old_quantity_ordered: number;
  new_quantity_ordered: number;
}

export function updatePurchaseOrderItemQuantity(
  itemId: number,
  quantityOrdered: number,
  reason?: string,
): Promise<PoItemQuantityUpdateResult> {
  return request<PoItemQuantityUpdateResult>(`/purchase/order-items/${itemId}/quantity`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ quantity_ordered: quantityOrdered, reason: reason || null }),
  });
}

export function deletePurchaseOrderItem(itemId: number): Promise<void> {
  return request<void>(`/purchase/order-items/${itemId}`, { method: "DELETE" });
}

// [ERP-PO-DETAIL-SELECTED-ITEMS-BULK-QTY-TRANSFER-DEV-20260625-001]
// 선택 품목 수량 일괄 변경 / 기존 PO 이동 / 새 PO 분리. backend require_admin + 입고/lock 가드 + audit.
export interface PoBulkQuantityResult {
  ok: boolean;
  purchase_order_id: number;
  updated: { item_id: number; sku: string | null; old_quantity_ordered: number; new_quantity_ordered: number }[];
  po_total_quantity: number;
  po_total_price_krw: number;
}
export function bulkUpdatePoItemQuantity(poId: number, body: { item_ids: number[]; mode: "set" | "delta"; quantity?: number; delta?: number; reason?: string }): Promise<PoBulkQuantityResult> {
  return request<PoBulkQuantityResult>(`/purchase/orders/${poId}/items/bulk-quantity`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface PoItemsMoveResult {
  ok: boolean;
  source_po_id: number;
  target_po_id: number;
  moved_count: number;
  merged_count: number;
  relocated_count: number;
  source_total_quantity: number;
  target_total_quantity: number;
}
export function movePoItemsToPo(sourcePoId: number, body: { item_ids: number[]; target_po_id: number }): Promise<PoItemsMoveResult> {
  return request<PoItemsMoveResult>(`/purchase/orders/${sourcePoId}/items/move`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface PoItemsSplitResult {
  ok: boolean;
  source_po_id: number;
  new_po_id: number;
  new_po_no: string;
  moved_count: number;
  source_total_quantity: number;
}
export function splitPoItemsToNewPo(sourcePoId: number, body: { item_ids: number[]; supplier_name?: string; memo?: string }): Promise<PoItemsSplitResult> {
  return request<PoItemsSplitResult>(`/purchase/orders/${sourcePoId}/items/split-to-new-po`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// [ERP-PO-ITEM-COST-SNAPSHOT-CURRENT-STOCK-LOCATION-UPDATE-DEV-20260623-001]
// 발주 단가를 상품DB 최신 원가로 명시 갱신. backend 에서 require_admin + 상태(requested|ordered) 가드 + audit.
// 개별: 성공 시 갱신된 품목 응답 반환. 호출자가 PO 상세 reload 로 합계 갱신.
export function refreshPurchaseOrderItemCost(
  poId: number,
  itemId: number,
): Promise<PurchaseOrderItem> {
  return request<PurchaseOrderItem>(
    `/purchase/orders/${poId}/items/${itemId}/refresh-cost`,
    { method: "POST" },
  );
}

export interface RefreshItemCostsResult {
  ok: boolean;
  po_id: number;
  updated_item_ids: number[];
  skipped: { item_id: number; sku: string | null; reason: string }[];
  total_price_krw: number;
}

// 선택(scope=selected, item_ids) 또는 전체(scope=all) 품목 일괄 갱신.
export function refreshPurchaseOrderItemCosts(
  poId: number,
  body: { scope: "selected" | "all"; item_ids?: number[] },
): Promise<RefreshItemCostsResult> {
  return request<RefreshItemCostsResult>(
    `/purchase/orders/${poId}/items/refresh-costs`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export function deletePurchaseOrder(poId: number): Promise<void> {
  return request<void>(`/purchase/orders/${poId}`, { method: "DELETE" });
}

export function getInboundShipments(params?: {
  status?: string;
  limit?: number;
}): Promise<InboundShipmentListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  return request<InboundShipmentListResponse>(`/purchase/shipments?${qs}`);
}

export function createInboundShipment(body: {
  po_ids?: number[];
  ezytao_order_no?: string;
  ezytao_box_count?: number;
  ezytao_total_weight_kg?: number;
  ezytao_shipping_method?: string;
  ezytao_forwarding_date?: string;
  ezytao_tracking_no?: string;
  domestic_courier?: string;
  domestic_tracking_no?: string;
  memo?: string;
}): Promise<InboundShipment> {
  return request<InboundShipment>("/purchase/shipments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateInboundShipment(
  id: number,
  body: Partial<{
    status: string;
    po_ids: number[];
    ezytao_order_no: string;
    ezytao_box_count: number;
    ezytao_total_weight_kg: number;
    ezytao_shipping_method: string;
    ezytao_departure_date: string;
    ezytao_arrival_china: string;
    ezytao_forwarding_date: string;
    ezytao_tracking_no: string;
    domestic_courier: string;
    domestic_tracking_no: string;
    arrival_date_korea: string;
    stocked_date: string;
    memo: string;
  }>
): Promise<InboundShipment> {
  return request<InboundShipment>(`/purchase/shipments/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── 이지타오 배송신청서 Export ──────────────────────────────────

export interface EzytaoExportPreviewRow {
  번호: number;
  PO번호: string;
  상품명: string;
  "상품 URL": string;
  "SKU/JAN코드": string;
  수량: number;
  "단가(CNY)": number | string;
  "소계(CNY)": number | string;
  수령인: string;
  전화번호: string;
  주소: string;
  운송방법: string;
  비고: string;
}

// [ERP-PO-EASYTAO-REQUIRED-FIELDS-PREVIEW-20260615-001]
// 필수값(영문명/통관품목번호/이미지 URL) 누락 품목 단위 행.
// missing_fields 코드: english_name | customs_hs_code | image_url | product_match
export interface EzytaoMissingRow {
  sku: string | null;
  product_name: string;
  brand: string | null;
  option1: string | null;
  option2: string | null;
  missing_fields: string[];
}

export interface EzytaoExportPreview {
  shipment_no: string;
  total_pos: number;
  total_rows: number;
  preview_rows: EzytaoExportPreviewRow[];
  missing_fields: string[];
  // [ERP-PO-EASYTAO-REQUIRED-FIELDS-PREVIEW-20260615-001] PO 단위 preview 만 채워짐.
  total_items?: number;
  missing_count?: number;
  missing_rows?: EzytaoMissingRow[];
}

export async function getEzytaoExportPreview(
  shipmentId: number
): Promise<EzytaoExportPreview> {
  return request<EzytaoExportPreview>(
    `/purchase/shipments/${shipmentId}/export-ezytao/preview`
  );
}

export async function downloadEzytaoExport(shipmentId: number): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(
    `${API_URL}/purchase/shipments/${shipmentId}/export-ezytao`,
    { headers }
  );
  if (!res.ok) throw new Error(`Export 실패: ${res.status}`);
  const blob = await res.blob();
  const contentDisposition = res.headers.get("Content-Disposition") || "";
  let filename = `이지타오_배송신청서_${shipmentId}.xlsx`;
  const match = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (match) filename = decodeURIComponent(match[1]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// 19차 — PO 단독 이지타오 배송신청서 다운로드 (shipment 미생성 상태에서 발주 단계 샘플)
export async function getPoEzytaoExportPreview(
  poId: number
): Promise<EzytaoExportPreview> {
  return request<EzytaoExportPreview>(
    `/purchase/orders/${poId}/export-ezytao/preview`
  );
}

export async function downloadPoEzytaoExport(poId: number): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(
    `${API_URL}/purchase/orders/${poId}/export-ezytao`,
    { headers }
  );
  if (!res.ok) throw new Error(`Export 실패: ${res.status}`);
  const blob = await res.blob();
  const contentDisposition = res.headers.get("Content-Disposition") || "";
  let filename = `이지타오_배송신청서_PO${poId}.xlsx`;
  const match = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (match) filename = decodeURIComponent(match[1]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// [ERP-PO-EASYTAO-EXPORT-30-ITEM-SPLIT-ZIP-20260616-001]
// PO 이지타오 export 를 품목 30개 단위로 분할한 xlsx 묶음 ZIP 다운로드.
// 품목이 30개 이하인 경우에도 ZIP 1개로 내려받는다.
export async function downloadPoEzytaoExportSplit(poId: number): Promise<void> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(
    `${API_URL}/purchase/orders/${poId}/export-ezytao-split`,
    { headers }
  );
  if (!res.ok) throw new Error(`분할 Export 실패: ${res.status}`);
  const blob = await res.blob();
  const contentDisposition = res.headers.get("Content-Disposition") || "";
  let filename = `이지타오_배송신청서_PO${poId}_분할.zip`;
  const match = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (match) filename = decodeURIComponent(match[1]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── 감사 로그 ─────────────────────────────────────────────────

export interface AuditLogListItem {
  id: number;
  entity_type: string;
  entity_id: string | null;
  action_type: string;
  changed_by: string | null;
  changed_at: string;
  summary: string | null;
}

export interface AuditLogDetail extends AuditLogListItem {
  before_json: Record<string, unknown> | null;
  after_json: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

export interface AuditLogListResponse {
  total: number;
  items: AuditLogListItem[];
}

export function getAuditLogs(params?: {
  entity_type?: string;
  action_type?: string;
  changed_by?: string;
  entity_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}): Promise<AuditLogListResponse> {
  const qs = new URLSearchParams();
  if (params?.entity_type) qs.set("entity_type", params.entity_type);
  if (params?.action_type) qs.set("action_type", params.action_type);
  if (params?.changed_by) qs.set("changed_by", params.changed_by);
  if (params?.entity_id) qs.set("entity_id", params.entity_id);
  if (params?.date_from) qs.set("date_from", params.date_from);
  if (params?.date_to) qs.set("date_to", params.date_to);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<AuditLogListResponse>(`/audit-logs?${qs}`);
}

export function getAuditLog(id: number): Promise<AuditLogDetail> {
  return request<AuditLogDetail>(`/audit-logs/${id}`);
}

// ── 승인 요청 ─────────────────────────────────────────────────

export const APPROVAL_REQUEST_TYPE_LABELS: Record<string, string> = {
  inventory_adjustment: "재고 조정",
  purchase_approval: "구매 발주 승인",
  manual_price_change: "수동 가격 변경",
  return_discard: "폐기 처리",
  as_charge_confirm: "AS 청구 확정",
  cost_factor_apply: "원가팩터 적용",
  deposit_confirm: "입금 확인",
  as_deposit_confirm: "AS 입금 확인",
  shipment_hold_release: "출고중지 해제",
  return_restock_as_new: "반품 새제품 재등록",
  // design_approval_workflow.md Tier 1 확장
  inventory_bulk_import: "재고 일괄 업로드",
  product_change: "상품 필드 변경",
  product_bulk_import: "상품 일괄 업로드",
  order_cancellation: "주문 취소",
  master_data_change: "마스터 데이터 변경",
};

export const APPROVAL_TYPE_ENTITY_GUIDE: Record<string, { entity_type: string; placeholder: string }> = {
  deposit_confirm:         { entity_type: "order",          placeholder: "주문번호 (예: 2026-XXXX)" },
  as_deposit_confirm:      { entity_type: "as_request",     placeholder: "AS ID (숫자)" },
  shipment_hold_release:   { entity_type: "return_receipt", placeholder: "출고중지 ID (숫자)" },
  return_restock_as_new:   { entity_type: "return_receipt", placeholder: "반품 접수 ID (숫자)" },
  inventory_adjustment:    { entity_type: "inventory",      placeholder: "SKU" },
  purchase_approval:       { entity_type: "purchase_order", placeholder: "발주 ID (숫자)" },
  manual_price_change:     { entity_type: "product",        placeholder: "SKU" },
  return_discard:          { entity_type: "return_receipt", placeholder: "반품 접수 ID (숫자)" },
  as_charge_confirm:       { entity_type: "as_request",     placeholder: "AS ID (숫자)" },
  cost_factor_apply:       { entity_type: "product",        placeholder: "SKU" },
  inventory_bulk_import:   { entity_type: "inventory",      placeholder: "업로드 배치 ID" },
  product_change:          { entity_type: "product",        placeholder: "SKU" },
  product_bulk_import:     { entity_type: "product",        placeholder: "업로드 배치 ID" },
  order_cancellation:      { entity_type: "order",          placeholder: "주문번호" },
  master_data_change:      { entity_type: "product",        placeholder: "대상 키" },
};

export const APPROVAL_STATUS_LABELS: Record<string, string> = {
  pending: "대기중",
  approved: "승인",
  rejected: "반려",
  cancelled: "취소",
};

export const APPROVAL_STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-500",
};

export interface ApprovalRequestListItem {
  id: number;
  request_type: string;
  entity_type: string;
  entity_id: string | null;
  requested_by: string | null;
  requested_at: string;
  status: string;
  approver: string | null;
  reason: string | null;
  memo: string | null;
  payload_json: Record<string, unknown> | null;
  auto_approved: boolean;
  updated_at: string;
}

export interface ApprovalRequestDetail extends ApprovalRequestListItem {
  approved_at: string | null;
  rejected_at: string | null;
  memo: string | null;
  created_at: string;
}

export interface ApprovalRequestListResponse {
  total: number;
  items: ApprovalRequestListItem[];
}

export function getApprovalRequests(params?: {
  status?: string;
  request_type?: string;
  requested_by?: string;
  entity_type?: string;
  limit?: number;
  offset?: number;
}): Promise<ApprovalRequestListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.request_type) qs.set("request_type", params.request_type);
  if (params?.requested_by) qs.set("requested_by", params.requested_by);
  if (params?.entity_type) qs.set("entity_type", params.entity_type);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return request<ApprovalRequestListResponse>(`/approvals?${qs}`);
}

export function getApprovalRequest(id: number): Promise<ApprovalRequestDetail> {
  return request<ApprovalRequestDetail>(`/approvals/${id}`);
}

export function createApprovalRequest(body: {
  request_type: string;
  entity_type: string;
  entity_id?: string;
  requested_by?: string;
  memo?: string;
}): Promise<ApprovalRequestDetail> {
  return request<ApprovalRequestDetail>("/approvals", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function approveRequest(id: number, body: { approver?: string; reason?: string }): Promise<ApprovalRequestDetail> {
  return request<ApprovalRequestDetail>(`/approvals/${id}/approve`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function rejectRequest(id: number, body: { approver?: string; reason?: string }): Promise<ApprovalRequestDetail> {
  return request<ApprovalRequestDetail>(`/approvals/${id}/reject`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── 출고 검수 (Picking) ───────────────────────────────────────────────────────

export interface HoldInfo {
  id: number;
  request_type: string;
  status: string;
  request_reason: string | null;
  requested_by: string;
  requested_at: string;
}

// [ERP-PACKING-BUNDLE-SET-COMPONENT-SCAN-CHECK-20260618-002]
export interface SetComponent {
  component_sku: string;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  required_qty: number;
  scanned_qty: number;
  is_scanned_complete: boolean;
}

export interface PickItemResponse {
  id: number;
  order_item_id: number;
  sku: string;
  product_name: string | null;
  option_name: string | null;
  required_qty: number;
  scanned_qty: number;
  status: string; // waiting | partial | completed | blocked
  // [ERP-PACKING-BUNDLE-SET-COMPONENT-SCAN-CHECK-20260618-002] 세트 구성품 스캔 진행
  is_set_item?: boolean;
  set_components?: SetComponent[];
}

export interface ScanLogResponse {
  id: number;
  scanned_value: string;
  scan_result: string;
  scanned_by: string | null;
  created_at: string;
}

export interface ShipmentPickDetail {
  id: number;
  order_no: string;
  location_code: string;
  status: string; // waiting | picking | picked | packed | shipped | blocked
  picker_name: string | null;
  started_at: string | null;
  completed_at: string | null;
  invoice_no: string | null;
  carrier_name: string | null;
  shipped_at: string | null;
  memo: string | null;
  receiver_name: string | null;
  platform: string | null;
  is_daiwa_direct_ship: boolean;
  active_holds: HoldInfo[];
  items: PickItemResponse[];
  recent_scan_logs: ScanLogResponse[];
  created_at: string;
  updated_at: string;
}

export interface ShipmentPickListItem {
  id: number;
  order_no: string;
  location_code: string;
  status: string;
  picker_name: string | null;
  started_at: string | null;
  completed_at: string | null;
  invoice_no: string | null;
  carrier_name: string | null;
  shipped_at: string | null;
  receiver_name: string | null;
  platform: string | null;
  item_count: number;
  scanned_count: number;
  has_active_hold: boolean;
  created_at: string;
  updated_at: string;
}

export interface ShipmentPickListResponse {
  total: number;
  items: ShipmentPickListItem[];
}

export interface ScanResponse {
  scan_result: string; // matched | over_scan | sku_not_in_order | cancelled_order | blocked_by_hold
  message: string;
  scanned_value: string;
  matched_sku: string | null;
  scanned_qty: number | null;
  required_qty: number | null;
  item_status: string | null;
  pick_status: string;
  // 26차 후속 #82 Phase 1 — 안정적 분기용 에러 코드 (없으면 null).
  // MATCHED | SHIPMENT_HOLD_ACTIVE | PACKING_ALREADY_COMPLETED |
  // WRONG_BARCODE | OVER_SCAN | SET_PARENT_SCAN | SET_COMPONENT_MATCHED | SET_COMPONENT_OVER
  error_code?: string | null;
  // [ERP-PACKING-BUNDLE-SET-COMPONENT-SCAN-CHECK-20260618-002] 세트 구성품 스캔 시 대표 SKU
  set_bundle_sku?: string | null;
}

export function startPick(body: {
  order_no: string;
  location_code?: string;
  picker_name?: string;
}): Promise<ShipmentPickDetail> {
  return request<ShipmentPickDetail>("/picking/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function getPickList(params?: {
  status?: string;
  order_no?: string;
  limit?: number;
  offset?: number;
}): Promise<ShipmentPickListResponse> {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.order_no) q.set("order_no", params.order_no);
  if (params?.limit !== undefined) q.set("limit", String(params.limit));
  if (params?.offset !== undefined) q.set("offset", String(params.offset));
  const qs = q.toString();
  return request<ShipmentPickListResponse>(`/picking${qs ? "?" + qs : ""}`);
}

export function getPickDetail(order_no: string): Promise<ShipmentPickDetail> {
  return request<ShipmentPickDetail>(`/picking/${encodeURIComponent(order_no)}`);
}

export function scanSku(
  order_no: string,
  body: { scanned_value: string; scanned_by?: string }
): Promise<ScanResponse> {
  return request<ScanResponse>(`/picking/${encodeURIComponent(order_no)}/scan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function completePick(
  order_no: string,
  body?: { picker_name?: string }
): Promise<ShipmentPickDetail> {
  return request<ShipmentPickDetail>(`/picking/${encodeURIComponent(order_no)}/complete`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

// ── 운영 대시보드 ─────────────────────────────────────────────────────────────

export interface DashboardOrderStats {
  count: number;
  total_product_amount: number;
  shipping_fee: number;
  additional_shipping_fee: number;
  paid_amount: number;
  fee_amount: number;
  settlement_amount: number;
}

export interface DashboardPickingStats {
  started_today: number;
  shipped_today: number;
  hold_pending: number;
  blocked_picks: number;
}

export interface DashboardReturnStats {
  awaiting_inspection: number;
  completed_today: number;
  quarantine_total: number;
}

export interface DashboardLocationItem {
  location_code: string;
  label: string;
  sku_count: number;
  total_quantity: number;
}

export interface DashboardInventoryStats {
  total_skus: number;
  total_quantity: number;
  quarantine_quantity: number;
  rocket_fc_quantity: number;
  by_location: DashboardLocationItem[];
}

export interface DashboardAsStats {
  active_count: number;
  repairing: number;
  completed_today: number;
  customer_charge_total: number;
}

export interface DashboardPurchaseStats {
  in_progress_count: number;
  waiting_arrival_count: number;
  stocked_in_today: number;
}

export interface DashboardSummary {
  date: string;
  orders: DashboardOrderStats;
  picking: DashboardPickingStats;
  returns: DashboardReturnStats;
  inventory: DashboardInventoryStats;
  as_requests: DashboardAsStats;
  purchase: DashboardPurchaseStats;
}

export function getDashboardSummary(date?: string): Promise<DashboardSummary> {
  const q = date ? `?date=${date}` : "";
  return request<DashboardSummary>(`/dashboard/summary${q}`);
}

export function getDailyReport(date?: string): Promise<DashboardSummary> {
  const q = date ? `?date=${date}` : "";
  return request<DashboardSummary>(`/dashboard/daily-report${q}`);
}

export function shipPick(
  order_no: string,
  body: { invoice_no: string; carrier_name?: string; shipped_by?: string }
): Promise<ShipmentPickDetail> {
  return request<ShipmentPickDetail>(`/picking/${encodeURIComponent(order_no)}/ship`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ─── Work Assignments ────────────────────────────────────────

export interface WorkAssignment {
  id: number;
  task_type: string;
  entity_type: string;
  entity_id: string | null;
  title: string;
  status: string;
  assignee: string | null;
  assigned_by: string | null;
  assigned_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  priority: string;
  due_date: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
  entity_link: string | null;
  assignee_on_leave: boolean;
}

export interface WorkAssignmentListResponse {
  items: WorkAssignment[];
  total: number;
  counts: Record<string, number>;
}

export interface WorkAssignmentCreate {
  task_type: string;
  entity_type: string;
  entity_id?: string;
  title: string;
  status?: string;
  assignee?: string;
  assigned_by?: string;
  priority?: string;
  due_date?: string;
  memo?: string;
}

export interface WorkAssignmentUpdate {
  assignee?: string | null;
  assigned_by?: string;
  priority?: string;
  status?: string;
  due_date?: string;
  memo?: string;
}

export function getWorkAssignments(params?: {
  assignee?: string;
  status?: string;
  task_type?: string;
  priority?: string;
}): Promise<WorkAssignmentListResponse> {
  const q = new URLSearchParams();
  if (params?.assignee) q.set("assignee", params.assignee);
  if (params?.status) q.set("status", params.status);
  if (params?.task_type) q.set("task_type", params.task_type);
  if (params?.priority) q.set("priority", params.priority);
  const qs = q.toString() ? `?${q.toString()}` : "";
  return request<WorkAssignmentListResponse>(`/work-assignments${qs}`);
}

export function createWorkAssignment(body: WorkAssignmentCreate): Promise<WorkAssignment> {
  return request<WorkAssignment>("/work-assignments", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateWorkAssignment(id: number, body: WorkAssignmentUpdate): Promise<WorkAssignment> {
  return request<WorkAssignment>(`/work-assignments/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function startWorkAssignment(id: number, started_by?: string): Promise<WorkAssignment> {
  return request<WorkAssignment>(`/work-assignments/${id}/start`, {
    method: "PATCH",
    body: JSON.stringify({ started_by }),
  });
}

export function completeWorkAssignment(id: number, completed_by?: string): Promise<WorkAssignment> {
  return request<WorkAssignment>(`/work-assignments/${id}/complete`, {
    method: "PATCH",
    body: JSON.stringify({ completed_by }),
  });
}

export function seedWorkAssignmentSamples(): Promise<{ message: string; created: number }> {
  return request("/work-assignments/seed-samples", { method: "POST" });
}

// ─── AS 2차 ──────────────────────────────────────────────────

export function confirmAsCharge(asId: number, confirmed_by?: string): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${asId}/confirm-charge`, {
    method: "POST",
    body: JSON.stringify({ confirmed_by }),
  });
}

export function createAsShipment(
  asId: number,
  body: { requested_by?: string; memo?: string }
): Promise<AsShipmentRequest> {
  return request<AsShipmentRequest>(`/as/requests/${asId}/create-shipment`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getAsShipment(asId: number): Promise<AsShipmentRequest | null> {
  return request<AsShipmentRequest | null>(`/as/requests/${asId}/shipment`);
}

// ─── Message Templates ────────────────────────────────────────

export interface MessageTemplate {
  id: number;
  template_type: string;
  title: string;
  body: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function getMessageTemplates(params?: {
  template_type?: string;
  active_only?: boolean;
}): Promise<MessageTemplate[]> {
  const q = new URLSearchParams();
  if (params?.template_type) q.set("template_type", params.template_type);
  if (params?.active_only) q.set("active_only", "true");
  const qs = q.toString() ? `?${q}` : "";
  return request<MessageTemplate[]>(`/message-templates${qs}`);
}

export function createMessageTemplate(body: {
  template_type: string;
  title: string;
  body: string;
  is_active?: boolean;
}): Promise<MessageTemplate> {
  return request<MessageTemplate>("/message-templates", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateMessageTemplate(
  id: number,
  body: { title?: string; body?: string; is_active?: boolean }
): Promise<MessageTemplate> {
  return request<MessageTemplate>(`/message-templates/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function seedMessageTemplates(): Promise<{ message: string; created: number }> {
  return request("/message-templates/seed", { method: "POST" });
}



// ─── Calendar & Leave ─────────────────────────

export interface CalendarEvent {
  id: number;
  title: string;
  description: string | null;
  event_type: string;
  start_date: string;
  end_date: string;
  is_all_day: boolean;
  owner_name: string | null;
  visibility: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CalendarEventCreate {
  title: string;
  description?: string;
  event_type?: string;
  start_date: string;
  end_date: string;
  is_all_day?: boolean;
  owner_name?: string;
  visibility?: string;
  created_by?: string;
}

export interface LeaveRequest {
  id: number;
  employee_name: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  is_half_day: boolean;
  half_day_type: string | null;
  reason: string | null;
  status: string;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeaveRequestCreate {
  employee_name: string;
  leave_type?: string;
  start_date: string;
  end_date: string;
  is_half_day?: boolean;
  half_day_type?: string;
  reason?: string;
}

export interface LeaveListResponse {
  total: number;
  items: LeaveRequest[];
}

// 26차 후속 — 운영대시보드 위젯 1차: 금주 등록 신상품.
export interface RecentProduct {
  sku: string;
  product_name: string;
  brand: string | null;
  category: string | null;
  created_at: string | null;
  total_stock: number;
  is_active: boolean;
  /** product_platform_meta 의 smartstore listing_url (없으면 null). */
  naver_url?: string | null;
}
export interface RecentProductsResponse {
  week_start: string;
  week_end: string;
  total: number;
  items: RecentProduct[];
}
export function getRecentProducts(limit = 50): Promise<RecentProductsResponse> {
  return request(`/dashboard/recent-products?limit=${limit}`);
}

// 26차 후속 — 운영대시보드 위젯 1차 보정: 내 주요 로그인 (DB SoT).
export interface MyExternalLink {
  id: number;
  title: string;
  url: string;
  sort_order: number;
  memo: string | null;
  created_at: string;
  updated_at: string;
}
export function listMyExternalLinks(): Promise<MyExternalLink[]> {
  return request(`/me/external-links`);
}
export function createMyExternalLink(body: {
  title: string;
  url: string;
  memo?: string | null;
}): Promise<MyExternalLink> {
  return request(`/me/external-links`, { method: "POST", body: JSON.stringify(body) });
}
export function updateMyExternalLink(
  id: number,
  body: Partial<{ title: string; url: string; memo: string | null; sort_order: number }>,
): Promise<MyExternalLink> {
  return request(`/me/external-links/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}
export function deleteMyExternalLink(id: number): Promise<void> {
  return request(`/me/external-links/${id}`, { method: "DELETE" });
}

// 26차 후속 — 운영대시보드 위젯 1차 보정: 소싱 업사이드 상품 (공유 SoT, soft delete).
export interface SourcingUpsideItem {
  id: number;
  planned_product_name: string;
  purchase_url: string | null;
  benchmark_url: string | null;
  expected_cost_krw: number | null;
  is_active: boolean;
  created_by_user_id: number;
  updated_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}
export function listSourcingUpside(includeInactive = false): Promise<SourcingUpsideItem[]> {
  const qs = includeInactive ? "?include_inactive=true" : "";
  return request(`/dashboard/sourcing-upside-products${qs}`);
}
export function createSourcingUpside(body: {
  planned_product_name: string;
  purchase_url?: string | null;
  benchmark_url?: string | null;
  expected_cost_krw?: number | null;
}): Promise<SourcingUpsideItem> {
  return request(`/dashboard/sourcing-upside-products`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
export function updateSourcingUpside(
  id: number,
  body: Partial<{
    planned_product_name: string;
    purchase_url: string | null;
    benchmark_url: string | null;
    expected_cost_krw: number | null;
    is_active: boolean;
  }>,
): Promise<SourcingUpsideItem> {
  return request(`/dashboard/sourcing-upside-products/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}
export function softDeleteSourcingUpside(id: number): Promise<SourcingUpsideItem> {
  return request(`/dashboard/sourcing-upside-products/${id}`, { method: "DELETE" });
}

export function getCalendarEvents(params: {
  year?: number;
  month?: number;
  start?: string;
  end?: string;
  event_type?: string;
  visibility?: string;
}): Promise<CalendarEvent[]> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined) qs.set(k, String(v)); });
  return request<CalendarEvent[]>(`/calendar/events?${qs}`);
}

export function createCalendarEvent(body: CalendarEventCreate): Promise<CalendarEvent> {
  return request<CalendarEvent>("/calendar/events", { method: "POST", body: JSON.stringify(body) });
}

export function updateCalendarEvent(id: number, body: Partial<CalendarEventCreate>): Promise<CalendarEvent> {
  return request<CalendarEvent>(`/calendar/events/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function deleteCalendarEvent(id: number): Promise<void> {
  return request<void>(`/calendar/events/${id}`, { method: "DELETE" });
}

export function getLeaves(params: {
  status?: string;
  employee_name?: string;
  leave_type?: string;
  year?: number;
}): Promise<LeaveListResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined) qs.set(k, String(v)); });
  return request<LeaveListResponse>(`/calendar/leaves?${qs}`);
}

export function createLeave(body: LeaveRequestCreate): Promise<LeaveRequest> {
  return request<LeaveRequest>("/calendar/leaves", { method: "POST", body: JSON.stringify(body) });
}

export function approveLeave(id: number, approved_by: string): Promise<LeaveRequest> {
  return request<LeaveRequest>(`/calendar/leaves/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({ approved_by }),
  });
}

export function rejectLeave(id: number, rejected_by: string, reject_reason?: string): Promise<LeaveRequest> {
  return request<LeaveRequest>(`/calendar/leaves/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ rejected_by, reject_reason }),
  });
}

export function cancelLeave(id: number, cancelled_by?: string): Promise<LeaveRequest> {
  return request<LeaveRequest>(`/calendar/leaves/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify({ cancelled_by }),
  });
}

export function seedCalendar(): Promise<{ message: string; events: number; leaves: number }> {
  return request("/calendar/seed", { method: "POST" });
}

export interface LeaveBalance {
  accrued: number;
  used: number;
  balance: number;
  hire_date: string | null;
  year_index: number | null;
  period_start: string | null;
  period_end: string | null;
  allocation: number;
}

export function getLeaveBalance(): Promise<LeaveBalance> {
  return request<LeaveBalance>("/calendar/leaves/balance");
}

// [ERP-VACATION-CALENDAR-SYNC-AND-REMAINING-DAYS-20260606-001] 전 직원 잔여 휴가 (admin)
export interface AllLeaveBalanceItem {
  user_id: number;
  name: string;
  role: string | null;
  hire_date: string | null;
  accrued: number;
  used: number;
  balance: number;
  pending: number;
  allocation: number;
  period_start: string | null;
  period_end: string | null;
  year_index: number | null;
}
export function getAllLeaveBalances(): Promise<{ items: AllLeaveBalanceItem[] }> {
  return request<{ items: AllLeaveBalanceItem[] }>("/calendar/leaves/balance/all");
}


// ─── Today Summary ─────────────────────────

export interface TodaySummaryEvent {
  id: number;
  title: string;
  event_type: string;
  start_date: string;
  end_date: string;
  visibility: string;
  owner_name: string | null;
}

export interface TodaySummary {
  today: string;
  today_events: TodaySummaryEvent[];
  today_leave_employees: string[];
  today_leave_count: number;
}

export function getTodaySummary(): Promise<TodaySummary> {
  return request<TodaySummary>("/calendar/today-summary");
}

// [ERP-OPERATIONS-DASHBOARD-ROUTINE-20260519-001] 운영대시보드 "오늘 해야 할 업무"
// 섹션용 종합 카운트. pending_accounts / pending_leaves 는 admin 일 때만 값,
// 그 외 직원에게는 null 로 응답 — 화면에서 숨김.
export interface RoutineSummary {
  pending_accounts: number | null;
  pending_leaves: number | null;
  shipment_holds_pending: number;
  barcode_missing_products: number;
}

export function getRoutineSummary(): Promise<RoutineSummary> {
  return request<RoutineSummary>("/dashboard/routine-summary");
}

export function getOnLeaveToday(): Promise<{ on_leave: string[] }> {
  return request<{ on_leave: string[] }>("/work-assignments/on-leave-today");
}


// ─── Period Report ────────────────────────

export interface PeriodOrderStats {
  count: number;
  total_product_amount: number;
  shipping_fee: number;
  additional_shipping_fee: number;
  paid_amount: number;
  fee_amount: number;
  settlement_amount: number;
}

export interface PeriodOpStats {
  shipped_count: number;
  hold_requested: number;
  return_received: number;
  return_completed: number;
  as_received: number;
  as_completed: number;
  as_customer_charge: number;
  stocked_in: number;
}

export interface PeriodSummary {
  date_from: string;
  date_to: string;
  orders: PeriodOrderStats;
  ops: PeriodOpStats;
}

export interface TrendRow {
  period_label: string;
  date_from: string;
  date_to: string;
  order_count: number;
  total_product_amount: number;
  shipping_fee: number;
  additional_shipping_fee: number;
  paid_amount: number;
  fee_amount: number;
  settlement_amount: number;
  shipped_count: number;
  hold_requested: number;
  return_received: number;
  return_completed: number;
  as_received: number;
  as_completed: number;
  stocked_in: number;
}

export interface TrendsResponse {
  date_from: string;
  date_to: string;
  group_by: string;
  rows: TrendRow[];
  totals: TrendRow;
}

export function getPeriodSummary(date_from: string, date_to: string): Promise<PeriodSummary> {
  return request<PeriodSummary>(`/dashboard/period-summary?date_from=${date_from}&date_to=${date_to}`);
}

export function getTrends(date_from: string, date_to: string, group_by: string): Promise<TrendsResponse> {
  return request<TrendsResponse>(`/dashboard/trends?date_from=${date_from}&date_to=${date_to}&group_by=${group_by}`);
}


// ─── Period Targets ───────────────────────

export const SUPPORTED_METRICS: Record<string, string> = {
  order_count:            "주문건수",
  paid_amount:            "결제총액",
  settlement_amount:      "정산금액",
  shipped_count:          "출고완료",
  as_completed_count:     "AS완료건수",
  return_completed_count: "반품검수완료",
  stocked_in_count:       "구매입고완료",
};

export interface TargetItem {
  id: number;
  year: number;
  month: number;
  metric: string;
  metric_label: string;
  target_value: number;
}

export interface TargetActual {
  metric: string;
  metric_label: string;
  target: number;
  actual: number;
  rate: number;      // 0~∞, 1.0 = 100%
  status: "green" | "yellow" | "red";
}

export function getPeriodTargets(year: number, month: number): Promise<TargetItem[]> {
  return request<TargetItem[]>(`/dashboard/period-targets?year=${year}&month=${month}`);
}

export function upsertPeriodTarget(body: {
  year: number; month: number; metric: string; target_value: number;
}): Promise<TargetItem> {
  return request<TargetItem>("/dashboard/period-targets", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updatePeriodTarget(id: number, target_value: number): Promise<TargetItem> {
  return request<TargetItem>(`/dashboard/period-targets/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ target_value }),
  });
}

export function getTargetActuals(
  date_from: string,
  date_to: string,
  year: number,
  month: number,
): Promise<TargetActual[]> {
  return request<TargetActual[]>(
    `/dashboard/period-target-actuals?date_from=${date_from}&date_to=${date_to}&year=${year}&month=${month}`,
  );
}

export interface TargetAlertResponse {
  at_risk_count: number;
  items: TargetActual[];
}

export function getTargetAlerts(year: number, month: number): Promise<TargetAlertResponse> {
  return request<TargetAlertResponse>(`/dashboard/target-alerts?year=${year}&month=${month}`);
}

// ─── 단건 주문접수 ────────────────────────────────────────────────

export interface CustomerSearchResult {
  id: number;
  customer_name: string;
  phone: string | null;
  address1: string | null;
  zipcode: string | null;
  total_order_count: number;
  last_order_date: string | null;
}

export function searchCustomers(q: string): Promise<CustomerSearchResult[]> {
  return request<CustomerSearchResult[]>(`/orders/customers/search?q=${encodeURIComponent(q)}`);
}

export interface CustomerListItem {
  id: number;
  customer_name: string;
  phone: string | null;
  address1: string | null;
  zipcode: string | null;
  total_order_count: number;
  last_order_date: string | null;
  as_count: number;
  memo: string | null;
  // [ERP-CUSTOMER-DB-SEARCH-ORDERER-RECIPIENT-20260527-001]
  // 검색어가 있을 때만 backend 가 매칭된 필드 집합을 채워서 반환. q 없으면 null/생략.
  // 가능 값: "customer_name" | "customer_phone" | "recipient_name" | "recipient_phone"
  match_sources?: string[] | null;
}

export async function getCustomers(
  params: { q?: string; page?: number; page_size?: number } = {},
): Promise<{ total: number; items: CustomerListItem[]; page: number; page_size: number; total_pages: number }> {
  // 26차 후속 #67 — page/page_size 페이지네이션
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  return request(`/orders/customers?${qs}`);
}

export async function getCustomerDetail(id: number): Promise<unknown> {
  // 반환 스키마는 고객 360 뷰 (customer_360_view.md) 기준으로 `app/customers/page.tsx` 의
  // CustomerDetail 인터페이스에서 타입 정의함. 여기선 unknown 으로 남겨 중복 방지.
  return request(`/orders/customers/${id}`);
}

export function getNextManualOrderNo(): Promise<{ order_no: string }> {
  return request<{ order_no: string }>("/orders/manual/next-no");
}

export interface ManualOrderItemIn {
  sku?: string | null;
  product_name: string;
  option_name?: string | null;
  quantity: number;
  unit_price: number;
  discount_rate?: number;
  discount_amount?: number;
  // [ERP-OFFLINE-POS-ITEM-LEVEL-STOCK-LOCATION-20260527-001]
  // POS(order_source='offline_store') 품목별 재고 차감 위치 — 'office' | 'warehouse'.
  // 미전달 시 body.stock_location → 'office' fallback. 비-POS 흐름에서는 무시.
  stock_location?: "office" | "warehouse" | null;
}

export interface ManualOrderRequest {
  company_code: string;
  customer_id?: number | null;
  customer_name: string;
  customer_phone?: string | null;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  zipcode?: string | null;
  address1?: string | null;
  address2?: string | null;
  payment_method: string;
  shipping_fee: number;
  /** 전체 주문 할인 (원). 라인별 discount_rate/discount_amount 와 동시 사용 금지. */
  order_discount_amount?: number;
  items: ManualOrderItemIn[];
  memo?: string | null;
  order_source?: string | null;
  // [ERP-OFFLINE-POS-ONSITE-SALE-POLICY-FIX-20260527-001]
  // POS(order_source='offline_store') 재고 차감 위치 — 'office' | 'warehouse'.
  stock_location?: "office" | "warehouse" | null;
}

export interface ManualOrderResponse {
  order_no: string;
  customer_id: number;
  total_product_amount: number;
  discount_amount: number;
  shipping_fee: number;
  paid_amount: number;
  item_count: number;
  payment_method: string;
  order_source?: string | null;
}

export function createManualOrder(body: ManualOrderRequest): Promise<ManualOrderResponse> {
  return request<ManualOrderResponse>("/orders/manual", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getNextPosOrderNo(): Promise<{ order_no: string }> {
  return request<{ order_no: string }>("/orders/pos/next-no");
}

// ─── 견적서 (26차 후속 #33) ──────────────────────────────────────

export type QuoteStatus = "quote_pending" | "quote_converted" | "quote_cancelled";

export interface QuoteItemIn {
  sku?: string | null;
  product_name: string;
  option_name?: string | null;
  quantity: number;
  unit_price: number;
  discount_amount?: number;
}

export interface QuoteCreateRequest {
  company_code: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  shipping_fee: number;
  // [ERP-INST-20260513-010] 추가배송비 별도 항목 (선택).
  additional_shipping_fee?: number;
  memo?: string | null;
  items: QuoteItemIn[];
}

export interface QuoteItemOut {
  id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
  unit_price: number | null;
  line_amount: number | null;
}

export interface QuoteResponse {
  id: number;
  order_no: string;
  order_status: QuoteStatus;
  customer_name: string | null;
  customer_phone: string | null;
  shipping_fee: number | null;
  // [ERP-INST-20260513-010] 추가배송비 (additive, optional).
  additional_shipping_fee?: number | null;
  total_product_amount: number | null;
  paid_amount: number | null;
  item_count: number;
  total_qty: number;
  memo: string | null;
  converted_order_no: string | null;
  created_at: string;
  items: QuoteItemOut[];
}

export interface QuoteListResponse {
  total: number;
  items: QuoteResponse[];
}

export interface QuoteConvertRequest {
  customer_name: string;
  customer_phone?: string | null;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  zipcode?: string | null;
  address1?: string | null;
  address2?: string | null;
  payment_method?: string;
  shipping_fee?: number | null;
  memo?: string | null;
}

export function getNextQuoteNo(): Promise<{ order_no: string }> {
  return request<{ order_no: string }>("/orders/quotes/next-no");
}

export function createQuote(body: QuoteCreateRequest): Promise<QuoteResponse> {
  return request<QuoteResponse>("/orders/quotes", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function listQuotes(params?: {
  status?: QuoteStatus | "all";
  limit?: number;
  offset?: number;
}): Promise<QuoteListResponse> {
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  return request<QuoteListResponse>(`/orders/quotes?${q.toString()}`);
}

export function convertQuoteToOrder(
  quoteId: number,
  body: QuoteConvertRequest,
): Promise<ManualOrderResponse> {
  return request<ManualOrderResponse>(`/orders/quotes/${quoteId}/convert`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function cancelQuote(quoteId: number): Promise<QuoteResponse> {
  return request<QuoteResponse>(`/orders/quotes/${quoteId}/cancel`, {
    method: "POST",
  });
}

// ─── 토글 Export ──────────────────────────────────────────────────

export interface ExportItem {
  id: number;
  source: string;
  order_no: string | null;
  as_no: string | null;
  customer_name: string;
  customer_phone: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  sku: string | null;
  product_name: string;
  quantity: number | null;
  paid_amount: number | null;
  payment_method: string | null;
  created_at: string;
  exported_at: string | null;
  as_status: string | null;   // AS 5단계 상태 (as_completed_shipment 행에만 존재 — 보조 표시용)
  // 26차 후속 #30 — 결제 흐름 / 배송 흐름 명시 분리
  payment_status: "paid" | "unpaid" | "not_required" | null;
  shipping_status: "pending" | "shipping" | "delivered" | null;
  // [ERP-TOGGLE-EXPORT-ORDER-TYPE-LABEL-FIX-20260528-001]
  // 업무 유형 구분 (memo prefix / source 기반 정규화)
  export_kind?: "reissue" | "quote" | "manual" | "as" | "invoice_split" | null;
  export_kind_label?: string | null;
}

export interface ExportListResponse {
  total: number;
  items: ExportItem[];
  // [ERP-TOGGLE-EXPORT-TAB-COUNT-BY-ORDER-NO-20260528-001]
  // total_orders: distinct order_no 기준. 탭 카운트 등 "주문건수" 표시에 사용.
  // total: item-level (outerjoin row 수) — 기존 화면 호환.
  total_orders?: number | null;
  // [ERP-TOGGLE-EXPORT-ORDER-TYPE-LABEL-FIX-20260528-001]
  // export_kind 별 distinct 주문 카운트 (chip 카운트 표시).
  kind_counts?: { reissue?: number; quote?: number; manual?: number; as?: number; invoice_split?: number } | null;
  // [ERP-TOGGLE-EXPORT-QUOTE-REGISTER-COUNT-AND-VISIBILITY-DEV-20260622-001]
  // 견적서 등록건(quote_pending) 수 — chip "견적서" 카운트용. export_status 무관 항상 동일.
  quote_register_count?: number;
}

export function getExportList(params?: {
  source?: string;
  unexported_only?: boolean;
  // [ERP-TOGGLE-EXPORT-COMPLETED-TAB-20260528-001] export_status: pending | exported | all
  export_status?: "pending" | "exported" | "all";
  // [ERP-TOGGLE-EXPORT-ORDER-TYPE-LABEL-FIX-20260528-001]
  // 업무 유형 필터 — reissue | quote | manual | as | invoice_split | all
  kind?: "reissue" | "quote" | "manual" | "as" | "invoice_split" | "all";
  limit?: number;
  offset?: number;
}): Promise<ExportListResponse> {
  const q = new URLSearchParams();
  if (params?.source) q.set("source", params.source);
  if (params?.unexported_only) q.set("unexported_only", "true");
  if (params?.export_status) q.set("export_status", params.export_status);
  if (params?.kind) q.set("kind", params.kind);
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  return request<ExportListResponse>(`/orders/export/toggle?${q.toString()}`);
}

// [ERP-TOGGLE-EXPORT-COMPLETED-TAB-20260528-001]
// Export 완료 처리한 주문을 미출고 상태로 되돌리기.
export interface RevertExportedResponse {
  reverted_orders: number;
  reverted_shipments: number;
  blocked: Array<{ id: number; order_no?: string; reason: string }>;
}

export function revertExported(body: {
  order_ids?: number[];
  shipment_ids?: number[];
}): Promise<RevertExportedResponse> {
  return request<RevertExportedResponse>("/orders/export/toggle/revert-exported", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function exportToggleXlsx(
  ids: number[],
  source: string,
): Promise<void> {
  const token = getToken();
  const res = await fetch(
    `${API_URL}/orders/export/toggle/xlsx?source=${encodeURIComponent(source)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(ids),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "xlsx export 실패" }));
    throw new Error(err.detail || "xlsx export 실패");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disp = res.headers.get("Content-Disposition") || "";
  const match = disp.match(/filename="(.+?)"/);
  a.download = match ? match[1] : "toggle_export.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

// ── 채팅/댓글 (업무 객체별) ──────────────────────────────────────

export interface ChatMessageAttachment {
  id: number;
  filename: string;
  kind: "image" | "file";
  mime_type: string;
  size_bytes: number;
  download_url: string;
  thumbnail_url: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface ChatMessage {
  id: number;
  thread_id: number;
  // Phase A — user_id 우선 노출 (직원별 색상 매핑 안정 키). 시스템/외부봇은 null.
  sender_user_id?: number | null;
  sender_name: string;
  sender_role: string | null;
  message: string;
  is_system: boolean;
  created_at: string;
  attachments?: ChatMessageAttachment[];
}

export interface ChatMessagesResponse {
  thread_id: number;
  entity_type: string;
  entity_id: string;
  message_count: number;
  messages: ChatMessage[];
  // 26차 후속 #61 — 페이지네이션 메타. 구버전 백엔드는 미반환할 수 있어 optional.
  has_more?: boolean;
  oldest_message_id?: number | null;
  newest_message_id?: number | null;
  limit?: number;
}

export function getChatMessages(
  entity_type: string,
  entity_id: string,
  options: { since_id?: number; before_id?: number; limit?: number } = {},
): Promise<ChatMessagesResponse> {
  const params = new URLSearchParams({
    entity_type,
    entity_id,
  });
  if (options.since_id) params.set("since_id", String(options.since_id));
  if (options.before_id) params.set("before_id", String(options.before_id));
  if (options.limit) params.set("limit", String(options.limit));
  return request<ChatMessagesResponse>(`/chat/messages?${params.toString()}`);
}

export function postChatMessage(body: {
  entity_type: string;
  entity_id: string;
  sender_name: string;
  sender_role?: string;
  message: string;
  is_system?: boolean;
}): Promise<ChatMessage> {
  return request<ChatMessage>("/chat/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function postChatMessageWithAttachments(opts: {
  entity_type: string;
  entity_id: string;
  sender_name: string;
  sender_role?: string;
  message?: string;
  files: File[];
}): Promise<ChatMessage> {
  const fd = new FormData();
  fd.append("entity_type", opts.entity_type);
  fd.append("entity_id", opts.entity_id);
  fd.append("sender_name", opts.sender_name);
  if (opts.sender_role) fd.append("sender_role", opts.sender_role);
  fd.append("message", opts.message ?? "");
  for (const f of opts.files) fd.append("files", f, f.name);
  return request<ChatMessage>("/chat/messages/with-attachments", {
    method: "POST",
    body: fd,
  });
}

export function markExported(body: {
  order_ids?: number[];
  shipment_ids?: number[];
}): Promise<{ marked_orders: number; marked_shipments: number }> {
  return request("/orders/export/toggle/mark-exported", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// 수기주문 soft delete (토글 Export 화면 행 단위 삭제)
export function deleteManualOrder(orderId: number): Promise<{
  ok: boolean;
  order_id: number;
  order_no: string;
  is_cancelled: boolean;
  order_status: string;
}> {
  return request(`/orders/manual/${orderId}`, { method: "DELETE" });
}

// [ERP-TOGGLE-EXPORT-MANUAL-ORDER-RECREATE-FLOW-20260528-001]
// 토글 Export 화면 행 단위 "재작성" — 기존 주문 무효화 + prefill payload 반환.
export interface RecreateManualOrderPrefill {
  customer_id: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  address2: string | null;
  items: Array<{
    sku: string | null;
    product_name: string;
    option_name: string | null;
    quantity: number;
    unit_price: number;
  }>;
  memo_prefix: string;
  reference_order_no: string;
}

export interface PrepareRecreateManualOrderResponse {
  ok: boolean;
  old_order_no: string;
  old_order_id: number;
  prefill: RecreateManualOrderPrefill;
}

export function prepareRecreateManualOrder(
  orderNo: string,
  reason?: string | null,
): Promise<PrepareRecreateManualOrderResponse> {
  return request<PrepareRecreateManualOrderResponse>(
    `/orders/${encodeURIComponent(orderNo)}/prepare-recreate-manual-order`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? null }),
    },
  );
}

// ── 알림 이벤트 (방문 고객 호출) ─────────────────────────────

export interface NotificationEvent {
  id: number;
  event_type: string;
  title: string;
  message: string | null;
  source_location: string | null;
  is_acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  created_at: string;
}

export function createNotificationEvent(body: {
  event_type?: string;
  title: string;
  message?: string;
  source_location?: string;
}): Promise<NotificationEvent> {
  return request<NotificationEvent>("/notifications/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event_type: "customer_visit", ...body }),
  });
}

export function getActiveNotificationEvents(): Promise<NotificationEvent[]> {
  return request<NotificationEvent[]>("/notifications/events/active");
}

export function acknowledgeNotificationEvent(
  id: number,
  acknowledged_by?: string
): Promise<NotificationEvent> {
  return request<NotificationEvent>(`/notifications/events/${id}/acknowledge`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ acknowledged_by }),
  });
}

// ── 채팅 메시지 운영 확인 (Acknowledge) ──────────────────────

export interface ChatAcknowledgement {
  id: number;
  message_id: number;
  acknowledged_by: string;
  acknowledged_at: string;
  note: string | null;
}

export interface DashboardUnackedItem {
  entity_type: string;
  entity_id: string;
  thread_id: number;
  message_id: number;
  sender_name: string;
  sender_role: string | null;
  message: string;
  created_at: string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  detail_url: string;
}

export function getDashboardUnacked(limit = 20): Promise<DashboardUnackedItem[]> {
  return request<DashboardUnackedItem[]>(`/chat/dashboard-unacked?limit=${limit}`);
}

export function acknowledgeChatMessage(
  messageId: number,
  acknowledged_by: string,
  note?: string
): Promise<ChatAcknowledgement> {
  return request<ChatAcknowledgement>(`/chat/messages/${messageId}/acknowledge`, {
    method: "POST",
    body: JSON.stringify({ acknowledged_by, note }),
  });
}

export function getChatAcknowledgements(messageId: number): Promise<ChatAcknowledgement[]> {
  return request<ChatAcknowledgement[]>(`/chat/messages/${messageId}/acknowledgements`);
}


// ── 확인필요 주문건 (운영대시보드 카드, 26차 후속 #57 Phase 2) ──────────────

export interface AttentionItemRow {
  id: number;
  entity_type: string;
  entity_id: string;
  work_type: string | null;
  order_id: number | null;
  order_no: string | null;
  target_no: string | null;
  customer_name: string | null;
  summary: string | null;
  last_message_text: string | null;
  last_message_at: string | null;
  status: "active" | "completion_requested" | "completed" | "rejected" | string;
  created_by_user_id: number | null;
  created_by_name: string | null;
  requested_by_user_id: number | null;
  requested_by_name: string | null;
  approved_by_user_id: number | null;
  approved_by_name: string | null;
  rejected_by_user_id: number | null;
  rejected_by_name: string | null;
  requested_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  detail_url: string | null;
  product_summary: string | null;
}

export interface AttentionListResponse {
  active: AttentionItemRow[];
  completion_requested: AttentionItemRow[];
  recent_old: AttentionItemRow[];
  counts: { active: number; completion_requested: number; completed_total: number };
}

export interface AttentionDetailMessage {
  id: number;
  thread_id: number;
  sender_user_id: number | null;
  sender_name: string;
  sender_role: string | null;
  message: string;
  is_system: boolean;
  created_at: string | null;
}

export interface AttentionDetailResponse {
  item: AttentionItemRow;
  messages: AttentionDetailMessage[];
}

export function getAttentionOrders(): Promise<AttentionListResponse> {
  return request<AttentionListResponse>(`/work/attention-orders`);
}

export function getAttentionOrderDetail(id: number): Promise<AttentionDetailResponse> {
  return request<AttentionDetailResponse>(`/work/attention-orders/${id}`);
}

export function requestAttentionComplete(id: number): Promise<AttentionItemRow> {
  return request<AttentionItemRow>(`/work/attention-orders/${id}/request-complete`, {
    method: "POST",
  });
}

export function approveAttentionComplete(id: number): Promise<AttentionItemRow> {
  return request<AttentionItemRow>(`/work/attention-orders/${id}/approve-complete`, {
    method: "POST",
  });
}

export function rejectAttentionComplete(
  id: number,
  reason?: string,
): Promise<AttentionItemRow> {
  return request<AttentionItemRow>(`/work/attention-orders/${id}/reject-complete`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}


// ── 계좌입금 실행 큐 (승인요청과 분리) ─────────────────────────
//   pending : 입금확인 필요
//   confirmed: 완료 (출고 전 + 3일 이내만 큐에 유지)

export interface PendingDepositItem {
  // 26차 후속 — Phase 1: "return_exchange" 추가 (반품/교환 입금)
  kind: "order" | "as" | "return_exchange";
  // 26차 후속 #25 / Phase 1 — 통합 입금확인 큐 출처 구분
  // additional_charge | exchange_diff | round_trip_shipping |
  // return_shipping | reshipping_fee | other 가 새로 추가됨.
  source_type:
    | "manual_order"
    | "offline_store"
    | "as_repair"
    | "additional_charge"
    | "exchange_diff"
    | "round_trip_shipping"
    | "return_shipping"
    | "reshipping_fee"
    | "other"
    | string;
  source_label: string;
  state: "pending" | "confirmed";
  order_no: string | null;
  as_id: number | null;
  as_no: string | null;
  /** 26차 후속 — Phase 1: ReturnReceipt 식별자 */
  return_receipt_id?: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  amount: number | null;
  ref_date: string | null;
  confirmed_at: string | null;
  memo: string | null;
  detail_url: string | null;
}

export interface PendingDepositsResponse {
  pending_total: number;
  confirmed_total: number;
  pending_amount_total: number;
  items: PendingDepositItem[];
  total: number;
}

export function getPendingDeposits(limit = 50): Promise<PendingDepositsResponse> {
  return request<PendingDepositsResponse>(`/dashboard/pending-deposits?limit=${limit}`);
}

// 관리자 전용 — 비-관리자가 호출하면 403. requested_by/is_admin 파라미터 제거 (JWT 기반).
export function confirmDeposit(
  order_no: string,
): Promise<{ result: string; order_no: string; order_status?: string; deposit_confirmed_at?: string }> {
  return request(`/orders/${encodeURIComponent(order_no)}/confirm-deposit`, { method: "POST" });
}

export function confirmAsDeposit(
  as_id: number,
): Promise<{ result: string; as_id: number; as_no: string; deposit_confirmed_at: string }> {
  return request(`/as/requests/${as_id}/confirm-deposit`, { method: "POST" });
}

// 26차 후속 #42 — AS 비용 내역 초기화 (repaired/payment_confirmed → recovered)
export function resetAsCost(asId: number): Promise<AsRequest> {
  return request<AsRequest>(`/as/requests/${asId}/reset-cost`, { method: "POST" });
}

// ─────────────────────────────────────────────
// 상품 대량등록
// ─────────────────────────────────────────────

export function downloadBulkTemplate(): void {
  window.open(`${API_URL}/products/bulk-import/template`, "_blank");
}

// ─────────────────────────────────────────────
// 포장 출고 (POS)
// ─────────────────────────────────────────────

export interface PackingOrderItem {
  id: number;
  // [ERP-PACKING-MULTI-ORDER-SAME-INVOICE-FIX-20260528-001]
  // 합포장 (multi_order) 시 각 item 의 실제 order_no — frontend 가 reduce-inventory /
  // mark-packed 호출 시 currentOrder.order_no 대신 이 값을 사용해야 함.
  order_no?: string;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
  unit_price: number | null;
  line_amount?: number | null;
  // [ERP-DAIWA-BARCODE-SCAN-20260519-001] products.barcode 동봉.
  // null/빈문자열이면 미등록 상태 — 포장 스캔 흐름에서 등록 모달 트리거 후보.
  barcode?: string | null;
  // [ERP-PACKING-OUTBOUND-IPAD-ORDER-INFO-UX-AND-COMPLETE-SCAN-FIX-DEV-20260626-001]
  // 주문내역 카드 표시용 — barcode_product_name 은 실제 DB Product.barcode_label_name 값.
  // last_inbound_at: SKU별 최근 입고일 ISO (없으면 null → 배지 숨김).
  barcode_product_name?: string | null;
  last_inbound_at?: string | null;
  // [ERP-ORDER-INVOICE-SPLIT-ITEM-MOVE-REDESIGN-20260529-001]
  // moved_out 항목은 분할자식으로 이동된 이력 → 원주문 송장 스캔/재고차감 제외.
  split_status?: string;            // none | moved_out
  split_child_order_no?: string | null;
  scan_required?: boolean;          // false 면 포장 스캔 대상 아님 (moved_out / 재고미관리 부품)
  stock_deduction_required?: boolean;
  // [ERP-PICKING-NON-STOCK-PART-SCAN-BYPASS-HOTFIX-20260619-001]
  is_non_stock_part?: boolean;      // true 면 재고미관리 부품 — 스캔/차감 불필요, 완료 차단 없음
  display_status?: string | null;   // "송장쪼개기됨" 등 표시 라벨
  // [ERP-INVOICE-SPLIT-ORIGINAL-ORDER-MOVED-OUT-VISIBILITY-20260601-001]
  // moved_out 항목의 분할자식 송장/Export 상태 (원송장 포장 화면 표시용).
  split_child_invoice_no?: string | null;
  split_child_export_status?: string | null; // "별도송장 {inv}" | "Export 완료 · 송장 대기" | "Export 대기"
}

// [ERP-PACKING-MULTI-ORDER-SAME-INVOICE-FIX-20260528-001]
// 합포장 (같은 invoice_no 공유 N주문) 정보 — find-by-invoice 응답에 동봉.
export interface PackingOrderSibling {
  order_no: string;
  order_id?: number;
  receiver_name?: string | null;
  order_status?: string | null;
  packing_request_note?: string | null;
  has_packing_request?: boolean;
  packing_request_updated_at?: string | null;
  packing_request_updated_by_name?: string | null;
  packing_request_completed_at?: string | null;
  packing_request_completed_by_name?: string | null;
}

export interface PackingOrder {
  order_no: string;
  invoice_no: string | null;
  order_status: string | null;
  order_type: string;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  address2: string | null;
  paid_amount: number | null;
  shipping_fee: number | null;
  platform: string | null;
  platform_store_name: string | null;
  is_exchange: boolean;
  memo: string | null;
  is_daiwa_direct_ship: boolean;
  daiwa_fulfillment_choice?: string | null;
  // 26차 후속 #83 Phase 2 — ShipmentPick 기반 포장 완료 분리 (optional/additive).
  packing_status?: "pending" | "completed" | null;
  packing_completed_at?: string | null;
  // [ERP-PACKING-REQUEST-NOTE-COLUMNS-AND-HIGHLIGHT-20260527-001]
  packing_request_note?: string | null;
  has_packing_request?: boolean;
  packing_request_updated_at?: string | null;
  packing_request_updated_by_name?: string | null;
  // [ERP-PACKING-REQUEST-TOP-BANNER-COMPLETE-AND-MANUAL-FINISH-20260528-001]
  packing_request_completed_at?: string | null;
  packing_request_completed_by_name?: string | null;
  // [ERP-PACKING-MULTI-ORDER-SAME-INVOICE-FIX-20260528-001]
  // 같은 invoice_no 를 공유하는 합포장 주문이 N건이면 multi_order=true + orders[N] 동봉.
  multi_order?: boolean;
  orders?: PackingOrderSibling[];
  // [ERP-ORDER-COMBINE-SHIPPING-PICKING-INTEGRATION-20260602-001] 합배송 그룹 연동(Phase 2).
  combined_shipping?: CombinedShippingPackingInfo | null;
  items: PackingOrderItem[];
}

export interface CombinedShippingPackingMember {
  order_no: string;
  role: string;
  payment_required: boolean;
  payment_confirmed: boolean;
  receiver_match_status: string;
  include_in_packing: boolean;
}
export interface CombinedShippingPackingInfo {
  is_combined: boolean;
  group_no: string;
  status: string;
  representative_order_no: string;
  representative_invoice_no: string | null;
  shipping_fee_policy: string;
  warnings: string[];
  members: CombinedShippingPackingMember[];
}

export function getPackingTodayOrders(params?: { review_date?: string }): Promise<{
  date: string;
  total: number;
  orders: PackingOrder[];
}> {
  const qs = new URLSearchParams();
  if (params?.review_date) qs.set("review_date", params.review_date);
  return request(`/orders/packing/today${qs.toString() ? "?" + qs : ""}`);
}

export function findPackingOrder(q: string): Promise<PackingOrder> {
  return request(`/orders/packing/find?q=${encodeURIComponent(q)}`);
}

/** 송장번호로 주문 조회 */
export function findPackingOrderByInvoice(invoiceNo: string): Promise<PackingOrder> {
  return request(`/orders/packing/find-by-invoice?invoice_no=${encodeURIComponent(invoiceNo)}`);
}

export interface PackingSkuLocationInfo {
  location_code: string;
  quantity: number;
}

export function getPackingSkuLocations(skus: string[]): Promise<Record<string, PackingSkuLocationInfo[]>> {
  if (skus.length === 0) return Promise.resolve({});
  return request(`/orders/packing/sku-locations?skus=${skus.join(",")}`);
}

// 26차 후속 #82 회귀 보강 — 전량 스캔 완료 시 서버에 포장완료를 마킹한다.
// idempotent 하므로 같은 주문에 중복 호출되어도 안전.
export function markPackedForOrder(
  orderNo: string,
  actor?: string,
  invoiceNo?: string,
): Promise<{
  ok: boolean;
  order_no: string;
  packing_status: "completed";
  packing_completed_at: string | null;
}> {
  const qs = new URLSearchParams();
  if (actor) qs.set("actor", actor);
  if (invoiceNo) qs.set("invoice_no", invoiceNo);
  const suffix = qs.toString() ? `?${qs}` : "";
  return request(`/orders/${encodeURIComponent(orderNo)}/mark-packed${suffix}`, {
    method: "POST",
  });
}

export function reduceInventoryForPacking(params: {
  order_no: string;
  sku: string;
  location_code: string;
  quantity?: number;
  actor?: string;
}): Promise<{ ok: boolean; sku: string; location_code: string; quantity_deducted: number; remaining_qty: number; skipped_inventory?: boolean; skipped_reason?: string | null; skipped_sku?: string | null }> {
  const qs = new URLSearchParams();
  qs.set("sku", params.sku);
  qs.set("location_code", params.location_code);
  if (params.quantity) qs.set("quantity", String(params.quantity));
  if (params.actor) qs.set("actor", params.actor);
  return request(`/orders/${encodeURIComponent(params.order_no)}/reduce-inventory?${qs}`, {
    method: "POST",
  });
}

// [ERP-PICKING-SET-PARENT-SKU-SCAN-NOT-FOUND-HOTFIX-20260619-001]
// 세트상품 완료 차감 — 대표 SKU 가 아니라 구성품 SKU 를 SELLABLE 위치 자동선택·합산 차감.
// 구성품 스캔이 모두 완료(required 충족)된 후에만 호출.
export function reduceSetComponentsForPacking(params: {
  order_no: string;
  bundle_sku: string;
  quantity?: number;
  actor?: string;
}): Promise<{
  ok: boolean;
  bundle_sku: string;
  quantity: number;
  is_set: boolean;
  components: number;
  deductions: { sku: string; location: string; quantity: number; before: number; after: number }[];
}> {
  const qs = new URLSearchParams();
  qs.set("bundle_sku", params.bundle_sku);
  if (params.quantity) qs.set("quantity", String(params.quantity));
  if (params.actor) qs.set("actor", params.actor);
  return request(`/orders/${encodeURIComponent(params.order_no)}/reduce-inventory-set?${qs}`, {
    method: "POST",
  });
}

// [ERP-PACKING-BARCODE-REGISTRATION-POPUP-BLOCK-20260527-001]
// 포장출고 스캔 fallback — 스캔값이 ERP 상품DB 에 등록되어 있는지 판정.
// exact match (barcode OR sku). DB 무변경 read-only.
export interface ProductBarcodeLookupResponse {
  found: boolean;
  sku: string | null;
  product_name: string | null;
  barcode: string | null;
  // [ERP-STORE-BARCODE-SCAN-AUTO-LISTUP-DEV-20260626-001] /store 자동 리스트업 보강(옵셔널, 하위호환).
  match_count?: number;          // exact 매칭 건수 (0/1/2+)
  conflict?: boolean;            // 2건 이상이면 true → 자동추가 차단
  product_id?: number;
  barcode_product_name?: string | null; // = Product.barcode_label_name
  option1?: string | null;
  option2?: string | null;
  selling_price?: number | null; // smartstore 기준 판매가
  is_active?: boolean;
}

export function lookupProductByBarcode(code: string): Promise<ProductBarcodeLookupResponse> {
  const qs = new URLSearchParams({ code });
  return request<ProductBarcodeLookupResponse>(`/products/lookup-barcode?${qs}`, {
    method: "GET",
  });
}

// [ERP-PRODUCT-BULK-INVENTORY-ADJUSTMENT-20260527-001]
// 선택 상품 일괄 재고 조정 (set_absolute) — Preview + Apply 두 단계.
export interface BulkInventoryAdjustItem {
  sku: string;
  product_name: string | null;
  location_code: string;
  current_quantity: number;
  target_quantity: number;
  delta: number;
  action: "update" | "no_change" | "blocked";
  warnings: string[];
}

export interface BulkInventoryAdjustSummary {
  total: number;
  will_update: number;
  no_change: number;
  blocked: number;
}

export interface BulkInventoryAdjustPreviewResponse {
  items: BulkInventoryAdjustItem[];
  summary: BulkInventoryAdjustSummary;
}

export interface BulkInventoryAdjustApplyResponse {
  items: BulkInventoryAdjustItem[];
  summary: BulkInventoryAdjustSummary;
  applied_count: number;
}

export function previewBulkInventoryAdjustment(body: {
  skus: string[];
  location_code: string;
  target_quantity: number;
  reason: string;
}): Promise<BulkInventoryAdjustPreviewResponse> {
  return request<BulkInventoryAdjustPreviewResponse>("/products/inventory-adjustments/preview", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function applyBulkInventoryAdjustment(body: {
  skus: string[];
  location_code: string;
  target_quantity: number;
  reason: string;
}): Promise<BulkInventoryAdjustApplyResponse> {
  return request<BulkInventoryAdjustApplyResponse>("/products/inventory-adjustments/apply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function previewBulkImport(file: File): Promise<{
  count: number;
  headers: string[];
  rows: Record<string, string>[];
}> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_URL}/products/bulk-import/preview`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function confirmBulkImport(rows: Record<string, string>[]): Promise<{
  created: number;
  skus: string[];
  errors: { row: number; sku?: string; error: string }[];
}> {
  return request("/products/bulk-import/confirm", {
    method: "POST",
    body: JSON.stringify(rows),
  });
}

// ─────────────────────────────────────────────
// 번들 구성
// ─────────────────────────────────────────────

export interface BundleComponent {
  id: number;
  bundle_sku: string;
  component_sku: string;
  component_name: string;
  quantity: number;
  component_type: "set_part" | "free_gift";
  memo?: string | null;
  // 26차 후속 #39 — 구성품 상세 (백엔드 응답 확장)
  image_url?: string | null;
  option1?: string | null;
  option2?: string | null;
  option3?: string | null;
  product_status?: string | null;
  sale_price?: number | null;
  stock_total?: number | null;
}

export function getBundleComponents(sku: string): Promise<BundleComponent[]> {
  return request<BundleComponent[]>(`/products/${sku}/bundle-components`);
}

export interface UsedInBundle {
  bundle_sku: string;
  bundle_name: string;
  quantity: number;
  component_type: "set_part" | "free_gift";
  memo?: string | null;
}

export function getUsedInBundles(sku: string): Promise<UsedInBundle[]> {
  return request<UsedInBundle[]>(`/products/${sku}/used-in-bundles`);
}

export function upsertBundleComponent(
  sku: string,
  body: { component_sku: string; quantity: number; component_type: string; memo?: string }
): Promise<{ ok: boolean }> {
  return request(`/products/${sku}/bundle-components`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteBundleComponent(
  sku: string,
  component_sku: string
): Promise<{ ok: boolean }> {
  return request(`/products/${sku}/bundle-components/${component_sku}`, {
    method: "DELETE",
  });
}

// 현재 로그인 유저 정보
export async function getCurrentUser() {
  return request<{
    id: number;
    name: string;
    role: string;
    permission_level?: string;
    company_id?: number | null;
    company_code?: string | null;
    company_name?: string | null;
  }>("/auth/me");
}

// [ERP-PO-SHARED-MUST-ADD-MEMO-CHECKLIST-AUDIT-20260616-001]
// role/permission_level → 유효 권한등급(lv2 이상 여부). backend effective_level 미러.
const _ROLE_DEFAULT_LEVEL: Record<string, string> = {
  admin: "lv3",
  inventory: "lv2",
  shipping: "lv2",
  as: "lv2",
  repair: "lv2",
  sales: "lv2",
  marketing: "lv1",
  store: "lv1",
  viewer: "lv1",
};
const _LEVEL_ORDER: Record<string, number> = { lv1: 1, lv2: 2, lv3: 3 };

export function userHasLevel(
  user: { role?: string; permission_level?: string } | null | undefined,
  minLevel: "lv1" | "lv2" | "lv3",
): boolean {
  if (!user) return false;
  if (user.role === "admin") return true;
  const lv =
    user.permission_level && _LEVEL_ORDER[user.permission_level]
      ? user.permission_level
      : _ROLE_DEFAULT_LEVEL[user.role || ""] || "lv1";
  return (_LEVEL_ORDER[lv] || 1) >= (_LEVEL_ORDER[minLevel] || 1);
}

// [ERP-PRODUCT-LIST-SEARCH-STATE-PRESERVE-AFTER-EDIT-20260619-001]
// 상품 목록 복귀 URL(returnTo/from) 보안 검증 — 내부 /products 목록 경로만 허용.
// 허용: "/products", "/products?..." (검색/필터/페이지 query 포함)
// 차단: 외부 URL(http/https), protocol-relative(//), javascript: 등 scheme, 다른 내부 경로(/admin 등)
export function safeProductsReturnUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let v: string;
  try {
    v = decodeURIComponent(raw);
  } catch {
    return null;
  }
  v = v.trim();
  if (!v) return null;
  if (v.startsWith("//")) return null;        // protocol-relative
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(v)) return null; // http:, https:, javascript:, data: 등 scheme
  if (v === "/products" || v.startsWith("/products?")) return v;
  return null;
}

export interface PoInboundItem {
  id: number;
  po_id: number;
  po_no: string;
  sku: string;
  product_name: string;
  // 26차 후속 #8(2026-04-24) — 한글 옵션(중문 아님) + SKU 입고완료 체크 상태
  option1?: string | null;
  option2?: string | null;
  // [Phase1 2026-06-15] 입고검수장 브랜드 정렬 + 썸네일 표시용 (Product 조인, 표시 전용)
  brand?: string | null;
  image_url?: string | null;
  quantity_ordered: number;
  quantity_actual: number;
  quantity_moved: number;
  quantity_remaining: number;
  inbound_checked_at: string | null;
  inbound_checked_by_user_id: number | null;
  inbound_checked_by_user_name: string | null;
  // 26차 후속 #9(2026-04-24) — 스태프가 지정한 이동 목적지(계획값).
  // 실제 재고 이동은 관리자 approve-stocked-in 시점에 커밋.
  planned_target_location_code: string | null;
  // 26차 후속 #10(2026-04-24) — 다중 목적지 분배 계획(allocations). 잔량을 쪼개서 보낼 때 사용.
  allocations: PoInboundAllocation[];
  allocated_qty: number;
}

export interface PoInboundAllocation {
  id: number;
  target_location_code: string;
  planned_qty: number;
}

export function getPoInboundItems(poId?: number): Promise<{ items: PoInboundItem[] }> {
  const qs = poId ? `?po_id=${poId}` : "";
  return request(`/purchase/inbound-items${qs}`);
}

export function adjustInboundQuantity(itemId: number, quantityActual: number): Promise<{ ok: boolean }> {
  return request(`/purchase/inbound-items/${itemId}/adjust`, {
    method: "PATCH",
    body: JSON.stringify({ quantity_actual: quantityActual }),
  });
}

export function moveInboundItem(
  itemId: number,
  body: { to_location_code: string; quantity: number; moved_by?: string }
): Promise<{ ok: boolean; all_moved: boolean }> {
  return request(`/purchase/inbound-items/${itemId}/move`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// 26차 후속 #8(2026-04-24) — SKU별 입고완료 체크 + 관리자 샵입고완료 승인.
export function checkInboundItem(itemId: number): Promise<{
  ok: boolean;
  id: number;
  inbound_checked_at: string | null;
  inbound_checked_by_user_id: number | null;
  inbound_checked_by_user_name: string | null;
}> {
  return request(`/purchase/inbound-items/${itemId}/check`, { method: "POST" });
}

export function uncheckInboundItem(itemId: number): Promise<{ ok: boolean; id: number }> {
  return request(`/purchase/inbound-items/${itemId}/check`, { method: "DELETE" });
}

// 26차 후속 #9(2026-04-24) — SKU 이동 목적지(계획) 저장. 실제 이동은 발생하지 않음.
// 26차 후속 #10(2026-04-24) — 레거시 backcompat 용. 신규 입력은 allocations API 사용.
export function planInboundTarget(
  itemId: number,
  toLocationCode: string | null
): Promise<{ ok: boolean; id: number; planned_target_location_code: string | null }> {
  return request(`/purchase/inbound-items/${itemId}/plan-target`, {
    method: "POST",
    body: JSON.stringify({ to_location_code: toLocationCode }),
  });
}

// 26차 후속 #10(2026-04-24) — SKU 분배 계획(다중 목적지) CRUD.
// allocations 합계 == 입고장 잔량 일 때만 입고완료 체크 / 승인 가능.
export function listInboundAllocations(itemId: number): Promise<{
  items: PoInboundAllocation[];
  remaining: number;
  allocated_qty: number;
}> {
  return request(`/purchase/inbound-items/${itemId}/allocations`);
}

export function createInboundAllocation(
  itemId: number,
  body: { target_location_code: string; planned_qty: number }
): Promise<{
  ok: boolean;
  id: number;
  target_location_code: string;
  planned_qty: number;
  allocated_qty: number;
  remaining: number;
}> {
  return request(`/purchase/inbound-items/${itemId}/allocations`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateInboundAllocation(
  allocId: number,
  body: { target_location_code?: string; planned_qty?: number }
): Promise<{
  ok: boolean;
  id: number;
  target_location_code: string;
  planned_qty: number;
}> {
  return request(`/purchase/inbound-item-allocations/${allocId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteInboundAllocation(allocId: number): Promise<{ ok: boolean }> {
  return request(`/purchase/inbound-item-allocations/${allocId}`, {
    method: "DELETE",
  });
}

export function approveStockedIn(poId: number): Promise<PurchaseOrder> {
  return request(`/purchase/orders/${poId}/approve-stocked-in`, { method: "POST" });
}

// ── 26차 후속 #47: SKU 바코드 라벨 PDF ────────────────────────────────────
// POST /barcode-labels/pdf → application/pdf Blob → 새 탭으로 오픈.
// 단건/다건 공용 (현재는 상품 상세 단건만 사용).
export type BarcodeLabelSize = "small" | "large";
// [ERP-BARCODE-20260513-013] 코드 형식 선택 (QR / Code128).
// 미지정 시 backend 가 label_size 기준 기본값 적용 (small→qr, large→code128).
export type BarcodeCodeType = "qr" | "code128";
export type BarcodeLabelItemRequest = { sku: string; quantity: number };

export async function openBarcodeLabelsPdf(body: {
  label_size: BarcodeLabelSize;
  code_type?: BarcodeCodeType;
  items: BarcodeLabelItemRequest[];
}): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_URL}/barcode-labels/pdf`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "바코드 PDF 생성 실패" }));
    throw new Error(err.detail || "바코드 PDF 생성 실패");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  // 새 탭으로 열기 — Mac 인쇄 대화상자 흐름. 일부 브라우저 팝업 차단 시 a click fallback.
  const win = window.open(url, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  }
  // 새 탭에서 PDF 가 로드된 뒤 revoke — 60s 후 안전 해제.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ── [ERP-PO-BARCODE-PRINT-JOBS-PHASE-B-...-20260616-001] PO 바코드 출력 이력 ──
// PDF export 1건 = job 1건(style 그룹 1개). "출력됨" 표시는 printed_keys 기준.
export interface PoBarcodePrintJobItem {
  id: number;
  job_id: number;
  purchase_order_id: number;
  purchase_order_item_id: number | null;
  sku: string;
  product_name: string | null;
  quantity: number;
  label_size: BarcodeLabelSize;
  code_type: BarcodeCodeType;
  created_at: string;
}

export interface PoBarcodePrintJob {
  id: number;
  purchase_order_id: number;
  label_size: BarcodeLabelSize;
  code_type: BarcodeCodeType;
  total_sku_count: number;
  total_label_count: number;
  printed_by_user_id: number | null;
  printed_at: string;
  created_at: string;
  items: PoBarcodePrintJobItem[];
}

export interface PoBarcodePrintedKey {
  sku: string;
  label_size: BarcodeLabelSize;
  code_type: BarcodeCodeType;
  total_quantity: number;
  job_count: number;
  last_printed_at: string;
}

export interface PoBarcodePrintHistory {
  purchase_order_id: number;
  jobs: PoBarcodePrintJob[];
  printed_keys: PoBarcodePrintedKey[];
}

export function getPoBarcodePrintJobs(poId: number): Promise<PoBarcodePrintHistory> {
  return request<PoBarcodePrintHistory>(`/purchase/orders/${poId}/barcode-print-jobs`);
}

// style 그룹 1개(label_size+code_type) 의 SKU 들을 한 PDF 로 export.
// 성공 시 PDF 를 새 탭으로 열고, job/items 이력이 저장됨.
export async function exportPoBarcodePrintJob(
  poId: number,
  body: {
    label_size: BarcodeLabelSize;
    code_type: BarcodeCodeType;
    items: { sku: string; quantity: number }[];
    include_reprint?: boolean;
  },
): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_URL}/purchase/orders/${poId}/barcode-print-jobs/export`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "바코드 PDF 생성 실패" }));
    throw new Error(err.detail || "바코드 PDF 생성 실패");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ── 개인 메모장 (26차 후속 #58 Phase 4, 2026-05-06) ─────────────────
export interface LinkedOrderRef {
  order_id: number;
  order_no: string;
  customer_name: string | null;
}

export interface PersonalNoteRow {
  id: number;
  user_id: number;
  title: string;
  content: string;
  note_date: string;        // YYYY-MM-DD
  is_pinned: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  linked_orders: LinkedOrderRef[];
  unmatched_order_refs: string[];
}

export interface PersonalNoteListResponse {
  items: PersonalNoteRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface PersonalNoteCreate {
  title?: string | null;
  content?: string | null;
  note_date?: string | null;   // YYYY-MM-DD
  is_pinned?: boolean;
}

export interface PersonalNoteUpdate {
  title?: string | null;
  content?: string | null;
  note_date?: string | null;
  is_pinned?: boolean | null;
  is_archived?: boolean | null;
}

export interface PersonalNoteListParams {
  q?: string;
  date_from?: string;
  date_to?: string;
  is_archived?: boolean;
  limit?: number;
  offset?: number;
}

export function getPersonalNotes(params: PersonalNoteListParams = {}): Promise<PersonalNoteListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (typeof params.is_archived === "boolean") qs.set("is_archived", String(params.is_archived));
  if (typeof params.limit === "number") qs.set("limit", String(params.limit));
  if (typeof params.offset === "number") qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<PersonalNoteListResponse>(`/personal-notes${suffix}`);
}

export function createPersonalNote(payload: PersonalNoteCreate): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>("/personal-notes", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getPersonalNote(id: number): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>(`/personal-notes/${id}`);
}

export function updatePersonalNote(id: number, payload: PersonalNoteUpdate): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>(`/personal-notes/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** 영구 삭제 — 휴지통(is_archived=true) 안의 메모만 삭제 가능. 휴지통이 아니면 백엔드 400. */
export function permanentDeletePersonalNote(
  id: number,
): Promise<{ ok: boolean; id: number }> {
  return request<{ ok: boolean; id: number }>(
    `/personal-notes/${id}/permanent`,
    { method: "DELETE" },
  );
}

export function deletePersonalNote(id: number): Promise<PersonalNoteRow> {
  // 백엔드는 hard delete 가 아니라 soft delete (is_archived=true) 후 row 반환.
  return request<PersonalNoteRow>(`/personal-notes/${id}`, { method: "DELETE" });
}

export function togglePersonalNotePin(id: number): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>(`/personal-notes/${id}/toggle-pin`, { method: "POST" });
}

export function restorePersonalNote(id: number): Promise<PersonalNoteRow> {
  return request<PersonalNoteRow>(`/personal-notes/${id}/restore`, { method: "POST" });
}

// ── ERP 개선요청 (26차 후속 #59, 2026-05-06) ─────────────────────

export type ErpRequestStatus =
  | "open"
  | "reviewing"
  | "planned"
  | "in_progress"
  | "completed"
  | "rejected";
export type ErpRequestCategory =
  | "bug"
  | "ux"
  | "feature"
  | "data"
  | "speed"
  | "other";
export type ErpRequestPriority = "low" | "normal" | "high" | "urgent";

export interface ErpImprovementRow {
  id: number;
  title: string;
  content: string;
  category: ErpRequestCategory | string;
  status: ErpRequestStatus | string;
  priority: ErpRequestPriority | string;
  page_url: string | null;
  reporter_user_id: number;
  reporter_name: string | null;
  assignee_user_id: number | null;
  assignee_name: string | null;
  admin_comment: string | null;
  completed_by_user_id: number | null;
  completed_by_name: string | null;
  completed_at: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  deleted_by_user_id: number | null;
  deleted_by_name: string | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface ErpImprovementListResponse {
  items: ErpImprovementRow[];
  total: number;
  limit: number;
  offset: number;
  counts_by_status: Array<{ status: string; count: number }>;
}

export interface ErpImprovementCreate {
  title?: string;
  content?: string;
  category?: ErpRequestCategory | string;
  priority?: ErpRequestPriority | string;
  page_url?: string | null;
}

export interface ErpImprovementUpdate {
  title?: string;
  content?: string;
  category?: ErpRequestCategory | string;
  priority?: ErpRequestPriority | string;
  page_url?: string | null;
}

export interface ErpImprovementAdminUpdate {
  status?: ErpRequestStatus | string;
  priority?: ErpRequestPriority | string;
  admin_comment?: string | null;
  assignee_user_id?: number | null;
}

export interface ErpImprovementListParams {
  q?: string;
  status?: string;
  category?: string;
  priority?: string;
  reporter_user_id?: number;
  mine?: boolean;
  /** 기본 false = 일반 목록(휴지통 제외). true = 휴지통 보기. */
  is_deleted?: boolean;
  limit?: number;
  offset?: number;
}

export function getErpImprovements(
  params: ErpImprovementListParams = {},
): Promise<ErpImprovementListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.category) qs.set("category", params.category);
  if (params.priority) qs.set("priority", params.priority);
  if (params.reporter_user_id != null)
    qs.set("reporter_user_id", String(params.reporter_user_id));
  if (params.mine) qs.set("mine", "true");
  if (params.is_deleted) qs.set("is_deleted", "true");
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<ErpImprovementListResponse>(`/erp-improvements${suffix}`);
}

export function getErpImprovement(id: number): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}`);
}

export function createErpImprovement(
  payload: ErpImprovementCreate,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateErpImprovement(
  id: number,
  payload: ErpImprovementUpdate,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function adminUpdateErpImprovement(
  id: number,
  payload: ErpImprovementAdminUpdate,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}/admin-update`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function completeErpImprovement(
  id: number,
  admin_comment?: string,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}/complete`, {
    method: "POST",
    body: JSON.stringify({ admin_comment: admin_comment ?? null }),
  });
}

export function rejectErpImprovement(
  id: number,
  admin_comment?: string,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ admin_comment: admin_comment ?? null }),
  });
}

/** 휴지통 이동 (soft delete). admin 전체, 직원은 본인 + open/reviewing 한정. */
export function softDeleteErpImprovement(
  id: number,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}`, {
    method: "DELETE",
  });
}

/** 휴지통 복원. admin 전체, 직원은 본인 한정. */
export function restoreErpImprovement(
  id: number,
): Promise<ErpImprovementRow> {
  return request<ErpImprovementRow>(`/erp-improvements/${id}/restore`, {
    method: "POST",
  });
}

// ── [ERP-PO-SHARED-MUST-ADD-MEMO-CHECKLIST-AUDIT-20260616-001] ────────
// 발주 공유 체크리스트 — "다음 발주에 꼭 추가해야 할 것".
// 발주건과 무관하게 유지되는 공유 메모/체크리스트.
// 읽기: 로그인 전체 / 작성·수정·완료·삭제: lv2 이상 (백엔드 게이트).
export type PoChecklistScope =
  | "china_import"
  | "shimano"
  | "daiwa"
  | "global";
export type PoChecklistStatus = "active" | "done";

export interface PoSharedChecklistRow {
  id: number;
  scope: PoChecklistScope | string;
  content: string;
  sku: string | null;
  product_name: string | null;
  status: PoChecklistStatus | string;
  sort_order: number;
  created_by_user_id: number;
  created_by_name: string | null;
  updated_by_user_id: number | null;
  updated_by_name: string | null;
  completed_by_user_id: number | null;
  completed_by_name: string | null;
  completed_at: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  deleted_by_user_id: number | null;
  deleted_by_name: string | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
}

export interface PoSharedChecklistListResponse {
  items: PoSharedChecklistRow[];
  total: number;
  limit: number;
  offset: number;
  counts_by_status: Array<{ status: string; count: number }>;
}

export interface PoSharedChecklistCreate {
  scope?: PoChecklistScope | string;
  content: string;
  sku?: string | null;
  product_name?: string | null;
}

export interface PoSharedChecklistUpdate {
  content?: string;
  sku?: string | null;
  product_name?: string | null;
  sort_order?: number;
}

export interface PoSharedChecklistListParams {
  scope?: PoChecklistScope | string;
  q?: string;
  status?: PoChecklistStatus | string;
  /** 기본 false = 일반 목록(휴지통 제외). true = 휴지통 보기. */
  is_deleted?: boolean;
  limit?: number;
  offset?: number;
}

export function getPoSharedChecklist(
  params: PoSharedChecklistListParams = {},
): Promise<PoSharedChecklistListResponse> {
  const qs = new URLSearchParams();
  if (params.scope) qs.set("scope", params.scope);
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.is_deleted) qs.set("is_deleted", "true");
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<PoSharedChecklistListResponse>(`/po-shared-checklist${suffix}`);
}

export function createPoSharedChecklist(
  payload: PoSharedChecklistCreate,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updatePoSharedChecklist(
  id: number,
  payload: PoSharedChecklistUpdate,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** 완료/완료해제 토글. done=true 면 완료(done), false 면 미완료(active) 복귀. */
export function toggleCompletePoSharedChecklist(
  id: number,
  done: boolean,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist/${id}/complete`, {
    method: "POST",
    body: JSON.stringify({ done }),
  });
}

/** 휴지통 이동 (soft delete). lv2 이상. */
export function softDeletePoSharedChecklist(
  id: number,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist/${id}`, {
    method: "DELETE",
  });
}

/** 휴지통 복원. lv2 이상. */
export function restorePoSharedChecklist(
  id: number,
): Promise<PoSharedChecklistRow> {
  return request<PoSharedChecklistRow>(`/po-shared-checklist/${id}/restore`, {
    method: "POST",
  });
}

// ── [ERP-INBOUND-20260513-002] 모바일 입고검수 세션 ─────────────────
// 본 API 는 재고 SoT 가 아니다. reviewed 도 단순 관리자 확인 표식.
export type MobileInboundSessionStatus =
  | "checking"
  | "submitted"
  | "cancelled"
  | "reviewed"
  | "rejected";
export type MobileInboundIssueType =
  | "normal"
  | "shortage"
  | "overage"
  | "damaged"
  | "wrong_item"
  | "hold";

export interface MobileInboundCheckItem {
  id: number;
  po_item_id: number | null;
  sku: string | null;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  expected_qty: number;
  checked_qty: number;
  issue_type: MobileInboundIssueType;
  memo: string | null;
  checked_at: string | null;
}

export interface MobileInboundCheckSessionSummary {
  id: number;
  purchase_order_id: number;
  po_no: string;
  status: MobileInboundSessionStatus;
  checked_by_user_id: number;
  checked_by_name: string | null;
  memo: string | null;
  started_at: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  reviewed_by_user_id: number | null;
  rejected_at: string | null;
  rejected_reason: string | null;
  total_items: number;
  checked_items: number;
  issue_items: number;
}

export interface MobileInboundCheckSessionDetail extends MobileInboundCheckSessionSummary {
  items: MobileInboundCheckItem[];
}

export interface MobileInboundCheckListResponse {
  total: number;
  items: MobileInboundCheckSessionSummary[];
}

export function listMobileInboundChecks(params?: {
  status?: MobileInboundSessionStatus;
  po_id?: number;
  limit?: number;
  offset?: number;
}): Promise<MobileInboundCheckListResponse> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.po_id != null) qs.set("po_id", String(params.po_id));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs}` : "";
  return request<MobileInboundCheckListResponse>(`/mobile/inbound-checks${suffix}`);
}

export function getMobileInboundCheck(id: number): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(`/mobile/inbound-checks/${id}`);
}

export function createMobileInboundCheck(
  purchase_order_id: number,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(`/mobile/inbound-checks`, {
    method: "POST",
    body: JSON.stringify({ purchase_order_id }),
  });
}

export function updateMobileInboundCheckItem(
  sessionId: number,
  itemId: number,
  body: { checked_qty?: number; issue_type?: MobileInboundIssueType; memo?: string | null },
): Promise<MobileInboundCheckItem> {
  return request<MobileInboundCheckItem>(
    `/mobile/inbound-checks/${sessionId}/items/${itemId}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export function updateMobileInboundCheckMemo(
  sessionId: number,
  memo: string | null,
): Promise<MobileInboundCheckSessionSummary> {
  return request<MobileInboundCheckSessionSummary>(
    `/mobile/inbound-checks/${sessionId}/memo`,
    { method: "PATCH", body: JSON.stringify({ memo }) },
  );
}

export function submitMobileInboundCheck(
  sessionId: number,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(
    `/mobile/inbound-checks/${sessionId}/submit`,
    { method: "POST" },
  );
}

export function cancelMobileInboundCheck(
  sessionId: number,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(
    `/mobile/inbound-checks/${sessionId}/cancel`,
    { method: "POST" },
  );
}

export function reviewMobileInboundCheck(
  sessionId: number,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(
    `/mobile/inbound-checks/${sessionId}/review`,
    { method: "POST" },
  );
}

export function rejectMobileInboundCheck(
  sessionId: number,
  reason?: string,
): Promise<MobileInboundCheckSessionDetail> {
  return request<MobileInboundCheckSessionDetail>(
    `/mobile/inbound-checks/${sessionId}/reject`,
    { method: "POST", body: JSON.stringify({ rejected_reason: reason ?? null }) },
  );
}

// ─── 송장쪼개기 (item-move) ─────────────────────────────────────
// [ERP-ORDER-INVOICE-SPLIT-ITEM-MOVE-REDESIGN-20260529-001]
// 송장쪼개기 = 원주문 항목 일부를 분할자식 주문(order_type='invoice_split_order')으로 "이동".
//   - 원주문 항목은 삭제하지 않고 split_status='moved_out' 으로 이력 보존.
//   - moved_out 항목은 원주문 송장 포장 스캔/재고차감 대상에서 제외.
//   - 분할자식은 자신의 order_no/order_items 를 가진 정상 주문 → 포장/스캔/재고차감/Export 대상.
//   - 재고차감은 분할자식 포장 스캔 시점에만 발생 (이중 차감 없음).
//   - Phase 1 은 "항목 행 전체 이동" 만 지원 (부분 수량 이동 범위 밖).

export interface InvoiceSplitMovableItem {
  order_item_id: number;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
  split_status: string; // none | moved_out
  split_child_order_no: string | null;
  movable: boolean;
  block_reason: string | null;
}

export interface InvoiceSplitItemsPreviewResponse {
  original_order_no: string;
  original_invoice_no: string | null;
  order_status: string | null;
  order_status_blocked: boolean;
  block_reason: string | null;
  customer_name: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  zipcode: string | null;
  address1: string | null;
  address2: string | null;
  existing_child_count: number;
  items: InvoiceSplitMovableItem[];
  notice: string;
}

export interface InvoiceSplitChildItem {
  order_item_id: number;
  source_order_item_id?: number | null;
  sku: string | null;
  product_name: string | null;
  option_name: string | null;
  quantity: number | null;
}

export interface InvoiceSplitChildResponse {
  child_order_no: string;
  original_order_no: string;
  order_status: string | null;
  invoice_no: string | null;
  exported_at: string | null;
  created_at: string;
  item_count: number;
  items: InvoiceSplitChildItem[];
}

export interface InvoiceSplitChildListResponse {
  total: number;
  items: InvoiceSplitChildResponse[];
}

export function previewInvoiceSplitItems(
  orderNo: string,
): Promise<InvoiceSplitItemsPreviewResponse> {
  return request<InvoiceSplitItemsPreviewResponse>(
    `/orders/${encodeURIComponent(orderNo)}/invoice-split-items/preview`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export function createInvoiceSplitItems(
  orderNo: string,
  orderItemIds: number[],
  splitReason?: string | null,
): Promise<InvoiceSplitChildResponse> {
  return request<InvoiceSplitChildResponse>(
    `/orders/${encodeURIComponent(orderNo)}/invoice-split-items`,
    {
      method: "POST",
      body: JSON.stringify({ order_item_ids: orderItemIds, split_reason: splitReason ?? null }),
    },
  );
}

export function listOrderInvoiceSplitItems(
  orderNo: string,
): Promise<InvoiceSplitChildListResponse> {
  return request<InvoiceSplitChildListResponse>(
    `/orders/${encodeURIComponent(orderNo)}/invoice-split-items`,
  );
}

export function listAllInvoiceSplitChildren(params: {
  page?: number;
  page_size?: number;
} = {}): Promise<InvoiceSplitChildListResponse> {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.page_size) qs.set("page_size", String(params.page_size));
  const q = qs.toString();
  return request<InvoiceSplitChildListResponse>(`/orders/invoice-splits${q ? `?${q}` : ""}`);
}

// [ERP-INVOICE-SPLIT-VISIBILITY-AND-CANCEL-FIX-20260601-002] 송장쪼개기 취소(분할 원복)
export interface InvoiceSplitCancelItem {
  order_item_id: number;
  sku?: string | null;
  product_name?: string | null;
  option_name?: string | null;
  quantity?: number | null;
}

export interface InvoiceSplitCancelPreviewResponse {
  can_cancel: boolean;
  child_order_no: string;
  original_order_no?: string | null;
  child_order_status?: string | null;
  child_invoice_no?: string | null;
  child_exported: boolean;
  restore_items: InvoiceSplitCancelItem[];
  child_items: InvoiceSplitCancelItem[];
  block_reasons: string[];
  warnings: string[];
  notice: string;
}

export interface InvoiceSplitCancelResponse {
  ok: boolean;
  child_order_no: string;
  original_order_no?: string | null;
  child_order_status: string;
  restored_order_item_ids: number[];
  cancelled_child_item_ids: number[];
}

export function previewCancelInvoiceSplit(
  childOrderNo: string,
): Promise<InvoiceSplitCancelPreviewResponse> {
  return request<InvoiceSplitCancelPreviewResponse>(
    `/orders/${encodeURIComponent(childOrderNo)}/invoice-split-items/cancel-preview`,
    { method: "POST" },
  );
}

export function cancelInvoiceSplit(
  childOrderNo: string,
): Promise<InvoiceSplitCancelResponse> {
  return request<InvoiceSplitCancelResponse>(
    `/orders/${encodeURIComponent(childOrderNo)}/invoice-split-items/cancel`,
    { method: "POST" },
  );
}

// ── 가격 조정 후보 (read-only) [ERP-PRICE-ADJUSTMENT-CANDIDATE-READONLY-VIEW-20260601-001] ──
export interface PriceCandidate {
  sku: string;
  product_name: string | null;
  option1: string | null;
  option2: string | null;
  brand: string | null;
  category: string | null;
  supplier: string | null;
  platform: string;
  current_price: number | null;
  base_price_source: string;
  cost_price: number | null;
  cost_currency: string | null;
  margin_rate: number | null;
  margin_quality: string;
  total_stock: number;
  available_stock: number;
  office_stock: number;
  warehouse_stock: number;
  rocket_stock: number;
  sales_qty_7d: number;
  sales_qty_30d: number;
  sales_qty_90d: number;
  sales_amount_30d: number;
  last_sold_at: string | null;
  daiwa_policy_price: number | null;
  daiwa_naver_price: number | null;
  policy_status: string;
  recommended_action: string;
  suggested_delta_amount: number | null;
  suggested_delta_percent: number | null;
  suggested_price: number | null;
  reason_type: string;
  reason_text: string;
  confidence: string;
  warning_flags: string[];
  // [ERP-COMMON-SKU-MARKET-LINKS-REUSE-DEV-20260629-001] 마켓 등록 플랫폼/링크(read-only).
  listed_platforms?: string[];
  listing_urls?: Record<string, string>;
}

export interface PriceCandidateResponse {
  total: number;
  returned: number;
  summary: Record<string, number>;
  notice: string;
  items: PriceCandidate[];
}

export function getPriceAdjustmentCandidates(params: {
  reason_type?: string;
  brand?: string;
  category?: string;
  supplier?: string;
  platform?: string;
  min_stock?: number;
  max_stock?: number;
  min_sales_30d?: number;
  max_sales_90d?: number;
  max_margin_rate?: number;
  q?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<PriceCandidateResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  const s = qs.toString();
  return request<PriceCandidateResponse>(`/price-adjustment/candidates${s ? "?" + s : ""}`);
}

// ── 품절 처리 요청 큐 [ERP-PACKING-SOLDOUT-REQUEST-QUEUE-20260601-001] ──
export interface SoldoutPreviewResult {
  can_create: boolean;
  block_reasons: string[];
  existing_open_request_id: number | null;
  sku: string;
  product_name: string | null;
  option_name: string | null;
  product_status: string | null;
  stock: { total: number; office: number; warehouse: number; rocket: number };
  order_no: string | null;
  invoice_no: string | null;
  notice: string;
}
export interface SoldoutRequestRow {
  id: number;
  sku: string;
  product_name_snapshot: string | null;
  option_name_snapshot: string | null;
  order_no: string | null;
  invoice_no: string | null;
  requested_by_name_snapshot: string | null;
  requested_at: string;
  request_source: string;
  reason: string;
  memo: string | null;
  current_stock_snapshot: number | null;
  office_stock_snapshot: number | null;
  warehouse_stock_snapshot: number | null;
  rocket_stock_snapshot: number | null;
  product_status_snapshot: string | null;
  status: string;
  processed_by_user_id: number | null;
  processed_at: string | null;
  process_memo: string | null;
  action_taken: string | null;
}
export interface SoldoutSummary {
  pending_count: number;
  in_progress_count: number;
  today_completed_count: number;
  stale_count: number;
}

export function previewSoldoutRequest(body: { sku: string; order_no?: string | null; invoice_no?: string | null; order_item_id?: number | null }): Promise<SoldoutPreviewResult> {
  return request<SoldoutPreviewResult>(`/packing/soldout-requests/preview`, { method: "POST", body: JSON.stringify(body) });
}
export function createSoldoutRequest(body: { sku: string; order_no?: string | null; invoice_no?: string | null; order_item_id?: number | null; reason?: string; memo?: string | null; request_source?: string }): Promise<SoldoutRequestRow> {
  return request<SoldoutRequestRow>(`/packing/soldout-requests`, { method: "POST", body: JSON.stringify(body) });
}
export function listSoldoutRequests(params: { status?: string; sku?: string; order_no?: string; limit?: number; offset?: number } = {}): Promise<{ total: number; items: SoldoutRequestRow[] }> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, String(v)); });
  const s = qs.toString();
  return request(`/packing/soldout-requests${s ? "?" + s : ""}`);
}
export function getSoldoutSummary(): Promise<SoldoutSummary> {
  return request<SoldoutSummary>(`/packing/soldout-requests/summary`);
}
export function updateSoldoutStatus(id: number, body: { status: string; process_memo?: string | null; action_taken?: string | null }): Promise<SoldoutRequestRow> {
  return request<SoldoutRequestRow>(`/packing/soldout-requests/${id}/status`, { method: "PATCH", body: JSON.stringify(body) });
}

// ── 가격 조정 작업 큐 (저장형) [ERP-PRICE-ADJUSTMENT-TASK-QUEUE-SCHEMA-20260601-001] ──
export interface PriceTask {
  id: number;
  sku: string;
  product_name_snapshot: string | null;
  option1_snapshot: string | null;
  option2_snapshot: string | null;
  brand_snapshot: string | null;
  category_snapshot: string | null;
  supplier_snapshot: string | null;
  platform: string;
  current_price_snapshot: number | null;
  cost_price_snapshot: number | null;
  cost_currency_snapshot: string | null;
  margin_rate_snapshot: number | null;
  total_stock_snapshot: number | null;
  available_stock_snapshot: number | null;
  sales_qty_7d_snapshot: number | null;
  sales_qty_30d_snapshot: number | null;
  sales_qty_90d_snapshot: number | null;
  sales_amount_30d_snapshot: number | null;
  last_sold_at_snapshot: string | null;
  adjustment_type: string | null;
  adjustment_value: number | null;
  target_price: number | null;
  delta_amount: number | null;
  delta_percent: number | null;
  reason_type: string;
  reason_text: string | null;
  memo: string | null;
  status: string;
  // [ERP-PRICE-ADJUSTMENT-ROUNDING-UNIT-SNAPSHOT-PERSISTENCE-DEV-20260624-001]
  // 플랫폼 적용 완료(applied) 시점에 못박힌 올림단위(10/100/1000). null = 미적용/없음.
  platform_rounding_unit_snapshot: number | null;
  created_by_name_snapshot: string | null;
  created_at: string | null;
  applied_at: string | null;
  confirmed_at: string | null;
  cancelled_at: string | null;
  apply_memo: string | null;
  confirm_memo: string | null;
  cancel_reason: string | null;
  // [ERP-COMMON-SKU-MARKET-LINKS-REUSE-DEV-20260629-001] 마켓 등록 플랫폼/링크(read-only).
  listed_platforms?: string[];
  listing_urls?: Record<string, string>;
}
export interface PriceTaskListResponse {
  total: number;
  summary: Record<string, number>;
  items: PriceTask[];
  notice: string;
}

export function listPriceTasks(params: { status?: string; sku?: string; q?: string; platform?: string; include_closed?: boolean; limit?: number; offset?: number } = {}): Promise<PriceTaskListResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, String(v)); });
  const s = qs.toString();
  return request(`/price-adjustment/tasks${s ? "?" + s : ""}`);
}
export function createPriceTask(body: { sku: string; platform?: string; adjustment_type?: string | null; adjustment_value?: number | null; target_price?: number | null; reason_type?: string; reason_text?: string | null; memo?: string | null }): Promise<PriceTask> {
  return request<PriceTask>(`/price-adjustment/tasks`, { method: "POST", body: JSON.stringify(body) });
}
export function patchPriceTask(id: number, body: { adjustment_type?: string | null; adjustment_value?: number | null; target_price?: number | null; status?: string; memo?: string | null; apply_memo?: string | null; confirm_memo?: string | null; cancel_reason?: string | null }): Promise<PriceTask> {
  return request<PriceTask>(`/price-adjustment/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

// 가격 조정 작업 큐 일괄 수정 [ERP-PRICE-ADJUSTMENT-BULK-SELECT-UPDATE-20260602-001]
export function bulkPatchPriceTasks(body: { task_ids: number[]; adjustment_type?: string | null; adjustment_value?: number | null; target_price?: number | null; status?: string | null; memo?: string | null; apply_memo?: string | null; confirm_memo?: string | null; cancel_reason?: string | null; overwrite_memo?: boolean; task_rounding_units?: Record<number, number> }): Promise<{ updated_count: number; updated_task_ids: number[]; status_summary: Record<string, number>; message: string }> {
  return request(`/price-adjustment/tasks/bulk`, { method: "PATCH", body: JSON.stringify(body) });
}

// [ERP-PRICE-ADJUSTMENT-ERP-APPLY-WITH-HISTORY-AND-ROLLBACK-20260608-001]
// Phase 3.1 — ERP 가격 반영 Preview (read-only, DB write 0). 실제 Apply 는 별도 차수.
export interface ErpApplyPlatformDiff {
  platform: string;
  label: string;
  multiplier: number;
  before: number | null;
  after: number;
  delta: number | null;
  changed: boolean;
}
export interface ErpApplyPreviewItem {
  task_id: number;
  sku?: string;
  product_name?: string | null;
  option1?: string | null;
  option2?: string | null;
  status?: string;
  target_price?: number | null;
  current_sale_price?: number | null;
  proposed_sale_price?: number | null;
  sale_price_changed?: boolean;
  platform_prices?: ErpApplyPlatformDiff[];
  warnings: string[];
  can_apply: boolean;
}
export interface ErpApplyPreviewResponse {
  rounding_unit: number;
  total: number;
  appliable_count: number;
  items: ErpApplyPreviewItem[];
  note: string;
}
export function erpApplyPreview(body: { task_ids: number[]; rounding_unit?: 10 | 100 | 1000 }): Promise<ErpApplyPreviewResponse> {
  return request(`/price-adjustment/tasks/erp-apply-preview`, { method: "POST", body: JSON.stringify(body) });
}

// [ERP-PRICE-ADJUSTMENT-ERP-APPLY-ACTIVATE-WITH-AUDIT-20260609-001]
// 실제 ERP 가격 반영 Apply (관리자 전용). product_prices + products.sale_price UPDATE 발생.
export interface ErpApplyResponse {
  applied_count: number;
  applied: { task_id: number; sku: string; target_price: number;
    before: { sale_price: number | null; product_prices: Record<string, number | null> };
    after: { sale_price: number; product_prices: Record<string, number> } }[];
  rounding_unit: number;
  message: string;
}
export function erpApply(body: { task_ids: number[]; rounding_unit?: 10 | 100 | 1000; confirm: boolean }): Promise<ErpApplyResponse> {
  return request(`/price-adjustment/tasks/erp-apply`, { method: "POST", body: JSON.stringify(body) });
}

// ── 합배송 그룹 [ERP-ORDER-COMBINE-SHIPPING-GROUP-SCHEMA-20260602-001] ──
export interface CombineGroupOrder {
  order_no: string;
  role: string;
  payment_required: boolean;
  payment_confirmed: boolean;
  amount_expected: number | null;
  receiver_match_status: string;
  include_in_packing: boolean;
}
export interface CombineGroup {
  group_no: string;
  status: string;
  representative_order_no: string;
  representative_invoice_no: string | null;
  receiver_name_snapshot: string | null;
  receiver_phone_snapshot: string | null;
  receiver_address1_snapshot: string | null;
  shipping_fee_policy: string;
  shipping_fee_note: string | null;
  memo: string | null;
  created_by_name_snapshot: string | null;
  created_at: string | null;
  cancelled_at: string | null;
  orders: CombineGroupOrder[];
}
export interface CombinePreviewResult {
  can_create: boolean;
  block_reasons: string[];
  warnings: string[];
  representative_order: Record<string, unknown> | null;
  additional_orders: Record<string, unknown>[];
  receiver_match_status: Record<string, string>;
  shipping_fee_policy: string;
  suggested_group_status: string;
  message: string;
}

export function previewCombinedShipping(body: { representative_order_no: string; additional_order_nos: string[]; shipping_fee_policy?: string; shipping_fee_note?: string | null; memo?: string | null }): Promise<CombinePreviewResult> {
  return request<CombinePreviewResult>(`/orders/combined-shipping/preview`, { method: "POST", body: JSON.stringify(body) });
}
export function createCombinedShipping(body: { representative_order_no: string; additional_order_nos: string[]; shipping_fee_policy?: string; shipping_fee_note?: string | null; memo?: string | null; confirm_warnings?: boolean }): Promise<CombineGroup> {
  return request<CombineGroup>(`/orders/combined-shipping`, { method: "POST", body: JSON.stringify(body) });
}
export function listCombinedShippingByOrder(orderNo: string): Promise<{ total: number; items: CombineGroup[] }> {
  return request(`/orders/combined-shipping?order_no=${encodeURIComponent(orderNo)}`);
}
export function cancelCombinedShipping(groupNo: string): Promise<{ ok: boolean; group_no: string; status: string }> {
  return request(`/orders/combined-shipping/${encodeURIComponent(groupNo)}/cancel`, { method: "POST" });
}

// ─── 고객통합ID (Customer Identity) — Phase 1-B ─────────────────────
// [ERP-CUSTOMER-IDENTITY-PHASE-1B-CANDIDATE-MERGE-UI-20260617-001]
// 자동 통합 아님 — 조회는 로그인 사용자, 생성/수정/alias 변경은 admin 만(백엔드 require_admin).
export type CustomerIdentityAliasType =
  | "phone_real" | "phone_coupang_safe" | "phone_past"
  | "name_orderer" | "name_receiver" | "email" | "manual_note";
export type CustomerIdentityAliasSource =
  | "naver" | "coupang" | "as" | "order" | "manual" | "import";
export type CustomerIdentityConfidence = "high" | "medium" | "low" | "manual";

export interface CustomerIdentityAlias {
  id: number;
  group_id: number | null;
  alias_type: string;
  value: string;
  value_normalized: string | null;
  source: string | null;
  linked_customer_id: number | null;
  linked_order_id: number | null;
  linked_as_request_id: number | null;
  confidence: string | null;
  verified_by_user_id: number | null;
  verified_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerIdentityGroup {
  id: number;
  display_name: string | null;
  memo: string | null;
  is_active: boolean;
  created_by_user_id: number | null;
  updated_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerIdentityGroupDetail extends CustomerIdentityGroup {
  aliases: CustomerIdentityAlias[];
}

export interface CustomerIdentityGroupListResponse {
  total: number;
  page: number;
  page_size: number;
  items: CustomerIdentityGroup[];
}

export interface CustomerIdentityCandidate {
  source_table: string;
  source_id: number;
  customer_name: string | null;
  phone_masked: string | null;
  phone_normalized: string | null;
  product_name: string | null;
  platform: string | null;
  confidence: string;
  reasons: string[];
}

export interface CustomerIdentityCandidateResponse {
  query: string;
  candidates: CustomerIdentityCandidate[];
  note: string;
}

export function getCustomerIdentityGroups(params: {
  q?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
} = {}): Promise<CustomerIdentityGroupListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.is_active != null) qs.set("is_active", String(params.is_active));
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<CustomerIdentityGroupListResponse>(`/customer-identity/groups${suffix}`);
}

export function createCustomerIdentityGroup(payload: {
  display_name?: string | null;
  memo?: string | null;
}): Promise<CustomerIdentityGroup> {
  return request<CustomerIdentityGroup>(`/customer-identity/groups`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getCustomerIdentityGroup(groupId: number): Promise<CustomerIdentityGroupDetail> {
  return request<CustomerIdentityGroupDetail>(`/customer-identity/groups/${groupId}`);
}

export function updateCustomerIdentityGroup(groupId: number, payload: {
  display_name?: string | null;
  memo?: string | null;
  is_active?: boolean;
}): Promise<CustomerIdentityGroup> {
  return request<CustomerIdentityGroup>(`/customer-identity/groups/${groupId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function addCustomerIdentityAlias(groupId: number, payload: {
  alias_type: string;
  value: string;
  source?: string | null;
  confidence?: string | null;
  linked_customer_id?: number | null;
  linked_order_id?: number | null;
  linked_as_request_id?: number | null;
}): Promise<CustomerIdentityAlias> {
  return request<CustomerIdentityAlias>(`/customer-identity/groups/${groupId}/aliases`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCustomerIdentityAlias(aliasId: number, payload: {
  group_id?: number | null;
  alias_type?: string;
  value?: string;
  source?: string | null;
  confidence?: string | null;
  is_active?: boolean;
}): Promise<CustomerIdentityAlias> {
  return request<CustomerIdentityAlias>(`/customer-identity/aliases/${aliasId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function getCustomerIdentityCandidates(params: {
  q?: string;
  phone?: string;
  name?: string;
  customer_id?: number;
  order_id?: number;
  as_request_id?: number;
  limit?: number;
} = {}): Promise<CustomerIdentityCandidateResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.phone) qs.set("phone", params.phone);
  if (params.name) qs.set("name", params.name);
  if (params.customer_id != null) qs.set("customer_id", String(params.customer_id));
  if (params.order_id != null) qs.set("order_id", String(params.order_id));
  if (params.as_request_id != null) qs.set("as_request_id", String(params.as_request_id));
  if (params.limit != null) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<CustomerIdentityCandidateResponse>(`/customer-identity/candidates${suffix}`);
}

// ─── Phase 1-C: 업무 화면 연결 (lookup / link) ─────────────────────
// [ERP-CUSTOMER-IDENTITY-PHASE-1C-CUSTOMER360-AS-LINK-20260617-001]
export interface CustomerIdentityLookupResponse {
  linked_groups: CustomerIdentityGroup[];
  aliases: CustomerIdentityAlias[];
  candidates: CustomerIdentityCandidate[];
  warning_notes: string[];
}

export function lookupCustomerIdentity(params: {
  customer_id?: number;
  order_id?: number;
  as_request_id?: number;
  phone?: string;
  name?: string;
  include_candidates?: boolean;
} = {}): Promise<CustomerIdentityLookupResponse> {
  const qs = new URLSearchParams();
  if (params.customer_id != null) qs.set("customer_id", String(params.customer_id));
  if (params.order_id != null) qs.set("order_id", String(params.order_id));
  if (params.as_request_id != null) qs.set("as_request_id", String(params.as_request_id));
  if (params.phone) qs.set("phone", params.phone);
  if (params.name) qs.set("name", params.name);
  if (params.include_candidates != null) qs.set("include_candidates", String(params.include_candidates));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<CustomerIdentityLookupResponse>(`/customer-identity/lookup${suffix}`);
}

export function linkCustomerToIdentityGroup(groupId: number, payload: {
  customer_id: number;
  alias_type?: string | null;
  memo?: string | null;
}): Promise<CustomerIdentityAlias> {
  return request<CustomerIdentityAlias>(`/customer-identity/groups/${groupId}/link-customer`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function linkAsRequestToIdentityGroup(groupId: number, payload: {
  as_request_id: number;
  alias_type?: string | null;
  memo?: string | null;
}): Promise<CustomerIdentityAlias> {
  return request<CustomerIdentityAlias>(`/customer-identity/groups/${groupId}/link-as-request`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ─── 무상 AS 정책 / 대상 SKU (Phase 1.5-B) ─────────────────────────
// [ERP-WARRANTY-POLICY-PHASE-1.5B-MANAGEMENT-UI-20260617-001]
// 조회=로그인 사용자, 정책 생성/수정=admin, SKU add/deactivate=lv2+ (백엔드 require_level("lv2")).
export type WarrantyPolicyType = "lifetime_once" | "period_once" | "period_unlimited" | "manual";
export type WarrantyCoverageScope = "parts_repair_only" | "parts_repair_and_shipping" | "full";

export interface WarrantyPolicy {
  id: number;
  policy_code: string;
  policy_name: string;
  policy_type: string;
  coverage_period_days: number | null;
  max_free_as_count: number;
  coverage_scope: string;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  memo: string | null;
  created_by_user_id: number | null;
  updated_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface WarrantyPolicyProduct {
  id: number;
  policy_id: number;
  sku: string;
  product_name_snapshot: string | null;
  option1_snapshot: string | null;
  option2_snapshot: string | null;
  is_active: boolean;
  added_by_user_id: number | null;
  removed_by_user_id: number | null;
  removed_at: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

export interface WarrantyPolicyWithProducts extends WarrantyPolicy {
  products: WarrantyPolicyProduct[];
}

export interface WarrantyPolicyListResponse {
  total: number;
  page: number;
  page_size: number;
  items: WarrantyPolicy[];
}

export interface EligibleWarrantySku {
  policy_id: number;
  policy_code: string;
  sku: string;
  product_name_snapshot: string | null;
}

export interface EligibleWarrantySkuResponse {
  total: number;
  items: EligibleWarrantySku[];
}

export function getWarrantyPolicies(params: {
  q?: string;
  is_active?: boolean;
  policy_type?: string;
  page?: number;
  page_size?: number;
} = {}): Promise<WarrantyPolicyListResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.is_active != null) qs.set("is_active", String(params.is_active));
  if (params.policy_type) qs.set("policy_type", params.policy_type);
  if (params.page != null) qs.set("page", String(params.page));
  if (params.page_size != null) qs.set("page_size", String(params.page_size));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<WarrantyPolicyListResponse>(`/warranty-policies${suffix}`);
}

export function createWarrantyPolicy(payload: {
  policy_code: string;
  policy_name: string;
  policy_type: string;
  coverage_period_days?: number | null;
  max_free_as_count?: number;
  coverage_scope?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  memo?: string | null;
}): Promise<WarrantyPolicy> {
  return request<WarrantyPolicy>(`/warranty-policies`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getWarrantyPolicy(policyId: number): Promise<WarrantyPolicyWithProducts> {
  return request<WarrantyPolicyWithProducts>(`/warranty-policies/${policyId}`);
}

export function updateWarrantyPolicy(policyId: number, payload: {
  policy_name?: string;
  memo?: string | null;
  is_active?: boolean;
  ends_at?: string | null;
  policy_type?: string;
  coverage_period_days?: number | null;
  max_free_as_count?: number;
  coverage_scope?: string;
}): Promise<WarrantyPolicy> {
  return request<WarrantyPolicy>(`/warranty-policies/${policyId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function addWarrantyPolicyProduct(policyId: number, payload: {
  sku: string;
  product_name_snapshot?: string | null;
  option1_snapshot?: string | null;
  option2_snapshot?: string | null;
  memo?: string | null;
  allow_unknown_sku?: boolean;
}): Promise<WarrantyPolicyProduct> {
  return request<WarrantyPolicyProduct>(`/warranty-policies/${policyId}/products`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateWarrantyPolicyProduct(policyProductId: number, payload: {
  is_active?: boolean;
  memo?: string | null;
}): Promise<WarrantyPolicyProduct> {
  return request<WarrantyPolicyProduct>(`/warranty-policies/products/${policyProductId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function getEligibleWarrantySkus(params: { policy_id?: number } = {}): Promise<EligibleWarrantySkuResponse> {
  const qs = new URLSearchParams();
  if (params.policy_id != null) qs.set("policy_id", String(params.policy_id));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<EligibleWarrantySkuResponse>(`/warranty-policies/eligible-skus${suffix}`);
}

// [ERP-DASHBOARD-STAFF-WORK-INSTRUCTIONS-MVP-20260622-001]
// 운영대시보드 업무지시 카드 — 대표→직원 지시 (공통/강원준/강동준).
export interface WorkInstructionSection {
  title: string;
  content: string;
  updated_at: string;
  updated_by: string;
}
export interface WorkInstructionsResponse {
  can_edit: boolean;
  // 사용자가 볼 수 있는 구역만 (key → 내용)
  sections: Record<string, WorkInstructionSection>;
}
export function getWorkInstructions() {
  return request<WorkInstructionsResponse>("/dashboard/work-instructions");
}
export function saveWorkInstructions(body: {
  common: { title: string; content: string };
  kang_wonjun: { title: string; content: string };
  kang_dongjun: { title: string; content: string };
}) {
  return request<WorkInstructionsResponse>("/dashboard/work-instructions", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

// [ERP-DASHBOARD-WORK-INSTRUCTIONS-BOARD-CHAT-COMPLETE-FLOW-DEV-20260622-001]
// 업무지시 보드 (타이틀 목록 → 상세 + 채팅 → 종료체크/승인/완료이력)
export interface DwiItem {
  id: number;
  section_key: string;
  title: string;
  content: string;
  status: string; // open | needs_check | done_requested | approved | archived
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  message_count: number;
  done_requested_by_name?: string | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
}
export interface DwiSection {
  key: string;
  label: string;
  active: DwiItem[];
  completed: DwiItem[];
}
export interface DwiBoard {
  can_manage: boolean;
  own_section: string | null;
  sections: DwiSection[];
}
export interface DwiMessage {
  id: number;
  user_id: number | null;
  sender_name: string;
  sender_role: string | null;
  message: string;
  created_at: string;
}
export interface DwiDetail {
  instruction: DwiItem;
  messages: DwiMessage[];
  can_edit: boolean;
  can_request_done: boolean;
  can_approve: boolean;
}

export function getWorkInstructionBoard() {
  return request<DwiBoard>("/dashboard/work-instructions/board");
}
export function createWorkInstruction(body: { section_key: string; title: string; content: string }) {
  return request<DwiItem>("/dashboard/work-instructions", { method: "POST", body: JSON.stringify(body) });
}
export function getWorkInstructionDetail(id: number) {
  return request<DwiDetail>(`/dashboard/work-instructions/${id}`);
}
export function patchWorkInstruction(id: number, body: { title?: string; content?: string }) {
  return request<DwiItem>(`/dashboard/work-instructions/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}
export function deleteWorkInstruction(id: number) {
  return request<{ ok: boolean; id: number }>(`/dashboard/work-instructions/${id}`, { method: "DELETE" });
}
export function postWorkInstructionMessage(id: number, message: string) {
  return request<DwiMessage>(`/dashboard/work-instructions/${id}/messages`, { method: "POST", body: JSON.stringify({ message }) });
}
export function requestWorkInstructionDone(id: number) {
  return request<DwiItem>(`/dashboard/work-instructions/${id}/request-done`, { method: "POST", body: "{}" });
}
export function approveWorkInstruction(id: number) {
  return request<DwiItem>(`/dashboard/work-instructions/${id}/approve`, { method: "POST", body: "{}" });
}
